import React, { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import CategoryDeepDive from "./CategoryDeepDive";
import StorePinMap from "./StorePinMap";
import ResearchGroupsModule from "./ResearchGroupsModule";
import BestSellerSchedulesPanel from "./BestSellerSchedulesPanel";
import SearchTrendsModule from "./SearchTrendsModule";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { geoCentroid, geoBounds } from "d3-geo";
import "@/App.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ControlPanel, getCategoryIcon } from "@/ControlPanel";
import { PriceGovernance } from "@/PriceGovernance";
import AppHeader from "@/AppHeader";
import BandwidthWidget from "@/BandwidthWidget";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Play, 
  Download, 
  LogOut, 
  Loader2, 
  FileSpreadsheet, 
  FileText,
  Upload,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Zap,
  Globe,
  Package,
  Timer,
  Database,
  Activity,
  ShoppingCart,
  History,
  ArrowLeft,
  Bell,
  X,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  Info,
  Settings,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  ExternalLink,
  SlidersHorizontal,
  Save,
  Filter,
  Search,
  PanelRightOpen,
  PanelRightClose,
  ChevronDown,
  MapPin,
  Square,
  RotateCcw,
  Shield,
  Truck,
  Award,
  BarChart3,
  Clock,
  ListChecks,
  Eye,
  Wifi,
  CalendarClock,
  Repeat,
  Power,
  Users,
  Layers,
  TrendingUp,
  Camera,
  Trophy,
  Droplets,
  FlaskConical,
  LayoutGrid,
  Pill,
  Coffee,
  Cherry,
  Citrus,
  Leaf,
  Circle,
  Check,
  ShieldCheck,
  LineChart,
  Star,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import DateRangePicker from "./components/DateRangePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;
axios.defaults.timeout = 180000;

// Attach session_token as Bearer header fallback (in case cookies are blocked)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('horizon_session_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Clear stale token on 401
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/session')) {
      localStorage.removeItem('horizon_session_token');
      // Banned mid-session → force immediate sign-out
      if (String(error.response?.data?.detail || '').includes('access has been revoked')) {
        sessionStorage.setItem('horizon_auth_error', error.response.data.detail);
        if (window.location.pathname !== '/login' && !window.__horizonBanRedirect) {
          window.__horizonBanRedirect = true;
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Available columns - simplified
const AVAILABLE_COLUMNS = [
  { id: 'asin', label: 'ASIN', default: true },
  { id: 'pincode', label: 'Pincode', default: true },
  { id: 'product_name', label: 'Product Name', default: true },
  { id: 'seller_name', label: 'Seller', default: false },
  { id: 'delivery_standard', label: 'Standard (Days)', default: true },
  { id: 'delivery_fast', label: 'Fast (Days)', default: true },
  { id: 'delivery_text', label: 'Delivery Text', default: false },
  { id: 'scraped_at', label: 'Scraped At', default: true },
];

const OFFLINE_COLUMNS = [
  { id: 'keyword', label: 'Keyword', default: true },
  { id: 'pincode', label: 'Pincode', default: true },
  { id: 'store_name', label: 'Store Name', default: true },
  { id: 'address', label: 'Address', default: true },
  { id: 'phone', label: 'Phone', default: true },
  { id: 'status', label: 'Status', default: true },
  { id: 'error', label: 'Error', default: true },
];

const BLINKIT_COLUMNS = [
  { id: 'asin', label: 'Product', default: true },
  { id: 'pincode', label: 'Pincode', default: true },
  { id: 'price', label: 'Selling Price', default: true },
  { id: 'mrp', label: 'MRP', default: true },
  { id: 'availability', label: 'Availability', default: true },
  { id: 'scraped_at', label: 'Scraped At', default: true },
  { id: 'status', label: 'Status', default: false },
];

// Fallback icon map for platforms without icon_url
const ICON_MAP = {
  amazon: ShoppingCart,
  flipkart: Package,
  blinkit: Zap,
  zepto: Timer,
  '1mg': Activity,
  swiggy: Globe,
  'price-governance': Shield,
};

const DEFAULT_ICON = Globe;

// Helper: render platform icon (URL image or fallback lucide icon)
const PlatformIcon = ({ platform, size = 'w-7 h-7', containerSize = 'w-14 h-14' }) => {
  if (platform.icon_url) {
    return (
      <div className={`${containerSize} rounded-xl flex items-center justify-center ${platform.color_bg || 'bg-gray-100'}`}>
        <img src={platform.icon_url} alt={platform.name} className={`${size} object-contain rounded`} onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    );
  }
  const IconComp = ICON_MAP[platform.platform_id] || DEFAULT_ICON;
  return (
    <div className={`${containerSize} rounded-xl flex items-center justify-center ${platform.color_bg || 'bg-gray-100'}`}>
      <IconComp className={`${size} ${platform.color_text || 'text-gray-600'}`} />
    </div>
  );
};

// Sample CSV content
const SAMPLE_ASIN_CSV = "ASIN\nB0CHX1W1XY\nB0DJHJ6M81\nB0CHX2H792";
const SAMPLE_PINCODE_CSV = "Pincode\n400001\n110001\n560001\n500001";
const SAMPLE_KEYWORD_CSV = "Keyword\ngym supplement store\nprotein shop\nnutrition store";

// Witty greetings by time of day
const GREETINGS = {
  morning: [
    "Rise and grind, {name}! Those ASINs won't scrape themselves.",
    "Good morning, {name}! Fresh data awaits.",
    "Morning, {name}! Let's make the data flow.",
    "Early bird gets the data, {name}!",
    "Coffee loaded. Browsers ready. Let's go, {name}!",
  ],
  afternoon: [
    "Afternoon hustle, {name}! Time to extract.",
    "Hey {name}, lunch break's over — data o'clock!",
    "Good afternoon, {name}! Peak extraction hours.",
    "{name}, the data mines are open for business.",
    "Post-lunch power hour, {name}. Let's scrape!",
  ],
  evening: [
    "Night owl mode: ON. Let's go, {name}!",
    "Evening, {name}! Best time for stealth scraping.",
    "Burning the midnight oil, {name}? We're here for it.",
    "Late session, {name}? The browsers never sleep.",
    "Quiet hours = fast scrapes. Ready, {name}?",
  ],
};

const getGreeting = (name) => {
  const hour = new Date().getHours();
  const firstName = name?.split(' ')[0] || 'there';
  const period = hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 18 ? 'afternoon' : 'evening';
  const msgs = GREETINGS[period];
  return { text: msgs[Math.floor(Math.random() * msgs.length)].replace('{name}', firstName), period };
};

const getTimeIcon = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return Sparkles;
  if (hour >= 12 && hour < 18) return Zap;
  return Activity;
};

// Tutorial steps
const getTutorialSteps = (asinLabel) => [
  {
    target: 'asin-input',
    title: `Enter ${asinLabel}s`,
    description: `Paste product ${asinLabel}s here, one per line. You can also upload a CSV file for bulk input.`,
  },
  {
    target: 'pincode-input',
    title: 'Add Pincodes',
    description: 'Enter delivery pincodes separated by commas to check availability across regions.',
  },
  {
    target: 'columns-card',
    title: 'Select Columns',
    description: 'Choose which data fields to include in your results and exports.',
  },
  {
    target: 'start-scraping-btn',
    title: 'Start Extraction',
    description: 'Kick off the extraction — data is fetched in parallel for speed.',
  },
  {
    target: 'results-card',
    title: 'View & Export Results',
    description: 'Results appear in a live table. Export as CSV, or revisit your recent runs.',
  },
];

// Web Notification helper
const requestNotificationPermission = async () => {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  return false;
};

const sendNotification = (title, body) => {
  if (Notification.permission === "granted") {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'horizon-job',
      requireInteraction: true
    });
  }
};

// ─── SOUND NOTIFICATIONS ───
const _audioCtx = { current: null };
const getAudioCtx = () => {
  if (!_audioCtx.current) _audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
  return _audioCtx.current;
};

const playTone = (frequencies, durations, type = 'sine') => {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
    let t = ctx.currentTime;
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + durations[i]);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + durations[i]);
      t += durations[i] * 0.8;
    });
  } catch {}
};

// ─── Sound preset library ─────────────────────────────────────────────────
// Each preset is a short standalone tune built from oscillator tones. Designed
// to feel native to a control-room app — short, clean, never longer than ~600ms.
// Add new ones here; the popover UI auto-renders them and the saved id drives
// `SOUNDS.completed()` so the user's pick is what plays on every job done.
const SOUND_PRESETS = [
  { id: 'chime',    label: 'Chime',    desc: 'Bright two-note (default)', play: () => playTone([784, 1047], [0.08, 0.12], 'sine') },
  { id: 'ding',     label: 'Ding',     desc: 'Single soft bell',          play: () => playTone([1318], [0.18], 'sine') },
  { id: 'pop',      label: 'Pop',      desc: 'Quick bubble',              play: () => playTone([440, 880], [0.04, 0.06], 'sine') },
  { id: 'triumph',  label: 'Triumph',  desc: 'Three rising notes',        play: () => playTone([523, 659, 784], [0.07, 0.07, 0.16], 'sine') },
  { id: 'beep',     label: 'Beep',     desc: 'Crisp terminal blip',       play: () => playTone([1200], [0.07], 'square') },
  { id: 'soft',     label: 'Soft',     desc: 'Mellow descent',            play: () => playTone([880, 660], [0.10, 0.20], 'triangle') },
  { id: 'arcade',   label: 'Arcade',   desc: 'Retro 8-bit jingle',        play: () => playTone([659, 988, 1319], [0.06, 0.06, 0.12], 'square') },
  { id: 'horizon',  label: 'Horizon',  desc: 'Hopeful four-note arc',     play: () => playTone([523, 659, 880, 1047], [0.06, 0.06, 0.06, 0.14], 'sine') },
  { id: 'off',      label: 'Mute',     desc: 'Silent — no chime',         play: () => {} },
];

const SOUND_STORAGE_KEY = 'horizon_sound_preset';
const getActiveSoundId = () => {
  try { return localStorage.getItem(SOUND_STORAGE_KEY) || 'chime'; } catch { return 'chime'; }
};
const findPreset = (id) => SOUND_PRESETS.find(p => p.id === id) || SOUND_PRESETS[0];

const SOUNDS = {
  // Reads the saved preset at CALL time so a fresh pick takes effect immediately
  // without needing the component to re-mount or pass state down.
  completed: () => findPreset(getActiveSoundId()).play(),
};

// ─── Reusable processing pre-loader (Dribbble-inspired orbital animation) ───
// Three orbital arcs rotating at different speeds around a pulsing core. Use
// <HorizonPreloader size={64} /> for an inline spinner or
// <HorizonPreloader splash label="Loading workspace" /> for a fullscreen overlay.
const HorizonPreloader = ({ size = 56, splash = false, label }) => {
  const orbit = (
    <div className="horizon-preloader" style={{ width: size, height: size }}>
      <span className="ring r1" />
      <span className="ring r2" />
      <span className="ring r3" />
      <span className="core" />
    </div>
  );
  if (!splash) return orbit;
  return (
    <div className="horizon-preloader-splash" data-testid="horizon-preloader-splash">
      {orbit}
      {label && <div className="horizon-preloader-label">{label}</div>}
    </div>
  );
};

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        toast.error("Authentication failed");
        navigate('/login');
        return;
      }
      
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        // Store session_token as fallback for environments where cookies are blocked
        if (response.data.session_token) {
          localStorage.setItem('horizon_session_token', response.data.session_token);
        }
        toast.success(`Welcome, ${response.data.name}!`);
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        navigate('/select-platform', { state: { user: response.data } });
      } catch (error) {
        const msg = error.response?.data?.detail || "Authentication failed. Please try again.";
        console.error("Auth error:", error.response?.status, msg);
        toast.error(msg);
        navigate('/login');
      }
    };
    
    processAuth();
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#32B4AC] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Heartbeat so a ban takes effect within ~15s (interceptor handles the redirect)
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => { axios.get(`${API}/auth/me`).catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, [isAuthenticated]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <HorizonPreloader splash label="Securing your session" />
      </div>
    );
  }
  
  if (!isAuthenticated) return null;
  return children({ user, setUser });
};

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [heroPlatforms, setHeroPlatforms] = useState([]);
  
  useEffect(() => {
    const authErr = sessionStorage.getItem('horizon_auth_error');
    if (authErr) {
      // Delayed removal: a pending hard reload of /login must still find the message
      setTimeout(() => sessionStorage.removeItem('horizon_auth_error'), 2000);
      toast.error(authErr, { id: 'auth-revoked', duration: 8000 });
    }
    const checkExisting = async () => {
      try {
        await axios.get(`${API}/auth/me`);
        navigate('/select-platform');
      } catch {
        setChecking(false);
      }
    };
    checkExisting();
    // Fetch platforms for hero display
    axios.get(`${API}/platforms`).then(r => setHeroPlatforms(r.data || [])).catch(() => {});
  }, [navigate]);
  
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/select-platform';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#32B4AC] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white" data-testid="login-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#32B4AC] rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">HORIZON</span>
          </div>
          <Button onClick={handleGoogleLogin} className="btn-primary px-6 py-2" data-testid="header-login-btn">
            Sign in
          </Button>
        </div>
      </header>

      <section className="hero-section">
        <h1 className="hero-title">
          Extract marketplace data<br />
          <span className="text-[#32B4AC]">at scale</span>
        </h1>
        <p className="hero-subtitle">
          HORIZON extracts real-time product data from Amazon, Flipkart, Blinkit, and more.
          Get pricing, availability, and delivery info for competitive intelligence.
        </p>
        
        <div className="search-box max-w-lg w-full mb-10">
          <Input 
            placeholder="Enter ASIN to start scraping..."
            className="border-0 shadow-none focus-visible:ring-0 text-base flex-1 px-4"
          />
          <Button onClick={handleGoogleLogin} className="btn-primary px-5 py-2.5" data-testid="google-login-btn">
            Try it now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl w-full mb-10">
          {[...heroPlatforms].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0)).map((platform) => (
            <div key={platform.platform_id} className={`marketplace-card ${!platform.active ? 'opacity-50' : ''}`}>
              <PlatformIcon platform={platform} size="w-5 h-5" containerSize="w-10 h-10 rounded-lg" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm truncate">{platform.name}</h3>
                <p className="text-xs text-gray-500 truncate">{platform.description}</p>
                {!platform.active && <span className="text-xs text-[#32B4AC] font-medium">Coming Soon</span>}
              </div>
              {platform.active && (
                <span className="inline-flex items-center gap-1.5 self-center shrink-0 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                  </span>
                  <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Live</span>
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl w-full">
          {[
            { icon: Zap, label: 'Lightning Fast', desc: 'Parallel extraction' },
            { icon: Database, label: 'Bulk Upload', desc: 'CSV support' },
            { icon: FileSpreadsheet, label: 'Export', desc: 'CSV' },
            { icon: Bell, label: 'Notifications', desc: 'Job alerts' },
          ].map((feature, i) => (
            <div key={i} className="text-center">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <feature.icon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="font-semibold text-gray-900 text-sm">{feature.label}</div>
              <div className="text-xs text-gray-500">{feature.desc}</div>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-10">Restricted to @wellversed.in accounts</p>
      </section>
    </div>
  );
};

// Platform Selection Page
const PlatformSelection = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    axios.get(`${API}/platforms`).then(r => {
      setPlatforms(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    } catch {
      localStorage.removeItem('horizon_session_token');
      toast.error("Logout failed");
    }
  };
  
  const selectPlatform = (platform) => {
    if (platform.active) {
      navigate(`/dashboard/${platform.platform_id}`, { state: { user, platformId: platform.platform_id } });
    } else {
      toast.info(`${platform.name} coming soon!`);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="platform-selection">
      <AppHeader
        user={user}
        breadcrumbs={[{ label: "Platforms" }]}
        onNavigate={(path) => navigate(path)}
        onLogout={handleLogout}
      />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Select a Platform</h1>
          <p className="text-gray-600">Choose which marketplace you want to extract data from</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#32B4AC] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Price Governance + Analytics — Premium Banners */}
            {platforms.find(p => p.platform_id === 'price-governance' && p.active) && (() => {
              const pgPlatform = platforms.find(p => p.platform_id === 'price-governance');
              return (
              <div className="mb-8" data-testid="featured-banners">
                <button
                  onClick={() => selectPlatform(pgPlatform)}
                  className="w-full text-left rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50/80 via-white to-violet-50/80 hover:border-indigo-300 hover:shadow-lg transition-all group cursor-pointer p-5"
                  data-testid="platform-price-governance-featured"
                >
                  <div className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-md shrink-0 overflow-hidden">
                      {pgPlatform.icon_url ? (
                        <img src={pgPlatform.icon_url} alt="Price Governance" className="w-10 h-10 object-contain rounded-lg" />
                      ) : (
                        <Shield className="w-6 h-6 text-indigo-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-0.5">
                        <h3 className="font-bold text-gray-900">Price Governance</h3>
                        {pgPlatform.beta && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                            </span>
                            <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Beta</span>
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                          </span>
                          <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Live</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">Compare product prices across Amazon & Flipkart in real-time with visual comparisons</p>
                    </div>
                    <div className="hidden md:flex items-center gap-4 text-[11px] text-gray-500 shrink-0 border-l border-gray-200 pl-5 ml-2">
                      <div className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-indigo-400" /> Brands</div>
                      <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-indigo-400" /> Pincodes</div>
                      <div className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5 text-indigo-400" /> Compare</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </button>
              </div>
              );
            })()}

            {/* Extraction Platforms Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...platforms].filter(p => p.platform_id !== 'price-governance').sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0)).map((platform) => (
                <button
                  key={platform.platform_id}
                  onClick={() => selectPlatform(platform)}
                  className={`platform-card text-left p-6 rounded-xl border-2 transition-all ${
                    platform.active 
                      ? 'border-gray-200 hover:border-[#32B4AC] hover:shadow-lg bg-white cursor-pointer' 
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                  data-testid={`platform-${platform.platform_id}`}
                >
                  <div className="mb-4 flex items-start justify-between">
                    <PlatformIcon platform={platform} />
                    <div className="flex items-center gap-1.5">
                      {platform.beta && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                          </span>
                          <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Beta</span>
                        </span>
                      )}
                      {platform.active && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                          </span>
                          <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Live</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{platform.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{platform.description}</p>
                  {platform.active ? (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#32B4AC]">
                      Start extracting <ArrowRight className="w-4 h-4" />
                    </span>
                  ) : (
                    <span className="inline-block px-3 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                      Coming Soon
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Analytics — Premium Banner (bottom) */}
            <div className="mt-8">
              <button
                onClick={() => navigate('/dashboard/analytics')}
                className="w-full text-left rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50/80 via-white to-cyan-50/80 hover:border-teal-300 hover:shadow-lg transition-all group cursor-pointer p-5"
                data-testid="platform-analytics-featured"
              >
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-md shrink-0 overflow-hidden">
                    <BarChart3 className="w-6 h-6 text-[#32B4AC]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-0.5">
                      <h3 className="font-bold text-gray-900">Analytics</h3>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#32B4AC]/10 border border-[#32B4AC]/20">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32B4AC] opacity-60"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#32B4AC]"></span>
                        </span>
                        <span className="text-[10px] font-semibold text-[#32B4AC] uppercase tracking-wider">Live</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">Delivery matrix, market research deep-dives, and Google Trends demand curves — all in one console</p>
                  </div>
                  <div className="hidden md:flex items-center gap-4 text-[11px] text-gray-500 shrink-0 border-l border-gray-200 pl-5 ml-2">
                    <div className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-teal-500" /> Delivery</div>
                    <div className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-teal-500" /> Research</div>
                    <div className="flex items-center gap-1.5"><LineChart className="w-3.5 h-3.5 text-teal-500" /> Trends</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-teal-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all shrink-0" />
                </div>
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// Color options for platform settings
const COLOR_OPTIONS = [
  { bg: 'bg-orange-100', text: 'text-orange-600', label: 'Orange' },
  { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Blue' },
  { bg: 'bg-yellow-100', text: 'text-yellow-600', label: 'Yellow' },
  { bg: 'bg-purple-100', text: 'text-purple-600', label: 'Purple' },
  { bg: 'bg-red-100', text: 'text-red-600', label: 'Red' },
  { bg: 'bg-green-100', text: 'text-green-600', label: 'Green' },
  { bg: 'bg-pink-100', text: 'text-pink-600', label: 'Pink' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600', label: 'Cyan' },
];


const TutorialOverlay = ({ onComplete, onSkip, asinLabel = 'ASIN' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const TUTORIAL_STEPS = getTutorialSteps(asinLabel);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: '50%', left: '50%' });

  const updatePosition = useCallback(() => {
    const step = TUTORIAL_STEPS[currentStep];
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 10;
    setSpotlightRect({ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 });
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect.right + 320 < vw) {
      setTooltipPos({ top: Math.min(rect.top, vh - 220), left: rect.right + 20 });
    } else if (rect.left - 320 > 0) {
      setTooltipPos({ top: Math.min(rect.top, vh - 220), left: rect.left - 320 });
    } else {
      setTooltipPos({ top: Math.min(rect.bottom + 16, vh - 220), left: Math.max(16, (vw - 300) / 2) });
    }
  }, [currentStep]);

  useEffect(() => {
    const timer = setTimeout(updatePosition, 100);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => { clearTimeout(timer); window.removeEventListener('resize', updatePosition); window.removeEventListener('scroll', updatePosition, true); };
  }, [updatePosition]);

  const nextStep = () => { if (currentStep < TUTORIAL_STEPS.length - 1) setCurrentStep(prev => prev + 1); else onComplete(); };
  const prevStep = () => { if (currentStep > 0) setCurrentStep(prev => prev - 1); };
  const step = TUTORIAL_STEPS[currentStep];

  return (
    <div data-testid="tutorial-overlay">
      {spotlightRect && <div className="tutorial-spotlight" style={{ top: spotlightRect.top, left: spotlightRect.left, width: spotlightRect.width, height: spotlightRect.height }} />}
      <div className="tutorial-tooltip" style={{ top: tooltipPos.top, left: tooltipPos.left }} data-testid="tutorial-tooltip">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#32B4AC] rounded-full flex items-center justify-center text-white text-xs font-bold">{currentStep + 1}</div>
            <h3 className="font-bold text-gray-900 text-sm">{step.title}</h3>
          </div>
          <button onClick={onSkip} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="tutorial-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-gray-600 mb-4 leading-relaxed">{step.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">{TUTORIAL_STEPS.map((_, i) => <div key={i} className={`tutorial-step-dot ${i === currentStep ? 'active' : 'inactive'}`} />)}</div>
          <div className="flex items-center gap-2">
            {currentStep > 0 && <button onClick={prevStep} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1" data-testid="tutorial-back">Back</button>}
            <button onClick={nextStep} className="bg-[#32B4AC] text-white text-xs font-semibold px-3.5 py-1.5 rounded-lg hover:bg-[#29958E] transition-colors flex items-center gap-1" data-testid="tutorial-next">
              {currentStep < TUTORIAL_STEPS.length - 1 ? <>Next <ChevronRight className="w-3 h-3" /></> : 'Got it!'}
            </button>
          </div>
        </div>
        {currentStep === 0 && <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600 mt-3 w-full text-center" data-testid="tutorial-skip">Skip tutorial</button>}
      </div>
    </div>
  );
};


// ─── PRODUCT PICKER MODAL ───
const extractIdentifier = (url, platformId) => {
  if (!url) return null;
  if (platformId === 'amazon') {
    // Extract ASIN from Amazon URL: /dp/ASIN or /gp/product/ASIN
    const m = url.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i);
    return m ? m[1] : null;
  }
  if (platformId === 'flipkart') {
    // Extract FSN/PID from Flipkart URL: /p/itm...?pid=XXXXX or /dl/XXXXX
    const pidMatch = url.match(/[?&]pid=([A-Za-z0-9]+)/);
    if (pidMatch) return pidMatch[1];
    // Try extracting from /p/ path segment
    const pMatch = url.match(/\/p\/([A-Za-z0-9]+)/);
    if (pMatch) return pMatch[1];
    // Last path segment fallback
    const parts = url.replace(/[?#].*/, '').split('/').filter(Boolean);
    return parts[parts.length - 1] || null;
  }
  return null;
};

const ProductPickerModal = ({ open, onOpenChange, platformId, onSelect }) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formFactorFilter, setFormFactorFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [expandedPickerBrands, setExpandedPickerBrands] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    setExpandedPickerBrands({});
    setSearch('');
    setBrandFilter('all');
    setTypeFilter('all');
    setFormFactorFilter('all');
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/brands`),
    ]).then(([pRes, bRes]) => {
      setProducts(pRes.data || []);
      setBrands(bRes.data || []);
    }).catch(() => {
      toast.error("Failed to load products");
    }).finally(() => setLoading(false));
  }, [open]);

  // Only products with a URL for the current platform
  const eligible = products.filter(p => {
    const url = (p.platform_urls || {})[platformId];
    if (!url) return false;
    if (platformId === 'blinkit') return true; // Blinkit uses raw URLs
    const id = extractIdentifier(url, platformId);
    return !!id;
  });

  const brandMap = {};
  brands.forEach(b => { brandMap[b.brand_id] = b.name; });

  // Gather unique form factors and types from eligible products
  const allFormFactors = [...new Set(eligible.map(p => p.form_factor).filter(Boolean))].sort();
  const allTypes = [...new Set(eligible.map(p => p.product_type).filter(Boolean))].sort();

  const filtered = eligible.filter(p => {
    if (brandFilter !== 'all' && p.brand_id !== brandFilter) return false;
    if (typeFilter !== 'all' && p.product_type !== typeFilter) return false;
    if (formFactorFilter !== 'all' && p.form_factor !== formFactorFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const bName = (brandMap[p.brand_id] || '').toLowerCase();
      return p.name.toLowerCase().includes(q) || bName.includes(q) || (p.ean || '').includes(q);
    }
    return true;
  });

  // Group by brand
  const grouped = {};
  filtered.forEach(p => {
    const bName = brandMap[p.brand_id] || 'Unknown';
    if (!grouped[bName]) grouped[bName] = [];
    grouped[bName].push(p);
  });

  const toggleProduct = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleBrand = (brandProducts) => {
    const pids = brandProducts.map(p => p.product_id);
    const allSelected = pids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      pids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleConfirm = () => {
    if (platformId === 'blinkit') {
      // Return full product objects with URL for Blinkit
      const items = [];
      eligible.forEach(p => {
        if (!selected.has(p.product_id)) return;
        const url = (p.platform_urls || {})[platformId];
        if (url) items.push({ url, name: p.name, image_url: p.image_url });
      });
      onSelect(items);
    } else {
      const identifiers = [];
      eligible.forEach(p => {
        if (!selected.has(p.product_id)) return;
        const url = (p.platform_urls || {})[platformId];
        const id = extractIdentifier(url, platformId);
        if (id) identifiers.push(id);
      });
      onSelect(identifiers);
    }
    onOpenChange(false);
  };

  const isBk = platformId === 'blinkit';
  const label = isBk ? 'Product' : platformId === 'flipkart' ? 'FSN' : 'ASIN';
  const activeBrands = brands.filter(b => eligible.some(p => p.brand_id === b.brand_id));
  const hasActiveFilters = brandFilter !== 'all' || typeFilter !== 'all' || formFactorFilter !== 'all' || !!search;

  const TYPE_COLORS = {
    'Unit': { active: 'bg-blue-100 border-blue-400 text-blue-700', dot: 'bg-blue-500' },
    'Combos': { active: 'bg-amber-100 border-amber-400 text-amber-700', dot: 'bg-amber-500' },
    'Multi-Brand Combo': { active: 'bg-purple-100 border-purple-400 text-purple-700', dot: 'bg-purple-500' },
    'Merchandise Combo': { active: 'bg-pink-100 border-pink-400 text-pink-700', dot: 'bg-pink-500' },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[#32B4AC]" />
            Pick Products
          </DialogTitle>
          <DialogDescription>
            Select products to auto-fill {label}s. {eligible.length} products with {platformId === 'flipkart' ? 'Flipkart' : 'Amazon'} URLs available.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-2.5">
          {/* Search */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, brand, or EAN..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
              data-testid="product-picker-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter Panel */}
          <div className="bg-gray-50/70 rounded-xl border border-gray-100 p-3 space-y-2.5">
            {/* Brand */}
            {activeBrands.length > 1 && (
              <div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Brand</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setBrandFilter('all')}
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                      brandFilter === 'all'
                        ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`} data-testid="product-picker-brand-all">
                    All
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${brandFilter === 'all' ? 'bg-white/60' : 'bg-gray-100'}`}>{eligible.length}</span>
                  </button>
                  {activeBrands.map(b => {
                    const count = eligible.filter(p => p.brand_id === b.brand_id).length;
                    return (
                      <button key={b.brand_id} onClick={() => setBrandFilter(brandFilter === b.brand_id ? 'all' : b.brand_id)}
                        className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                          brandFilter === b.brand_id
                            ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`} data-testid={`product-picker-brand-${b.brand_id}`}>
                        {b.name}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${brandFilter === b.brand_id ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Type */}
            {allTypes.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Type</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setTypeFilter('all')}
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                      typeFilter === 'all'
                        ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    All
                  </button>
                  {allTypes.map(t => {
                    const tc = TYPE_COLORS[t] || { active: 'bg-gray-100 border-gray-400 text-gray-700', dot: 'bg-gray-500' };
                    const count = eligible.filter(p => p.product_type === t).length;
                    return (
                      <button key={t} onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
                        className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                          typeFilter === t ? `${tc.active} shadow-sm` : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`} data-testid={`product-picker-type-${t}`}>
                        <div className={`w-2 h-2 rounded-full ${tc.dot}`} />
                        {t}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${typeFilter === t ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Form Factor */}
            {allFormFactors.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Form Factor</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setFormFactorFilter('all')}
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                      formFactorFilter === 'all'
                        ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    All
                  </button>
                  {allFormFactors.map(ff => {
                    const count = eligible.filter(p => p.form_factor === ff).length;
                    return (
                      <button key={ff} onClick={() => setFormFactorFilter(formFactorFilter === ff ? 'all' : ff)}
                        className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                          formFactorFilter === ff
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`} data-testid={`product-picker-ff-${ff}`}>
                        {ff}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${formFactorFilter === ff ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Status bar */}
          <div className="flex items-center justify-between px-0.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-400">
                {filtered.length} of {eligible.length} products
              </span>
              {hasActiveFilters && (
                <button onClick={() => { setSearch(''); setBrandFilter('all'); setTypeFilter('all'); setFormFactorFilter('all'); }}
                  className="text-[10px] text-red-400 hover:text-red-600 flex items-center gap-0.5">
                  <X className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>
            <span className="text-[11px] font-medium text-[#32B4AC]" data-testid="picker-selected-count">
              {selected.size} selected
            </span>
          </div>
        </div>

        {/* Product list */}
        <div className="max-h-[360px] overflow-y-auto border border-gray-100 rounded-lg" data-testid="product-picker-list">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#32B4AC]" /></div>
          ) : Object.keys(grouped).length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              {eligible.length === 0
                ? `No products have ${platformId === 'flipkart' ? 'Flipkart' : 'Amazon'} URLs configured. Add them in Control Panel.`
                : 'No products match your search.'}
            </div>
          ) : (
            Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([brandName, prods]) => {
              const allChecked = prods.every(p => selected.has(p.product_id));
              const someChecked = prods.some(p => selected.has(p.product_id));
              const isExpanded = expandedPickerBrands[brandName] || false;
              return (
                <div key={brandName}>
                  {/* Brand header */}
                  <div
                    className="sticky top-0 z-10 flex items-center gap-2.5 px-4 py-2.5 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                    data-testid={`picker-brand-${brandName}`}
                  >
                    <Checkbox
                      checked={allChecked}
                      className={`h-3.5 w-3.5 ${allChecked || someChecked ? 'data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC]' : ''}`}
                      onCheckedChange={() => toggleBrand(prods)}
                    />
                    <div className="flex-1 flex items-center gap-2" onClick={() => setExpandedPickerBrands(prev => ({ ...prev, [brandName]: !prev[brandName] }))}>
                      <div className="text-gray-400 transition-transform duration-150" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{brandName}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{prods.filter(p => selected.has(p.product_id)).length}/{prods.length}</span>
                    </div>
                  </div>
                  {/* Products - collapsed by default */}
                  {isExpanded && prods.map(p => {
                    const url = (p.platform_urls || {})[platformId];
                    const identifier = isBk ? null : extractIdentifier(url, platformId);
                    const isChecked = selected.has(p.product_id);
                    return (
                      <label
                        key={p.product_id}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 cursor-pointer transition-colors ${
                          isChecked ? 'bg-[#F0FDFA]' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`picker-product-${p.product_id}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleProduct(p.product_id)}
                          className="h-3.5 w-3.5 data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC]"
                        />
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover border border-gray-200" onError={e => e.target.style.display = 'none'} />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center"><Package className="w-3.5 h-3.5 text-gray-400" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">{p.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] text-gray-400 font-mono">{isBk ? url?.replace('https://blinkit.com', '').slice(0,40) : `${label}: ${identifier}`}</span>
                            {p.product_type && (
                              <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                p.product_type === 'Unit' ? 'bg-blue-50 text-blue-600' :
                                p.product_type === 'Combos' ? 'bg-amber-50 text-amber-600' :
                                p.product_type === 'Multi-Brand Combo' ? 'bg-purple-50 text-purple-600' :
                                'bg-pink-50 text-pink-600'
                              }`}>{p.product_type}</span>
                            )}
                            {p.form_factor && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-gray-50 text-gray-500 font-medium">{p.form_factor}</span>
                            )}
                          </div>
                        </div>
                        {p.decided_prices?.[platformId] && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 tabular-nums shrink-0">
                            MAP ₹{p.decided_prices[platformId]}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-500">Cancel</Button>
            <Button
              onClick={() => { setSelected(new Set(filtered.map(p => p.product_id))); }}
              variant="outline"
              className="text-xs"
              data-testid="picker-select-all"
            >Select All Shown</Button>
            <Button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="btn-accent"
              data-testid="picker-confirm"
            >
              Add {selected.size} {label}{selected.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ─── PINCODE PICKER MODAL ───
const KeywordPickerModal = ({ open, onOpenChange, onSelect }) => {
  const [allKeywords, setAllKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    setSearch('');
    setCategoryFilter('all');
    axios.get(`${API}/offline-keywords`).then(r => {
      setAllKeywords(r.data || []);
    }).catch(() => {
      toast.error("Failed to load keyword library");
    }).finally(() => setLoading(false));
  }, [open]);

  const categories = ['all', ...Array.from(new Set(allKeywords.map(k => k.category || 'Uncategorized'))).sort()];

  const filtered = allKeywords.filter(k => {
    if (categoryFilter !== 'all' && (k.category || 'Uncategorized') !== categoryFilter) return false;
    if (search.trim() && !(k.keyword || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAllFiltered = () => {
    const allIds = filtered.map(k => k.id);
    const allSelected = allIds.every(i => selected.has(i));
    setSelected(prev => {
      const n = new Set(prev);
      allIds.forEach(i => { if (allSelected) n.delete(i); else n.add(i); });
      return n;
    });
  };

  const handleConfirm = () => {
    const picked = allKeywords.filter(k => selected.has(k.id)).map(k => k.keyword);
    if (!picked.length) { toast.error("Select at least one keyword"); return; }
    onSelect(picked);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="keyword-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-[#32B4AC]" /> Pick Keywords
          </DialogTitle>
          <DialogDescription>
            Choose from your saved keyword library. Manage these in Control Panel → Keywords.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search keywords…"
                className="h-9 pl-7 text-xs"
                data-testid="keyword-picker-search"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-9 text-xs px-2.5 border border-gray-200 rounded-md bg-white"
              data-testid="keyword-picker-category-filter"
            >
              {categories.map(c => (
                <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-500 px-1">
            <span data-testid="keyword-picker-selected-count">{selected.size} selected · {filtered.length} shown</span>
            <button onClick={selectAllFiltered} className="text-[#32B4AC] hover:underline" data-testid="keyword-picker-select-all">
              {filtered.every(k => selected.has(k.id)) && filtered.length > 0 ? 'Unselect visible' : 'Select all visible'}
            </button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : allKeywords.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">
              No keywords saved yet.<br />
              <span className="text-[11px]">Add some in Control Panel → Keywords.</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">No matches.</div>
          ) : (
            <div className="max-h-[340px] overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100" data-testid="keyword-picker-list">
              {filtered.map(k => {
                const isSel = selected.has(k.id);
                const { icon: RowIcon, tone } = getCategoryIcon(k.category);
                return (
                  <label
                    key={k.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${isSel ? 'bg-[#F0FDFA]' : ''}`}
                    data-testid={`keyword-picker-row-${k.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(k.id)}
                      className="h-3.5 w-3.5 accent-[#32B4AC] cursor-pointer"
                    />
                    <span className="text-sm text-gray-800 flex-1">{k.keyword}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] uppercase tracking-wide ${tone}`}>
                      <RowIcon className="w-3 h-3" />
                      <span>{k.category || 'Uncategorized'}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-[#32B4AC] hover:bg-[#2a9d96]"
            onClick={handleConfirm}
            disabled={selected.size === 0}
            data-testid="keyword-picker-confirm"
          >
            Add {selected.size > 0 ? `(${selected.size})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PincodePickerModal = ({ open, onOpenChange, onSelect }) => {
  const [allPincodes, setAllPincodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [expandedStates, setExpandedStates] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    setSearch('');
    setStateFilter('all');
    setExpandedStates({});
    axios.get(`${API}/pincodes`).then(r => {
      setAllPincodes(r.data || []);
    }).catch(() => {
      toast.error("Failed to load pincodes");
    }).finally(() => setLoading(false));
  }, [open]);

  // Group by state → city
  const grouped = {};
  allPincodes.forEach(p => {
    if (!grouped[p.state]) grouped[p.state] = {};
    if (!grouped[p.state][p.city]) grouped[p.state][p.city] = [];
    grouped[p.state][p.city].push(p.pincode);
  });

  const states = Object.keys(grouped).sort();

  // Filter
  const matchesSearch = (pincode, city, state) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return pincode.includes(q) || city.toLowerCase().includes(q) || state.toLowerCase().includes(q);
  };

  const filteredStates = states.filter(st => {
    if (stateFilter !== 'all' && st !== stateFilter) return false;
    const cities = grouped[st];
    return Object.entries(cities).some(([city, pins]) =>
      pins.some(pin => matchesSearch(pin, city, st))
    );
  });

  const totalFiltered = filteredStates.reduce((acc, st) => {
    return acc + Object.values(grouped[st]).reduce((a, pins) =>
      a + pins.filter(pin => matchesSearch(pin, Object.keys(grouped[st]).find(c => grouped[st][c].includes(pin)) || '', st)).length, 0);
  }, 0);

  const togglePincode = (pin) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pin)) next.delete(pin); else next.add(pin);
      return next;
    });
  };

  const toggleCity = (pins) => {
    const allIn = pins.every(p => selected.has(p));
    setSelected(prev => {
      const next = new Set(prev);
      pins.forEach(p => allIn ? next.delete(p) : next.add(p));
      return next;
    });
  };

  const toggleState = (state) => {
    const allPins = Object.values(grouped[state]).flat();
    const allIn = allPins.every(p => selected.has(p));
    setSelected(prev => {
      const next = new Set(prev);
      allPins.forEach(p => allIn ? next.delete(p) : next.add(p));
      return next;
    });
  };

  const handleConfirm = () => {
    onSelect([...selected]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#32B4AC]" />
            Pick Pincodes
          </DialogTitle>
          <DialogDescription>
            Select pincodes from your master list. {allPincodes.length} pincodes across {states.length} states.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by pincode, city, or state..."
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
                data-testid="pincode-picker-search"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* State filter chips */}
          {states.length > 1 && (
            <div className="bg-gray-50/70 rounded-xl border border-gray-100 p-3">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">State</div>
              <div className="flex items-center gap-1.5 flex-wrap max-h-[80px] overflow-y-auto">
                <button onClick={() => setStateFilter('all')}
                  className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                    stateFilter === 'all'
                      ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`} data-testid="pincode-picker-state-all">
                  All
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${stateFilter === 'all' ? 'bg-white/60' : 'bg-gray-100'}`}>{allPincodes.length}</span>
                </button>
                {states.map(st => {
                  const count = Object.values(grouped[st]).flat().length;
                  return (
                    <button key={st} onClick={() => setStateFilter(stateFilter === st ? 'all' : st)}
                      className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                        stateFilter === st
                          ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                      }`} data-testid={`pincode-picker-state-${st}`}>
                      {st}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${stateFilter === st ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status bar */}
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[11px] text-gray-400">{totalFiltered} pincodes shown</span>
            <span className="text-[11px] font-medium text-[#32B4AC]" data-testid="pincode-picker-selected-count">{selected.size} selected</span>
          </div>
        </div>

        {/* Pincode list — state → city → pincodes */}
        <div className="max-h-[340px] overflow-y-auto border border-gray-100 rounded-lg" data-testid="pincode-picker-list">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#32B4AC]" /></div>
          ) : filteredStates.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              {allPincodes.length === 0 ? 'No pincodes in master list. Add them in Control Panel.' : 'No pincodes match your search.'}
            </div>
          ) : (
            filteredStates.map(state => {
              const cities = grouped[state];
              const statePins = Object.values(cities).flat();
              const stateAllSelected = statePins.every(p => selected.has(p));
              const stateExpanded = expandedStates[state] || false;
              const selectedInState = statePins.filter(p => selected.has(p)).length;
              return (
                <div key={state}>
                  {/* State header */}
                  <div className="sticky top-0 z-10 flex items-center gap-2.5 px-4 py-2.5 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                    data-testid={`pincode-state-${state}`}>
                    <Checkbox
                      checked={stateAllSelected}
                      className={`h-3.5 w-3.5 ${stateAllSelected ? 'data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC]' : ''}`}
                      onCheckedChange={() => toggleState(state)}
                    />
                    <div className="flex-1 flex items-center gap-2" onClick={() => setExpandedStates(prev => ({ ...prev, [state]: !prev[state] }))}>
                      <div className="text-gray-400 transition-transform duration-150" style={{ transform: stateExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{state}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{selectedInState}/{statePins.length}</span>
                    </div>
                  </div>
                  {/* Cities — collapsed by default */}
                  {stateExpanded && Object.entries(cities).sort(([a], [b]) => a.localeCompare(b)).map(([city, pins]) => {
                    const filteredPins = pins.filter(pin => matchesSearch(pin, city, state));
                    if (filteredPins.length === 0) return null;
                    const cityAllSelected = filteredPins.every(p => selected.has(p));
                    return (
                      <div key={`${state}-${city}`} className="border-b border-gray-50">
                        {/* City header */}
                        <div className="flex items-center gap-2.5 px-4 pl-10 py-2 hover:bg-gray-50/80 cursor-pointer transition-colors"
                          onClick={() => toggleCity(filteredPins)}
                          data-testid={`pincode-city-${city}`}>
                          <Checkbox
                            checked={cityAllSelected}
                            className={`h-3 w-3 ${cityAllSelected ? 'data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC]' : ''}`}
                            onCheckedChange={() => toggleCity(filteredPins)}
                          />
                          <span className="text-[11px] font-medium text-gray-600">{city}</span>
                          <span className="text-[10px] text-gray-400 ml-auto">{filteredPins.filter(p => selected.has(p)).length}/{filteredPins.length}</span>
                        </div>
                        {/* Individual pincodes */}
                        <div className="flex flex-wrap gap-1.5 px-4 pl-16 pb-2">
                          {filteredPins.map(pin => {
                            const isChecked = selected.has(pin);
                            return (
                              <button
                                key={pin}
                                onClick={() => togglePincode(pin)}
                                className={`text-[11px] font-mono px-2.5 py-1 rounded-md border transition-all duration-100 ${
                                  isChecked
                                    ? 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] font-semibold shadow-sm'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                }`}
                                data-testid={`pincode-chip-${pin}`}
                              >
                                {pin}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-500">Cancel</Button>
            <Button
              onClick={() => { const all = filteredStates.flatMap(st => Object.values(grouped[st]).flat()); setSelected(new Set(all)); }}
              variant="outline"
              className="text-xs"
              data-testid="pincode-picker-select-all"
            >Select All Shown</Button>
            <Button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="btn-accent"
              data-testid="pincode-picker-confirm"
            >
              Add {selected.size} Pincode{selected.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};





// ─── Live Activity Ticker (marquee) ───
// Shows users how the scraper is working behind the scenes — especially useful when
// the residential proxy hits Amazon's bot-block and has to rotate IPs.
const ActivityTicker = ({ logs = [], isLoading = false, variant = 'default' }) => {
  // Prefer recent live logs; if none, show how the scraper works (edu copy)
  const liveMessages = logs.slice(-8).map(l => l.message?.replace(/^\[[A-Z]+\]\s*/, '')).filter(Boolean);
  const educational = [
    '🚀 Launching headless Chromium with residential proxy',
    '🌐 Rotating Indian IP pool via Webshare',
    '🛡️ Applying stealth fingerprint patches',
    '📍 Setting pincode via AJAX (no UI clicks)',
    '⚡ Reusing browser context across pincodes for speed',
    '🔁 On bot-block: fast-fail, rotate IP, retry',
    '💾 Saving each result incrementally (no data loss)',
    '🔔 Audio alert fires when your job completes',
    '📦 Extracting price, seller, delivery days, MRP',
    '🛰️ Apify handles Buy Box monitoring without pincode friction',
  ];
  const messages = liveMessages.length > 0 ? liveMessages : educational;
  // Duplicate list so the seamless loop has content across the full track
  const doubled = [...messages, ...messages];

  const typeColor = (msg) => {
    const m = msg.toLowerCase();
    if (m.includes('error') || m.includes('failed') || m.includes('block')) return 'text-orange-300';
    if (m.includes('complete') || m.includes('success') || m.includes('done')) return 'text-emerald-300';
    if (m.includes('rotat') || m.includes('retry')) return 'text-amber-300';
    return 'text-gray-300';
  };

  return (
    <div className="activity-ticker" data-testid="activity-ticker">
      <div className="activity-ticker-badge">
        <span className="dot" />
        {isLoading ? 'LIVE' : 'HOW IT WORKS'}
      </div>
      <div className="activity-ticker-track">
        {doubled.map((msg, i) => (
          <span key={i} className={`text-[12px] font-mono ${typeColor(msg)}`}>
            <span className="text-[#32B4AC] mr-1.5">›</span>{msg}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── ANALYTICS CHOOSER — landing screen for Amazon › Analytics ─────────
// Renders a two-card picker (Delivery vs Market Research). Clicking a card
// mounts the corresponding module on its own dedicated screen. A back arrow
// at the top returns to the chooser. Per product spec: no pre-selection on
// first load, no stacked-view fallback.
const AnalyticsChooser = ({ active: controlledActive, onActiveChange }) => {
  const [uncontrolledActive, setUncontrolledActive] = useState(null); // 'delivery' | 'market' | null
  const active = controlledActive !== undefined ? controlledActive : uncontrolledActive;
  const setActive = (v) => {
    if (onActiveChange) onActiveChange(v);
    else setUncontrolledActive(v);
  };

  if (active === 'delivery') {
    return (
      <div data-testid="analytics-module-delivery">
        <button
          onClick={() => setActive(null)}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="analytics-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Analytics
        </button>
        <DeliveryAnalytics />
      </div>
    );
  }
  if (active === 'market') {
    return (
      <div data-testid="analytics-module-market">
        <button
          onClick={() => setActive(null)}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="analytics-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Analytics
        </button>
        <MarketResearchAnalytics />
      </div>
    );
  }
  if (active === 'trends') {
    return (
      <div data-testid="analytics-module-trends">
        <button
          onClick={() => setActive(null)}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="analytics-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Analytics
        </button>
        <SearchTrendsModule />
      </div>
    );
  }
  if (active === 'offline') {
    return (
      <div data-testid="analytics-module-offline">
        <button
          onClick={() => setActive(null)}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="analytics-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Analytics
        </button>
        <OfflineMasterAnalytics />
      </div>
    );
  }

  // Chooser landing screen — light theme on HORIZON brand teal (#32B4AC)
  // Both cards anchored to the brand hue; Delivery uses a slightly cooler
  // cyan-teal, Market Research a slightly deeper teal — keeps the family
  // recognisable while letting users tell the two modules apart.
  const cards = [
    {
      key: 'delivery',
      title: 'Delivery Analytics',
      tagline: 'Pincode × SKU delivery matrix',
      desc: 'Track Fast (same-day/express) promise coverage across pincodes and SKUs. Filter by product, export CSV, snapshot history.',
      icon: BarChart3,
      hue: '184',   // cooler cyan-teal sibling
      stats: [
        { label: 'Live', value: 'matrix' },
        { label: 'Filter', value: 'by SKU' },
        { label: 'Export', value: 'CSV' },
      ],
    },
    {
      key: 'market',
      title: 'Market Research',
      tagline: 'Bestseller intelligence · multi-marketplace',
      desc: 'Deep-dive into category structure, brand share, pricing landscape, and demand signals across Amazon India, US, and beyond.',
      icon: TrendingUp,
      hue: '170',   // brand teal (#32B4AC ≈ 176°) shifted slightly warmer
      stats: [
        { label: 'Markets', value: (
          <span className="inline-flex items-center gap-1 align-middle">
            <img src="https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/in.svg" alt="India"
                 width={11} height={11} draggable={false}
                 className="rounded-full select-none" style={{ width: '11px', height: '11px' }} />
            <span className="opacity-50">·</span>
            <img src="https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/us.svg" alt="USA"
                 width={11} height={11} draggable={false}
                 className="rounded-full select-none" style={{ width: '11px', height: '11px' }} />
          </span>
        ) },
        { label: 'Dive', value: 'inline' },
        { label: 'Export', value: 'PDF' },
      ],
    },
    {
      key: 'trends',
      title: 'Search Trends',
      tagline: 'Google Trends · keyword demand curves',
      desc: 'Upload Google Trends CSVs to visualise keyword interest by category, with Creatine-normalised comparisons across the supplement landscape.',
      icon: LineChart,
      hue: '258',   // violet/indigo sibling — distinct from teal family but futuristic
      stats: [
        { label: 'Source', value: 'CSV' },
        { label: 'Norm', value: 'Creatine=100' },
        { label: 'Charts', value: 'Recharts' },
      ],
    },
    {
      key: 'offline',
      title: 'Offline Market Intelligence',
      tagline: 'Store master DB · Pan-India retail coverage',
      desc: 'Explore the cleaned offline store master — India coverage map, KPI tiles, top categories/keywords/pincodes leaderboards, and paginated store rows.',
      icon: MapPin,
      hue: '30',   // warm amber/orange sibling — distinguishes the retail/offline surface
      stats: [
        { label: 'Source', value: 'Cleaned + Live' },
        { label: 'Coverage', value: 'India map' },
        { label: 'Export', value: 'CSV' },
      ],
    },
  ];

  return (
    <div className="py-4 relative" data-testid="analytics-chooser">
      {/* Local keyframes / utilities — scoped here so they don't leak globally */}
      <style>{`
        @keyframes ac-border-spin {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes ac-sweep {
          0%   { transform: translateX(-150%) skewX(-25deg); opacity: 0; }
          20%  { opacity: 0.7; }
          100% { transform: translateX(250%) skewX(-25deg); opacity: 0; }
        }
        @keyframes ac-pulse-dot {
          0%, 100% { opacity: 0.35; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.15); }
        }
        @keyframes ac-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        .ac-card { position: relative; isolation: isolate; }
        .ac-card::before {
          content: ''; position: absolute; inset: -1.5px; border-radius: 22px; z-index: -1;
          background: conic-gradient(from var(--angle, 0deg),
            transparent 0%, transparent 35%,
            hsl(var(--ac-hue) 70% 50% / 0.95) 50%,
            transparent 65%, transparent 100%);
          background-size: 200% 200%;
          opacity: 0; transition: opacity 0.5s ease;
        }
        .ac-card:hover::before {
          opacity: 1;
          animation: ac-border-spin 3.2s linear infinite;
        }
        .ac-card .ac-sweep {
          position: absolute; top: 0; bottom: 0; left: 0; width: 35%;
          background: linear-gradient(110deg,
            transparent 0%,
            hsl(var(--ac-hue) 70% 90% / 0.4) 45%,
            hsl(var(--ac-hue) 70% 95% / 0.7) 50%,
            hsl(var(--ac-hue) 70% 90% / 0.4) 55%,
            transparent 100%);
          pointer-events: none; opacity: 0; mix-blend-mode: screen;
        }
        .ac-card:hover .ac-sweep {
          animation: ac-sweep 1.1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .ac-grid-bg {
          background-image:
            linear-gradient(hsl(var(--ac-hue) 35% 60% / 0.08) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--ac-hue) 35% 60% / 0.08) 1px, transparent 1px);
          background-size: 28px 28px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
        }
        .ac-icon-glow {
          box-shadow:
            0 0 0 1px hsl(var(--ac-hue) 70% 55% / 0.35),
            0 10px 24px -8px hsl(var(--ac-hue) 70% 45% / 0.45),
            inset 0 1px 0 0 rgba(255,255,255,0.45);
        }
        .ac-card:hover .ac-icon-glow {
          box-shadow:
            0 0 0 1px hsl(var(--ac-hue) 70% 50% / 0.55),
            0 14px 36px -6px hsl(var(--ac-hue) 70% 45% / 0.6),
            0 0 0 6px hsl(var(--ac-hue) 75% 88% / 0.45),
            inset 0 1px 0 0 rgba(255,255,255,0.55);
        }
        .ac-dot { animation: ac-pulse-dot 2.4s ease-in-out infinite; }
        .ac-float { animation: ac-float 5s ease-in-out infinite; }
      `}</style>

      <div className="mb-10 px-1 relative">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#32B4AC] ac-dot" />
          <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-500">Analytics · Console</span>
        </div>
        <h2 className="text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
          What do you want to <span className="bg-gradient-to-r from-[#32B4AC] via-teal-500 to-cyan-500 bg-clip-text text-transparent">explore</span>?
        </h2>
        <p className="text-sm text-slate-500 mt-3 font-mono">Pick a module · you can switch any time</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 px-1">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              data-testid={`analytics-card-${c.key}`}
              style={{ '--ac-hue': c.hue }}
              className="ac-card group relative text-left rounded-[20px] overflow-hidden p-7 transition-transform duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#32B4AC]/40 focus:ring-offset-2"
            >
              {/* Light surface — soft brand-tinted gradient with subtle depth */}
              <div
                className="absolute inset-0 rounded-[20px] border border-slate-200/70 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.2)]"
                style={{
                  background: `linear-gradient(135deg, #ffffff 0%, hsl(${c.hue} 55% 96%) 38%, hsl(${c.hue} 60% 90%) 72%, hsl(${c.hue} 65% 84%) 100%)`,
                }}
              />
              {/* Brand-tinted radial washes — stronger so the card has presence */}
              <div
                className="absolute inset-0 rounded-[20px] transition-opacity duration-500 opacity-100 group-hover:opacity-100"
                style={{
                  background: `radial-gradient(85% 60% at 100% 0%, hsl(${c.hue} 75% 70% / 0.45) 0%, transparent 65%), radial-gradient(60% 60% at 0% 100%, hsl(${c.hue} 70% 60% / 0.32) 0%, transparent 70%), radial-gradient(50% 40% at 50% 50%, hsl(${c.hue} 60% 78% / 0.2) 0%, transparent 75%)`,
                }}
              />
              {/* Top sheen highlight — fakes a glass curve */}
              <div className="absolute inset-x-0 top-0 h-32 rounded-t-[20px] bg-gradient-to-b from-white/70 via-white/20 to-transparent pointer-events-none" />
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 rounded-[20px] ac-grid-bg" />
              {/* Diagonal sweep on hover */}
              <span className="ac-sweep" />

              {/* Content */}
              <div className="relative">
                {/* Top row — icon + status pulse */}
                <div className="flex items-start justify-between mb-7">
                  <div
                    className="ac-icon-glow ac-float w-14 h-14 rounded-2xl flex items-center justify-center text-white transition-shadow duration-500"
                    style={{
                      background: `linear-gradient(135deg, hsl(${c.hue} 70% 52%) 0%, hsl(${c.hue} 75% 40%) 100%)`,
                    }}
                  >
                    <Icon className="w-7 h-7" strokeWidth={2.2} />
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-emerald-600/90 bg-emerald-50/80 border border-emerald-200/60 rounded-full px-2 py-0.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 ac-dot" />
                    Online
                  </div>
                </div>

                <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-1.5"
                     style={{ color: `hsl(${c.hue} 70% 35%)` }}>
                  {c.tagline}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight mb-2.5">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6 line-clamp-2">{c.desc}</p>

                {/* Stat chips — tinted to match the card's hue */}
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {c.stats.map(s => (
                    <span
                      key={s.label}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono border bg-white/70 backdrop-blur-sm"
                      style={{
                        borderColor: `hsl(${c.hue} 50% 80%)`,
                        color: `hsl(${c.hue} 60% 28%)`,
                      }}
                    >
                      <span className="opacity-60">{s.label}:</span>
                      <span className="font-semibold" style={{ color: `hsl(${c.hue} 75% 25%)` }}>{s.value}</span>
                    </span>
                  ))}
                </div>

                {/* Open hint with arrow that slides */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200/60">
                  <div
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    style={{ color: `hsl(${c.hue} 70% 30%)` }}
                  >
                    <span>Open module</span>
                    <span className="relative inline-flex items-center">
                      <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                      <span
                        className="absolute -right-1 -bottom-1 w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: `hsl(${c.hue} 75% 50%)`, boxShadow: `0 0 8px 2px hsl(${c.hue} 80% 55% / 0.7)` }}
                      />
                    </span>
                  </div>
                  <span className="text-[9px] font-mono text-slate-400 tracking-wider">
                    {c.key === 'delivery' ? '01' : c.key === 'market' ? '02' : c.key === 'trends' ? '03' : '04'} / 04
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="mt-8 px-1 flex items-center gap-2 text-[10px] font-mono text-slate-500">
        <span className="inline-block w-1 h-1 rounded-full bg-slate-400" />
        <span>more modules coming · Buy Box Monitor · Price Governance</span>
      </div>
    </div>
  );
};

// ─── DELIVERY ANALYTICS — Days × ASIN matrix ───
const DeliveryAnalytics = () => {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(thirtyDaysAgo);
  const [end, setEnd] = useState(today);
  const daysType = 'fast';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showDetailed, setShowDetailed] = useState(false);
  // ASIN filter — null = include ALL; explicit array (even empty) = whitelist
  const [selectedAsins, setSelectedAsins] = useState(null);
  const [asinFilterOpen, setAsinFilterOpen] = useState(false);
  const [asinFilterQuery, setAsinFilterQuery] = useState('');
  const asinFilterBtnRef = useRef(null);
  const [asinFilterPos, setAsinFilterPos] = useState({ top: 0, left: 0 });

  // When dropdown opens, position it just below the trigger button (uses fixed positioning
  // to escape the parent .dashboard-card's overflow:hidden which was clipping it).
  useLayoutEffect(() => {
    if (asinFilterOpen && asinFilterBtnRef.current) {
      const r = asinFilterBtnRef.current.getBoundingClientRect();
      setAsinFilterPos({ top: r.bottom + 4, left: r.left });
    }
  }, [asinFilterOpen]);

  // Close on outside click + on Escape, and reposition on resize/scroll.
  useEffect(() => {
    if (!asinFilterOpen) return;
    const onDown = (e) => {
      const panel = document.querySelector('[data-testid="analytics-product-filter-panel"]');
      const btn = asinFilterBtnRef.current;
      if (panel && panel.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      setAsinFilterOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setAsinFilterOpen(false); };
    const onReposition = () => {
      if (asinFilterBtnRef.current) {
        const r = asinFilterBtnRef.current.getBoundingClientRect();
        setAsinFilterPos({ top: r.bottom + 4, left: r.left });
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [asinFilterOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end, days_type: daysType };
      if (selectedAsins && selectedAsins.length > 0) {
        params.asins = selectedAsins.join(',');
      }
      const r = await axios.get(`${API}/analytics/delivery-matrix`, { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load analytics');
    }
    setLoading(false);
  }, [start, end, selectedAsins]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!data || !data.rows.length) { toast.error('Nothing to export'); return; }
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [];

    // ─── Metadata header ───
    lines.push(`HORIZON Delivery Analytics — Days × SKU matrix`);
    lines.push(`Date range,${start} to ${end}`);
    lines.push(`Generated,${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`);
    lines.push(`Jobs scanned,${data.meta.jobs_scanned}`);
    lines.push(`Samples counted,${data.meta.samples_counted}`);
    lines.push(`SKUs,${data.asins.length}`);
    lines.push('');

    // ─── SUMMARY block (% only by SKU + Average col) ───
    lines.push('SUMMARY (% by SKU, days bucketed)');
    const sumHeader = ['Days/SKU'];
    data.asins.forEach(a => sumHeader.push(escape(a.name || a.asin)));
    sumHeader.push('Average');
    lines.push(sumHeader.join(','));
    if (summary) {
      summary.forEach(r => {
        const row = [escape(r.label + (r.sublabel ? ` (${r.sublabel})` : ''))];
        data.asins.forEach(a => row.push(`${r.cells[a.asin] || 0}%`));
        row.push(`${r.avg}%`);
        lines.push(row.join(','));
      });
    }
    lines.push('');

    // ─── DETAILED block — properly labelled headers (Count / %) per SKU ───
    lines.push('DETAILED (every day with count + %)');
    const detailHeader = ['Days/SKU'];
    data.asins.forEach(a => {
      const label = a.name || a.asin;
      detailHeader.push(escape(`${label} — Count`));
      detailHeader.push(escape(`${label} — %`));
    });
    lines.push(detailHeader.join(','));
    data.rows.forEach(row => {
      const dayLabel = row.day === 0 ? '0 (today)' : String(row.day);
      const r = [dayLabel];
      data.asins.forEach(a => {
        const c = row.cells[a.asin] || { count: 0, pct: 0 };
        r.push(c.count);
        r.push(`${c.pct}%`);
      });
      lines.push(r.join(','));
    });
    const totalsRow = ['Total'];
    data.asins.forEach(a => { totalsRow.push(a.total); totalsRow.push('100%'); });
    lines.push(totalsRow.join(','));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `delivery_matrix_${start}_to_${end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported summary + ${data.rows.length} day rows × ${data.asins.length} SKUs`);
  };

  // Heat-map intensity for a percentage (0-100)
  const heatBg = (pct) => {
    if (pct >= 50) return 'bg-emerald-100 text-emerald-800 font-semibold';
    if (pct >= 25) return 'bg-emerald-50 text-emerald-700';
    if (pct >= 10) return 'bg-amber-50 text-amber-700';
    if (pct > 0) return 'bg-amber-50/60 text-amber-600';
    return 'text-gray-300';
  };

  // Build summary buckets: 0, 1, 2, 3, "4+" — only % values + Average column
  const summary = (() => {
    if (!data || !data.asins.length) return null;
    const buckets = [
      { key: 0, label: '0', sublabel: 'today' },
      { key: 1, label: '1' },
      { key: 2, label: '2' },
      { key: 3, label: '3' },
      { key: '4plus', label: '4 and above' },
    ];
    const rows = buckets.map(b => {
      const cells = {};
      data.asins.forEach(a => {
        let pct = 0;
        if (b.key === '4plus') {
          // sum all days >= 4
          data.rows.forEach(r => {
            if (r.day >= 4) {
              pct += (r.cells[a.asin]?.pct || 0);
            }
          });
        } else {
          const r = data.rows.find(x => x.day === b.key);
          pct = r ? (r.cells[a.asin]?.pct || 0) : 0;
        }
        cells[a.asin] = Math.round(pct * 10) / 10;
      });
      const vals = Object.values(cells);
      const avg = vals.length ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10 : 0;
      return { ...b, cells, avg };
    });
    return rows;
  })();

  return (
    <div className="space-y-5" data-testid="delivery-analytics">
      {/* Header */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200/60 rounded-xl px-5 py-4">
        <div className="w-10 h-10 rounded-xl bg-white border border-indigo-200 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">Delivery Analytics</h3>
          <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Days × SKU matrix — counts only Fast (same-day/express) promises. Filter by product to drill in.</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wider" title="Aggregates Amazon delivery scrapes from every user in your organisation">
              Org-wide
            </span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-card">
        <div className="dashboard-card-body flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Date range</label>
            <DateRangePicker
              value={{ from: start, to: end }}
              onChange={(v) => { setStart(v.from); setEnd(v.to); }}
              maxDate={today}
              testId="analytics-date-range"
            />
          </div>

          {/* Product filter — multi-select dropdown over every ASIN seen in the window */}
          <div className="relative">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Products</label>
            {(() => {
              const available = data?.available_asins || [];
              const activeCount = selectedAsins ? selectedAsins.length : 0;
              const allCount = available.length;
              const label = activeCount === 0
                ? (allCount ? `All products (${allCount})` : 'No products in range')
                : `${activeCount} of ${allCount} selected`;
              return (
                <>
                  <button
                    ref={asinFilterBtnRef}
                    type="button"
                    onClick={() => setAsinFilterOpen(o => !o)}
                    disabled={!allCount}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors min-w-[200px]"
                    data-testid="analytics-product-filter-btn"
                  >
                    <Filter className="w-3.5 h-3.5 text-gray-400" />
                    <span className="flex-1 text-left truncate">{label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${asinFilterOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {asinFilterOpen && createPortal(
                    <div
                      style={{ position: 'fixed', top: asinFilterPos.top, left: asinFilterPos.left, zIndex: 100 }}
                      className="w-[340px] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                      data-testid="analytics-product-filter-panel"
                    >
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
                        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <input
                          autoFocus
                          type="text"
                          value={asinFilterQuery}
                          onChange={(e) => setAsinFilterQuery(e.target.value)}
                          placeholder="Search product name or ASIN..."
                          className="flex-1 bg-transparent text-[12px] outline-none placeholder:text-gray-400"
                          data-testid="analytics-product-filter-search"
                        />
                      </div>
                      <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        <button
                          type="button"
                          onClick={() => setSelectedAsins(null)}
                          className="hover:text-indigo-600 transition-colors"
                          data-testid="analytics-product-filter-select-all"
                        >Select all</button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={() => setSelectedAsins([])}
                          className="hover:text-indigo-600 transition-colors"
                          data-testid="analytics-product-filter-clear"
                        >Clear all</button>
                      </div>
                      <div className="max-h-[280px] overflow-y-auto py-1">
                        {(() => {
                          const q = asinFilterQuery.toLowerCase().trim();
                          const filtered = q
                            ? available.filter(a =>
                                (a.name || '').toLowerCase().includes(q) ||
                                (a.asin || '').toLowerCase().includes(q))
                            : available;
                          if (!filtered.length) {
                            return <div className="px-3 py-4 text-center text-[11px] text-gray-400">No products match</div>;
                          }
                          // selectedAsins null = all selected; treat empty list as none selected
                          const isAllOn = selectedAsins === null;
                          return filtered.map(a => {
                            const isOn = isAllOn || (selectedAsins || []).includes(a.asin);
                            return (
                              <label
                                key={a.asin}
                                className="flex items-start gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-[12px] select-none"
                                data-testid={`analytics-product-filter-row-${a.asin}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  onChange={() => {
                                    if (selectedAsins === null) {
                                      // From "all" → remove this one
                                      setSelectedAsins(available.filter(x => x.asin !== a.asin).map(x => x.asin));
                                    } else if (selectedAsins.includes(a.asin)) {
                                      setSelectedAsins(selectedAsins.filter(x => x !== a.asin));
                                    } else {
                                      setSelectedAsins([...selectedAsins, a.asin]);
                                    }
                                  }}
                                  className="mt-0.5 h-3.5 w-3.5 accent-indigo-600 cursor-pointer shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-800 truncate">{a.name || a.asin}</div>
                                  {a.name && <div className="text-[10px] text-gray-400 font-mono">{a.asin}</div>}
                                </div>
                              </label>
                            );
                          });
                        })()}
                      </div>
                      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex justify-end">
                        <button
                          type="button"
                          onClick={() => { setAsinFilterOpen(false); setAsinFilterQuery(''); }}
                          className="px-3 py-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                          data-testid="analytics-product-filter-close"
                        >Done</button>
                      </div>
                    </div>,
                    document.body
                  )}
                </>
              );
            })()}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-indigo-700 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors"
              data-testid="analytics-refresh-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={!data || !data.rows?.length}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="analytics-export-csv-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      {data && (
        <div className="flex items-center gap-6 px-2 text-[11px] text-gray-500">
          <span>Jobs scanned: <span className="font-semibold text-gray-700 tabular-nums">{data.meta.jobs_scanned}</span></span>
          <span>Samples: <span className="font-semibold text-gray-700 tabular-nums">{data.meta.samples_counted}</span></span>
          <span>SKUs: <span className="font-semibold text-gray-700 tabular-nums">{data.asins.length}</span></span>
          <span>Day range: <span className="font-semibold text-gray-700 tabular-nums">{data.days_range.length ? `0–${data.days_range[data.days_range.length-1]}` : '—'}</span></span>
          {data.meta.skipped_no_metric > 0 && (
            <span title="Successful scrape rows with no Fast-days value — excluded from this matrix">
              No fast value: <span className="font-semibold text-amber-600 tabular-nums">{data.meta.skipped_no_metric}</span>
            </span>
          )}
          {data.meta.filter_applied && (
            <span className="inline-flex items-center gap-1 text-indigo-700 font-semibold">
              <Filter className="w-3 h-3" /> Filter active ({data.meta.filter_size})
            </span>
          )}
        </div>
      )}

      {/* Empty/Loading state OR helper hint when nothing is expanded */}
      {loading && (
        <div className="dashboard-card">
          <div className="py-16 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading matrix...
          </div>
        </div>
      )}
      {!loading && (!data || !data.asins.length) && (
        <div className="dashboard-card">
          <div className="py-16 text-center" data-testid="analytics-empty">
            <BarChart3 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">No data in this date range</p>
            <p className="text-[11px] text-gray-400 mt-1">Run an Amazon scrape, or widen the range.</p>
          </div>
        </div>
      )}

      {/* Summary Matrix (Days/SKU buckets — % only with Average col) */}
      {summary && data?.asins.length > 0 && (
        <div className="dashboard-card overflow-hidden" data-testid="analytics-summary-card">
          <button
            onClick={() => setShowSummary(!showSummary)}
            className="dashboard-card-header w-full flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
            data-testid="analytics-summary-toggle"
          >
            <div className="flex items-center gap-2">
              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showSummary ? 'rotate-90' : ''}`} />
              <span className="font-semibold text-sm text-gray-700">Summary</span>
              <span className="text-[10px] text-gray-400">(% by SKU, days bucketed)</span>
            </div>
            <span className="text-[10px] text-gray-400">{showSummary ? 'Click to hide' : 'Click to expand'}</span>
          </button>
          {showSummary && (
          <div className="dashboard-card-body p-0">
            <div className="overflow-auto">
              <table className="min-w-full text-[12px]" data-testid="analytics-summary-table">
                <thead className="bg-indigo-100/40 border-b border-indigo-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-900 uppercase tracking-wide bg-pink-100/60 sticky left-0 z-10 min-w-[110px]">
                      Days/SKU
                    </th>
                    {data.asins.map(a => (
                      <th key={a.asin} className="px-2 py-2 text-center text-[10px] font-semibold text-indigo-900 border-l border-indigo-200 min-w-[120px] bg-indigo-100/40">
                        <div className="leading-snug whitespace-normal break-words" title={a.name || a.asin}>
                          {a.name ? a.name.split(/[(]/)[0].trim().slice(0, 30) : a.asin}
                          {a.name && a.name.includes('(') ? ` (${a.name.split('(')[1]?.split(')')[0]})` : ''}
                        </div>
                        <div className="text-[9px] font-mono text-indigo-500 mt-0.5">{a.asin}</div>
                      </th>
                    ))}
                    <th className="px-2 py-2 text-center text-[10px] font-semibold text-pink-900 border-l-2 border-pink-300 min-w-[80px] bg-pink-100/60">
                      Average
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((r, ridx) => (
                    <tr key={r.key} className={`border-b border-gray-100 ${ridx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`} data-testid={`analytics-summary-row-${r.key}`}>
                      <td className="px-3 py-2 font-bold text-gray-700 tabular-nums text-center sticky left-0 bg-pink-50/60 z-10 whitespace-nowrap">
                        {r.label}
                        {r.sublabel && <span className="text-[9px] text-pink-600 ml-1">{r.sublabel}</span>}
                      </td>
                      {data.asins.map(a => {
                        const pct = r.cells[a.asin] || 0;
                        return (
                          <td key={a.asin} className={`px-2 py-2 text-center tabular-nums border-l border-gray-100 ${heatBg(pct)}`}>
                            {pct ? `${pct}%` : <span className="text-gray-300">0%</span>}
                          </td>
                        );
                      })}
                      <td className={`px-2 py-2 text-center tabular-nums border-l-2 border-pink-200 font-bold ${heatBg(r.avg)}`}>
                        {r.avg ? `${r.avg}%` : <span className="text-gray-300">0%</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Detailed Matrix */}
      <div className="dashboard-card overflow-hidden">
        <button
          onClick={() => setShowDetailed(!showDetailed)}
          className="dashboard-card-header w-full flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
          data-testid="analytics-detailed-toggle"
        >
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showDetailed ? 'rotate-90' : ''}`} />
            <span className="font-semibold text-sm text-gray-700">Detailed</span>
            <span className="text-[10px] text-gray-400">(every day with count + %)</span>
          </div>
          <span className="text-[10px] text-gray-400">{showDetailed ? 'Click to hide' : 'Click to expand'}</span>
        </button>
        {showDetailed && data && data.asins.length > 0 && (
        <div className="dashboard-card-body p-0">
          <div className="overflow-auto max-h-[640px]">
              <table className="min-w-full text-[12px]" data-testid="analytics-matrix-table">
                <thead className="bg-indigo-50 border-b border-indigo-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-900 uppercase tracking-wide bg-pink-50 sticky left-0 z-20 min-w-[80px]">
                      Days/SKU
                    </th>
                    {data.asins.map(a => (
                      <th key={a.asin} colSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold text-indigo-900 border-l border-indigo-200 min-w-[160px]">
                        <div className="leading-snug whitespace-normal break-words" title={a.name || a.asin}>
                          {a.name ? a.name.split(/[(]/)[0].trim().slice(0, 38) : a.asin}
                          {a.name && a.name.includes('(') ? ` (${a.name.split('(')[1]?.split(')')[0]})` : ''}
                        </div>
                        <div className="text-[9px] font-mono text-indigo-500 mt-0.5">{a.asin}</div>
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-indigo-100/50">
                    <th className="px-3 py-1 text-left text-[9px] font-medium text-indigo-700 sticky left-0 bg-indigo-100/50 z-20"></th>
                    {data.asins.map(a => (
                      <React.Fragment key={a.asin}>
                        <th className="px-2 py-1 text-center text-[9px] font-bold text-indigo-700 border-l border-indigo-200">#</th>
                        <th className="px-2 py-1 text-center text-[9px] font-bold text-indigo-700">%</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, ridx) => (
                    <tr key={row.day} className={`border-b border-gray-100 ${ridx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`} data-testid={`analytics-row-${row.day}`}>
                      <td className="px-3 py-2 font-bold text-gray-700 tabular-nums text-center sticky left-0 bg-pink-50/60 z-10">
                        {row.day === 0 ? <span title="Today">0<span className="text-[9px] text-pink-600 ml-1">today</span></span> : row.day}
                      </td>
                      {data.asins.map(a => {
                        const c = row.cells[a.asin] || { count: 0, pct: 0 };
                        return (
                          <React.Fragment key={a.asin}>
                            <td className="px-2 py-2 text-center tabular-nums border-l border-gray-100 text-gray-700">{c.count || <span className="text-gray-300">0</span>}</td>
                            <td className={`px-2 py-2 text-center tabular-nums ${heatBg(c.pct)}`}>{c.pct ? `${c.pct}%` : <span className="text-gray-300">0%</span>}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="border-t-2 border-indigo-200 bg-indigo-50/60 font-semibold" data-testid="analytics-row-total">
                    <td className="px-3 py-2 text-gray-700 sticky left-0 bg-indigo-50/60 z-10">Total</td>
                    {data.asins.map(a => (
                      <React.Fragment key={a.asin}>
                        <td className="px-2 py-2 text-center tabular-nums border-l border-indigo-200 text-indigo-800">{a.total}</td>
                        <td className="px-2 py-2 text-center tabular-nums text-indigo-700">100%</td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
        </div>
        )}
      </div>
    </div>
  );
};


// ─── MARKET RESEARCH ANALYTICS ─────────────────────────────────────────────
// Reusable multi-select dropdown for picking Market-Research attributes.
// Closed by default; pops a checkbox list anchored below the button.
// Click-outside / Escape closes. Lightweight — no extra deps.
const AttributeDropdown = ({ available, selected, onToggle, testId, placeholder = 'Attributes' }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const label = selected.length === 0
    ? placeholder
    : selected.length <= 2
      ? selected.join(', ')
      : `${selected.length} attributes selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-9 px-3 text-xs border border-gray-200 rounded-md bg-white hover:bg-gray-50 inline-flex items-center gap-1.5 min-w-[180px] justify-between"
        data-testid={testId}
      >
        <span className="truncate">{label}</span>
        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg p-1.5" data-testid={`${testId}-menu`}>
          {available.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-gray-400 italic">Run a scrape first to populate.</div>
          ) : available.map(a => {
            const active = selected.includes(a.key);
            return (
              <label
                key={a.key}
                className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 rounded cursor-pointer"
                data-testid={`${testId}-opt-${a.key}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => onToggle(a.key)}
                  className="rounded"
                />
                <span className="flex-1 truncate text-gray-700">{a.key}</span>
                <span className="text-[10px] text-gray-400 font-mono">{a.count}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Sits inside the Amazon → Analytics tab, beneath DeliveryAnalytics. Renders:
//  • run + product counts in the lookback window
//  • top brands (horizontal bars)
//  • top flavours (horizontal bars)
//  • repeat ASINs across multiple lists
//  • daily product-extracted trendline
// ─── Smart icon mappers — pick a lucide icon based on a flavour / form value ───
const _flavourIconMap = [
  [/(unflavoured|unflavored|plain|none|original)/i, Circle],
  [/(chocolate|cocoa|coffee|mocha|espresso|cappuccino|latte)/i, Coffee],
  [/(strawberry|raspberry|cherry|blueberry|blackberry|cranberry|mixed berry|berry|grape)/i, Cherry],
  [/(orange|lemon|lime|citrus|tangerine|grapefruit)/i, Citrus],
  [/(mint|peppermint|spearmint|menthol|cool)/i, Leaf],
  [/(mango|pineapple|tropical|kiwi|passion|guava|peach|apricot|watermelon|melon)/i, Sparkles],
  [/(vanilla|caramel|butterscotch|toffee|honey)/i, Sparkles],
  [/(cola|fizz|soda|punch|tropical)/i, Droplets],
];
const getFlavourIcon = (label) => {
  for (const [re, icon] of _flavourIconMap) if (re.test(label || '')) return icon;
  return Droplets;
};

const _itemFormIconMap = [
  [/(tablet|pill|caplet)/i, Pill],
  [/(capsule|softgel|soft gel)/i, Pill],
  [/(gummy|gummies|chewable|chew)/i, Sparkles],
  [/(liquid|syrup|drops|tonic|oil|serum)/i, Droplets],
  [/(powder|granule|sachet)/i, FlaskConical],
  [/(cream|lotion|gel|ointment|balm|paste)/i, Droplets],
  [/(spray|mist|aerosol)/i, Sparkles],
  [/(bar|wafer|cookie|biscuit)/i, Package],
];
const getItemFormIcon = (label) => {
  for (const [re, icon] of _itemFormIconMap) if (re.test(label || '')) return icon;
  return Package;
};


// ─── Reusable: Multi-select Value Panel (rows with smart icons + View All) ───
const ValueFilterPanel = ({
  title,
  subtitle,
  icon: HeaderIcon,
  iconBgClass = 'bg-emerald-50',
  iconColorClass = 'text-emerald-500',
  barClass = 'bg-emerald-400',
  rowIconBgClass = 'bg-slate-50',
  rowIconColorClass = 'text-slate-500',
  values,             // [{label, count}, ...]
  onRowClick,         // (value) => void  — opens ASIN list modal
  getRowIcon,         // (label) => LucideIcon
  showAll,
  setShowAll,
  testIdPrefix,
  emptyHint = 'No data for this selection yet.',
  aliasAttribute,     // 'flavour' | 'item_form' | 'brand' | … — enables pencil-edit
  aliasCategory,      // optional per-category scope (currently single-category context)
  onAliasSaved,       // callback after save → parent should refetch analytics
}) => {
  const maxCount = Math.max(1, ...values.map(v => v.count));
  const visible  = showAll ? values : values.slice(0, 10);
  const [editing, setEditing] = useState(null);  // { label } when edit popover is open

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-lg ${iconBgClass} shadow-sm flex items-center justify-center`}>
            {HeaderIcon && <HeaderIcon className={`w-4 h-4 ${iconColorClass}`} />}
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800 tracking-tight">{title}</h3>
            <p className="text-[10px] text-slate-500">{subtitle}</p>
          </div>
        </div>
        {values.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAll(s => !s)}
            className="h-8 px-3 text-[11px] font-semibold border border-slate-200 rounded-lg bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 text-slate-700 transition-all"
            data-testid={`${testIdPrefix}-viewall-btn`}
          >
            <Eye className="w-3.5 h-3.5" />
            {showAll ? 'Show top 10' : `View all (${values.length})`}
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="text-[11px] text-slate-400 py-6 text-center">{emptyHint}</div>
      ) : (
        <div className={`space-y-1 ${showAll ? 'max-h-80 overflow-y-auto pr-1' : ''}`}>
          {visible.map(v => {
            const RowIcon = getRowIcon ? getRowIcon(v.label) : null;
            return (
              <div
                key={v.label}
                className="group flex items-center gap-2 text-[11px] px-2 py-1.5 rounded-lg border border-transparent hover:bg-slate-50 hover:border-slate-200 hover:shadow-sm transition-all duration-150"
                data-testid={`${testIdPrefix}-row-${v.label}`}
              >
                <button
                  type="button"
                  onClick={() => onRowClick && onRowClick(v.label)}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left hover:scale-[1.005] transition-transform"
                  title={`View ${v.count} ASIN${v.count !== 1 ? 's' : ''} for "${v.label}"`}
                >
                  {RowIcon && (
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${rowIconBgClass} group-hover:scale-110 transition-transform`}>
                      <RowIcon className={`w-3.5 h-3.5 ${rowIconColorClass}`} />
                    </span>
                  )}
                  <span className="w-36 truncate font-medium text-slate-700 group-hover:text-slate-900">{v.label}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${barClass} transition-all duration-500`}
                      style={{ width: `${(v.count / maxCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-mono text-slate-500 tabular-nums">{v.count}</span>
                </button>
                {aliasAttribute && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditing({ label: v.label }); }}
                    className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all shrink-0"
                    title={`Standardize "${v.label}" — set a clean display value`}
                    data-testid={`${testIdPrefix}-alias-edit-${v.label}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
                <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <AliasEditModal
          attribute={aliasAttribute}
          category={aliasCategory}
          initialRaw={editing.label}
          initialDisplay={editing.label}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onAliasSaved && onAliasSaved(); }}
        />
      )}
    </div>
  );
};

// ─── Tiny modal for setting an attribute alias (raw → display). ───
// Used by ValueFilterPanel rows and brand chips. POSTs to
// /api/attribute-aliases and triggers a parent refetch on success.
const AliasEditModal = ({ attribute, category, initialRaw, initialDisplay, onClose, onSaved }) => {
  const [raw, setRaw] = useState(initialRaw || '');
  const [display, setDisplay] = useState(initialDisplay || '');
  const [scope, setScope] = useState(category ? 'category' : 'global');
  const [saving, setSaving] = useState(false);

  const attrLabel = {
    brand: 'Brand', flavour: 'Flavour', item_form: 'Item Form',
    diet_type: 'Diet Type', specialty: 'Specialty', pack_size: 'Pack Size',
  }[attribute] || attribute;

  const handleSave = async () => {
    if (!raw.trim() || !display.trim()) {
      toast.error('Both raw and display values are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/attribute-aliases`, {
        attribute,
        raw_value: raw.trim(),
        display_value: display.trim(),
        category: scope === 'category' ? (category || null) : null,
      });
      toast.success(`Standardized "${raw}" → "${display}"`);
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save alias';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="alias-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-indigo-500" />
            Standardize value
          </DialogTitle>
          <DialogDescription>
            Map a messy scraped value to a clean display label. Aggregation across
            the platform will use this display value — raw data stays untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{attrLabel}</span>
            {category && <span className="text-slate-400">·  {category}</span>}
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              Scraped value (raw)
            </label>
            <input
              type="text"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={`e.g. P`}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 font-mono"
              data-testid="alias-raw-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Exactly as Amazon returned it (case-insensitive matching)</p>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              Display as
            </label>
            <input
              type="text"
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              placeholder={`e.g. Powder`}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 font-semibold"
              data-testid="alias-display-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">All matching raw values will roll up under this label</p>
          </div>

          {category && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
                Scope
              </label>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setScope('global')}
                  className={`px-3 py-1 text-xs font-semibold rounded transition-all ${scope === 'global' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="alias-scope-global"
                >Global</button>
                <button
                  type="button"
                  onClick={() => setScope('category')}
                  className={`px-3 py-1 text-xs font-semibold rounded transition-all ${scope === 'category' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="alias-scope-category"
                >Only in {category}</button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid="alias-cancel-btn"
          >Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !raw.trim() || !display.trim()}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
            data-testid="alias-save-btn"
          >
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving…' : 'Save mapping'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ─── Reusable: Category-segmented Top-Values panel with View All modal ───
const AttributeAnalyticsPanel = ({
  title,
  icon: Icon,
  iconBgClass = 'bg-purple-50',
  iconColorClass = 'text-purple-500',
  barClass = 'bg-purple-400',
  overall,                  // [{label, count}, ...]
  perCategory,              // { categoryName: [{label, count}, ...] }
  categories,               // [{name, products, runs}]
  testIdPrefix,
}) => {
  const [activeCat, setActiveCat] = useState('__overall__');
  const [showAll, setShowAll] = useState(false);

  const values = useMemo(() => {
    if (activeCat === '__overall__') return overall || [];
    return perCategory?.[activeCat] || [];
  }, [activeCat, overall, perCategory]);

  const top10 = values.slice(0, 10);
  const maxCount = Math.max(1, ...values.map(v => v.count));
  const catChips = [
    { key: '__overall__', label: 'All categories', total: (overall || []).reduce((s, v) => s + v.count, 0) },
    ...((categories || []).filter(c => (perCategory?.[c.name] || []).length > 0).map(c => ({
      key: c.name,
      label: c.name,
      total: (perCategory?.[c.name] || []).reduce((s, v) => s + v.count, 0),
    }))),
  ];

  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg ${iconBgClass} flex items-center justify-center`}>
            {Icon && <Icon className={`w-4 h-4 ${iconColorClass}`} />}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            <p className="text-[10px] text-gray-400">Pick a category card to filter the list below.</p>
          </div>
        </div>
        {values.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="h-8 px-3 text-[11px] font-medium border border-gray-200 rounded-md bg-white hover:bg-purple-50 hover:border-purple-200 inline-flex items-center gap-1.5 text-gray-700"
            data-testid={`${testIdPrefix}-viewall-btn`}
          >
            <Eye className="w-3.5 h-3.5" /> View all ({values.length})
          </button>
        )}
      </div>

      {/* Category selector cards */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`${testIdPrefix}-cat-chips`}>
        {catChips.map(c => {
          const active = c.key === activeCat;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCat(c.key)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                active
                  ? 'bg-purple-500 border-purple-500 text-white shadow-sm'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
              }`}
              data-testid={`${testIdPrefix}-cat-${c.key}`}
            >
              <span className="truncate inline-block max-w-[180px] align-middle">{c.label}</span>
              <span className={`ml-1.5 text-[9px] font-mono ${active ? 'text-purple-100' : 'text-gray-400'}`}>
                ×{c.total}
              </span>
            </button>
          );
        })}
      </div>

      {/* Top 10 list */}
      {top10.length === 0 ? (
        <div className="text-[11px] text-gray-400 py-4">No data for this selection yet.</div>
      ) : (
        <div className="space-y-1">
          {top10.map(v => (
            <div key={v.label} className="flex items-center gap-2 text-[11px]">
              <span className="w-36 truncate text-gray-700">{v.label}</span>
              <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${barClass}`} style={{ width: `${(v.count / maxCount) * 100}%` }} />
              </div>
              <span className="w-8 text-right font-mono text-gray-500">{v.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* View All modal */}
      {showAll && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowAll(false)}
          data-testid={`${testIdPrefix}-viewall-modal`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg ${iconBgClass} flex items-center justify-center`}>
                  {Icon && <Icon className={`w-4 h-4 ${iconColorClass}`} />}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{title} · all values</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {activeCat === '__overall__' ? 'Across every category' : activeCat} · {values.length} unique value{values.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAll(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                data-testid={`${testIdPrefix}-viewall-close`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto space-y-1">
              {values.map(v => (
                <div key={v.label} className="flex items-center gap-2 text-xs px-2 py-1.5 hover:bg-gray-50 rounded">
                  <span className="flex-1 truncate text-gray-700">{v.label}</span>
                  <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barClass}`} style={{ width: `${(v.count / maxCount) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right font-mono text-gray-500">{v.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── LastScrapeTicker ──────────────────────────────────────────────
// Animated "last scrape was X ago" badge that updates every 30s. Sits in
// the Market Research analytics meta strip and pulses a tiny status dot
// so the user gets a live signal of how fresh the data is.
const LastScrapeTicker = ({ lastScrapeAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!lastScrapeAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, [lastScrapeAt]);
  if (!lastScrapeAt) return null;

  const ts = Date.parse(lastScrapeAt);
  if (isNaN(ts)) return null;
  const diffSec = Math.max(0, Math.round((now - ts) / 1000));
  let rel;
  if (diffSec < 60) rel = `${diffSec}s ago`;
  else if (diffSec < 3600) rel = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) rel = `${Math.floor(diffSec / 3600)}h ago`;
  else rel = `${Math.floor(diffSec / 86400)}d ago`;

  // Freshness tint: <1h emerald, <24h amber, older slate
  const tint = diffSec < 3600
    ? { dot: 'bg-emerald-500', ring: 'bg-emerald-400/40', text: 'text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50' }
    : diffSec < 86400
    ? { dot: 'bg-amber-500', ring: 'bg-amber-400/40', text: 'text-amber-700', border: 'border-amber-200', bg: 'bg-amber-50' }
    : { dot: 'bg-slate-400', ring: 'bg-slate-300/40', text: 'text-slate-600', border: 'border-slate-200', bg: 'bg-slate-50' };
  const absLocal = new Date(ts).toLocaleString();

  return (
    <span
      className={`ml-auto inline-flex items-center gap-2 px-2.5 py-1 rounded-full border ${tint.border} ${tint.bg} text-[10px] font-semibold overflow-hidden`}
      title={`Most recent scrape · ${absLocal}`}
      data-testid="mr-last-scrape-ticker"
    >
      <span className="relative inline-flex w-2 h-2">
        <span className={`absolute inset-0 rounded-full ${tint.ring} animate-ping`} />
        <span className={`relative inline-flex w-2 h-2 rounded-full ${tint.dot}`} />
      </span>
      <span className={`uppercase tracking-wider text-[9px] ${tint.text} opacity-70`}>Last scrape</span>
      <span className={`font-mono tabular-nums ${tint.text}`}>{rel}</span>
    </span>
  );
};

const MarketResearchAnalytics = () => {
  // Analytics view always spans "all time" — the window dropdown was removed
  // per product spec. 10-year window is functionally unbounded for this app.
  const days = 3650;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBrand, setActiveBrand] = useState(null); // brand drilldown modal (now opened via "info" icon)
  const [busy, setBusy] = useState(null);               // 'backfill' | 'clear' | 'snapshot' | null
  const snapshotRef = useRef(null);
  // ─── Filter state ─── (categories + brands cascade; flavour/item-form rows now open an ASIN list modal)
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedBrands, setSelectedBrands] = useState(() => new Set());
  const [showAllFlavours, setShowAllFlavours] = useState(false);
  const [showAllItemForms, setShowAllItemForms] = useState(false);
  // Standardize-brand modal trigger (raw brand label clicked from a brand card)
  const [editingBrandAlias, setEditingBrandAlias] = useState(null);
  // ASIN list modal — opens when a Flavour / Item-Form row is clicked
  const [asinModal, setAsinModal] = useState(null); // { attrKey, attrValue, accent, loading, count, asins, categories, brands } | null

  const toggleInSet = (setter) => (value) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };
  const toggleCategory  = toggleInSet(setSelectedCategories);
  const toggleBrand     = toggleInSet(setSelectedBrands);
  const clearAllFilters = () => {
    setSelectedCategories(new Set());
    setSelectedBrands(new Set());
  };

  const openAsinModal = async (attrKey, attrValue, accent) => {
    const cats = Array.from(selectedCategories).join(',');
    const bnds = Array.from(selectedBrands).join(',');
    setAsinModal({ attrKey, attrValue, accent, loading: true, count: 0, asins: [], categories: cats, brands: bnds });
    try {
      const r = await axios.get(`${API}/amazon/bestseller/asins-by-value`, {
        params: {
          attribute_key: attrKey,
          attribute_value: attrValue,
          days,
          categories: cats,
          brands: bnds,
        },
      });
      setAsinModal(m => m ? { ...m, loading: false, count: r.data.count, asins: r.data.asins || [] } : null);
    } catch (e) {
      toast.error('Failed to load ASIN list');
      setAsinModal(null);
    }
  };
  const closeAsinModal = () => setAsinModal(null);
  // ─── Marketplace filter (All / IN / US / …) — drives both UI filtering
  // AND the backend query so brand/flavour aggregates are scoped properly.
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  // Read attribute selection from the same localStorage the input panel writes to.
  // NOTE: "Dosage Form" was retired — strip it out for older saved selections.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Analytics view ALWAYS shows Flavour + Item Form only (per product spec).
      // `_t` cache-buster: guarantees a fresh response after alias saves; some
      // proxy paths were serving stale GETs and making the change look no-op.
      const params = new URLSearchParams({ days: String(days), attributes: 'Item Form', _t: String(Date.now()) });
      if (marketplaceFilter && marketplaceFilter !== 'all') params.set('marketplace', marketplaceFilter);
      const r = await axios.get(`${API}/amazon/bestseller/analytics?${params.toString()}`);
      setData(r.data);
    } catch {
      toast.error('Failed to load Market Research analytics');
    } finally {
      setLoading(false);
    }
  }, [days, marketplaceFilter]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Smart Delete (granular: category / brand / brand-in-category) ───
  // `deleteRequest` is { category, brand } — null for "all". When set we run a
  // dry-run preview to populate the confirm modal, then commit on Confirm.
  const [deleteRequest, setDeleteRequest] = useState(null);     // { category?, brand?, scope }
  const [deletePreview, setDeletePreview] = useState(null);     // server preview response
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Manage panel — lists every category + brand with row counts for bulk ops.
  const [managePanelOpen, setManagePanelOpen] = useState(false);
  const [manageInventory, setManageInventory] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);

  const requestDelete = useCallback(({ category = null, brand = null, scope = "custom" }) => {
    setDeleteRequest({ category, brand, scope });
  }, []);
  const closeDeleteModal = useCallback(() => {
    setDeleteRequest(null);
    setDeletePreview(null);
  }, []);
  // Whenever a delete request opens, fetch the preview so we can show row counts.
  useEffect(() => {
    if (!deleteRequest) return;
    let cancelled = false;
    setDeletePreviewLoading(true);
    setDeletePreview(null);
    axios.post(`${API}/amazon/bestseller/analytics/delete-preview`, {
      category: deleteRequest.category,
      brand: deleteRequest.brand,
    }).then(r => { if (!cancelled) setDeletePreview(r.data); })
      .catch(() => { if (!cancelled) toast.error("Failed to compute delete preview"); })
      .finally(() => { if (!cancelled) setDeletePreviewLoading(false); });
    return () => { cancelled = true; };
  }, [deleteRequest]);

  const loadManageInventory = useCallback(async () => {
    setManageLoading(true);
    try {
      const r = await axios.get(`${API}/amazon/bestseller/analytics/data-inventory`);
      setManageInventory(r.data);
    } catch {
      toast.error("Failed to load data inventory");
    } finally {
      setManageLoading(false);
    }
  }, []);
  const confirmDelete = useCallback(async () => {
    if (!deleteRequest || deleting) return;
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/amazon/bestseller/analytics/delete`, {
        category: deleteRequest.category,
        brand: deleteRequest.brand,
      });
      const d = r.data || {};
      toast.success(
        `Deleted ${d.rows_affected || 0} row${d.rows_affected !== 1 ? "s" : ""} across ${d.jobs_affected || 0} job${d.jobs_affected !== 1 ? "s" : ""}` +
        (d.jobs_emptied ? ` · ${d.jobs_emptied} job${d.jobs_emptied !== 1 ? "s" : ""} fully removed` : "")
      );
      closeDeleteModal();
      // Clear any selection that pointed at the deleted entity.
      if (deleteRequest.category) {
        setSelectedCategories(prev => { const n = new Set(prev); n.delete(deleteRequest.category); return n; });
      }
      if (deleteRequest.brand) {
        setSelectedBrands(prev => { const n = new Set(prev); n.delete(deleteRequest.brand); return n; });
      }
      await fetchData();
      if (managePanelOpen) await loadManageInventory();
    } catch {
      toast.error("Delete failed — see backend logs");
    } finally {
      setDeleting(false);
    }
  }, [deleteRequest, deleting, fetchData, closeDeleteModal, managePanelOpen, loadManageInventory]);

  const openManagePanel = useCallback(() => {
    setManagePanelOpen(true);
    loadManageInventory();
  }, [loadManageInventory]);
  const closeManagePanel = useCallback(() => setManagePanelOpen(false), []);

  const handleSnapshot = async () => {
    if (!snapshotRef.current || busy) return;
    setBusy('snapshot');
    try {
      const canvas = await html2canvas(snapshotRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `market-research-analytics-${ts}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Snapshot saved to your downloads');
    } catch (e) {
      console.error('Snapshot failed', e);
      toast.error('Could not generate snapshot');
    } finally {
      setBusy(null);
    }
  };

  const handleBackfill = async () => {
    if (busy) return;
    setBusy('backfill');
    try {
      const r = await axios.post(`${API}/amazon/bestseller/analytics/backfill`);
      const d = r.data || {};
      const catCount = Object.keys(d.categories || {}).length;
      toast.success(`Backfilled ${d.rows_updated || 0} rows across ${d.jobs_updated || 0} jobs (${catCount} categories)`);
      await fetchData();
    } catch {
      toast.error('Backfill failed — see backend logs');
    } finally {
      setBusy(null);
    }
  };

  const handleClear = async () => {
    if (busy) return;
    const ok = window.confirm(
      'Delete ALL Market Research scrape history?\n\nThis wipes both the analytics view AND the Job Queue rows for every bestseller scrape you own. This cannot be undone.'
    );
    if (!ok) return;
    setBusy('clear');
    try {
      const r = await axios.post(`${API}/amazon/bestseller/analytics/clear`);
      toast.success(`Deleted ${r.data?.deleted || 0} bestseller jobs`);
      await fetchData();
    } catch {
      toast.error('Clear failed — see backend logs');
    } finally {
      setBusy(null);
    }
  };

  // Backwards-compat: backend now returns *_overall but older deployments returned
  // top_brands / top_flavours. Coalesce both shapes so the UI never crashes.
  const topBrands   = data?.top_brands_overall   || data?.top_brands   || [];
  const topFlavours = data?.top_flavours_overall || data?.top_flavours || [];
  const brandDrilldown     = data?.brand_drilldown || {};
  const allCategories      = data?.categories || [];
  // (marketplaceFilter state declared above, near fetchData)
  // Track ALL marketplaces ever seen — accumulates across filter changes
  // so the toggle never collapses to a single option after the user filters.
  const [knownMarketplaces, setKnownMarketplaces] = useState({}); // { IN: n, US: n, … }
  useEffect(() => {
    // ONLY refresh when looking at the unfiltered analytics — otherwise we'd
    // overwrite our "known" set with the filtered subset.
    if (marketplaceFilter !== 'all' || !data?.categories) return;
    const counts = {};
    (data.categories || []).forEach(c => {
      const m = (c.marketplace || 'IN').toUpperCase();
      counts[m] = (counts[m] || 0) + 1;
    });
    setKnownMarketplaces(counts);
  }, [data?.categories, marketplaceFilter]);
  // Server already filters by marketplace when set, so categoryList === allCategories.
  const categoryList = allCategories;
  const availableMarketplaces = knownMarketplaces;
  const byAttribute        = data?.top_by_attribute || {};

  // ─── Research Groups appear alongside categories in the same grid ───
  // We fetch only "completed with rows" groups, then merge into a unified
  // `mergedCardList` sorted by product-count so categories + groups are
  // ranked together. Groups carry a `kind: 'group'` flag so the card
  // renderer can switch its theme to violet (vs. the amber of categories).
  const [researchGroups, setResearchGroups] = useState([]);
  // ─── Starred categories ────────────────────────────────────────────
  // Users can star a category card so it floats to the top of the row.
  // Persisted in localStorage so the star sticks across reloads. Applies
  // to both Categories and Research Groups (unified `key`).
  const [starredCategories, setStarredCategories] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("mr_starred_categories") || "[]"));
    } catch {
      return new Set();
    }
  });
  const toggleStar = useCallback((key) => {
    setStarredCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem("mr_starred_categories", JSON.stringify([...next]));
      } catch { /* quota / private mode — the star just won't persist */ }
      return next;
    });
  }, []);
  useEffect(() => {
    axios.get(`${API}/research-groups`).then(r => setResearchGroups(r.data?.groups || [])).catch(() => {});
  }, []);
  const refetchResearchGroups = useCallback(() => {
    axios.get(`${API}/research-groups`).then(r => setResearchGroups(r.data?.groups || [])).catch(() => {});
  }, []);
  const mergedCardList = useMemo(() => {
    const cats = (categoryList || []).map(c => ({ ...c, kind: 'category', key: c.name }));
    const grps = (researchGroups || [])
      .filter(g => g.last_scrape_status === 'completed' && (g.last_scrape_ok || 0) > 0)
      .map(g => ({
        kind: 'group',
        key: g.group_id,
        name: g.name,
        group_id: g.group_id,
        products: g.last_scrape_ok || 0,
        runs: 1,
        marketplace: g.marketplace,
      }));
    // Starred first (also sorted by products within the starred group), then
    // the rest by products desc — matches how users naturally think about
    // "my priority set" vs "everything else".
    return [...cats, ...grps].sort((a, b) => {
      const aStar = starredCategories.has(a.key) ? 1 : 0;
      const bStar = starredCategories.has(b.key) ? 1 : 0;
      if (aStar !== bStar) return bStar - aStar;
      return (b.products || 0) - (a.products || 0);
    });
  }, [categoryList, researchGroups, starredCategories]);
  // Helper to determine if a selected "category" key is actually a research group.
  const isGroupKey = useCallback((key) => researchGroups.some(g => g.group_id === key), [researchGroups]);
  const groupMetaByKey = useCallback((key) => researchGroups.find(g => g.group_id === key) || null, [researchGroups]);

  // ─── Cross-panel filter cascade: CATEGORY → BRAND → FLAVOUR/ITEM-FORM ───
  // Compute the brand cards to render based on the selected category filter.
  // When no category is selected → use the global `topBrands` ranking.
  // When 1+ categories are selected → recompute brand counts from
  // `brandDrilldown` summing only the categories the user picked.
  const visibleBrands = useMemo(() => {
    if (selectedCategories.size === 0) return topBrands;
    const arr = Object.entries(brandDrilldown).map(([name, bd]) => {
      let count = 0;
      for (const cat of selectedCategories) {
        count += bd?.categories?.[cat]?.count || 0;
      }
      return { label: name, count };
    });
    return arr.filter(b => b.count > 0).sort((a, b) => b.count - a.count);
  }, [selectedCategories, brandDrilldown, topBrands]);
  const maxVisibleBrandCount = Math.max(1, ...visibleBrands.map(b => b.count));

  // Compute Flavour + Item Form using BOTH brand and category filters.
  const filteredView = useMemo(() => {
    const noBrand = selectedBrands.size === 0;
    const noCat   = selectedCategories.size === 0;
    if (noBrand && noCat) {
      return {
        flavours: topFlavours,
        itemForms: byAttribute['Item Form'] || [],
      };
    }
    const flavMap = new Map();
    const itemFormMap = new Map();
    const brandsToScan = noBrand ? Object.keys(brandDrilldown) : Array.from(selectedBrands);
    for (const brand of brandsToScan) {
      const bd = brandDrilldown[brand];
      if (!bd) continue;
      for (const [cat, info] of Object.entries(bd.categories || {})) {
        if (!noCat && !selectedCategories.has(cat)) continue;
        (info.top_flavours || []).forEach(f => {
          flavMap.set(f.label, (flavMap.get(f.label) || 0) + (f.count || 0));
        });
        const ifVals = (info.attributes || {})['Item Form'] || [];
        ifVals.forEach(v => {
          itemFormMap.set(v.label, (itemFormMap.get(v.label) || 0) + (v.count || 0));
        });
      }
    }
    const toSorted = (m) => Array.from(m, ([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
    return { flavours: toSorted(flavMap), itemForms: toSorted(itemFormMap) };
  }, [selectedBrands, selectedCategories, topFlavours, byAttribute, brandDrilldown]);

  const filterBadgeCount = selectedCategories.size + selectedBrands.size;
  const categoryFilterChips = Array.from(selectedCategories);
  const brandFilterChips    = Array.from(selectedBrands);

  return (
    <div
      className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-6 mt-6 shadow-sm"
      style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(15, 23, 42, 0.04) 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }}
      data-testid="marketresearch-analytics"
    >
      {/* Decorative gradient bar at the top — gives the panel a "console" feel */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-indigo-500 via-emerald-400 to-amber-400" />

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3 relative">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-sm flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold tracking-tight bg-gradient-to-r from-slate-900 to-indigo-700 bg-clip-text text-transparent">
              Market Research · Analytics
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Repeat runs only contribute newly-listed ASINs
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleBackfill}
            disabled={!!busy}
            className="h-9 px-3 text-xs font-medium border border-slate-200 rounded-lg bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 inline-flex items-center gap-1.5 text-slate-700 transition-all"
            title="Re-fetch category name for any older jobs that don't have one"
            data-testid="mr-backfill-btn"
          >
            {busy === 'backfill' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Backfill
          </button>
          <button
            type="button"
            onClick={openManagePanel}
            disabled={!!busy}
            className="h-9 px-3 text-xs font-medium border border-slate-200 rounded-lg bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 inline-flex items-center gap-1.5 text-slate-700 transition-all"
            title="Browse categories & brands · bulk delete data"
            data-testid="mr-manage-data-btn"
          >
            <Settings className="w-3.5 h-3.5" />
            Manage data
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={!!busy}
            className="h-9 px-3 text-xs font-medium border border-red-200 text-red-600 rounded-lg bg-white hover:bg-red-50 hover:border-red-300 disabled:opacity-50 inline-flex items-center gap-1.5 transition-all"
            title="Delete every Market Research job in your history"
            data-testid="mr-clear-btn"
          >
            {busy === 'clear' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading analytics…
        </div>
      ) : !data || (data.products === 0) ? (
        <div className="text-center py-10 text-sm text-gray-400">
          No Market Research runs yet. Start a scrape on the Market Research tab.
        </div>
      ) : (
        <div ref={snapshotRef} className="bg-white relative">
          {/* ─── Inline meta strip — replaces the 5-tile KPI block ─── */}
          <div className="flex items-center gap-3 flex-wrap mb-4 text-[11px] font-mono tabular-nums text-slate-600">
            {[
              { icon: Activity,   label: 'runs',       value: data.runs,           color: 'text-slate-500' },
              { icon: Package,    label: 'products',   value: data.products,       color: 'text-indigo-500' },
              { icon: LayoutGrid, label: 'categories', value: categoryList.length, color: 'text-amber-500' },
              { icon: Trophy,     label: 'brands',     value: topBrands.length,    color: 'text-violet-500' },
              { icon: Droplets,   label: 'flavours',   value: topFlavours.length,  color: 'text-emerald-500' },
            ].map((kpi, i) => (
              <React.Fragment key={kpi.label}>
                <span className="inline-flex items-center gap-1.5">
                  <kpi.icon className={`w-3 h-3 ${kpi.color}`} />
                  <span className="text-sm font-bold text-slate-800">{kpi.value}</span>
                  <span className="text-slate-400 lowercase tracking-wide">{kpi.label}</span>
                </span>
                {i < 4 && <span className="text-slate-300">·</span>}
              </React.Fragment>
            ))}
            <LastScrapeTicker lastScrapeAt={data.last_scrape_at} />
          </div>

          {/* ─── UNIFIED FILTER CARD — Categories + Brands inside one container ─── */}
          <div
            className="relative rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50/50 shadow-sm mb-5 overflow-hidden"
            data-testid="mr-filter-zone"
          >
            {/* Header strip with Reset button */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2 text-[11px] font-bold tracking-tight text-slate-700">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                <span>Filter</span>
                {filterBadgeCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-indigo-500 text-white text-[9px] font-bold tabular-nums">
                    {filterBadgeCount}
                  </span>
                )}
              </div>
              {filterBadgeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="h-6 px-2 text-[10px] font-semibold rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 inline-flex items-center gap-1 transition-colors"
                  data-testid="mr-clear-filters-btn"
                >
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>

            {/* Categories row */}
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="w-3 h-3 text-amber-500" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Categories</span>
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums">· {categoryList.length}{marketplaceFilter !== 'all' && allCategories.length !== categoryList.length ? ` of ${allCategories.length}` : ''}</span>
                </div>
                {Object.keys(availableMarketplaces).length >= 1 && (() => {
                  // ISO codes map to circle-flags SVG icons (crisp, OS-agnostic rendering)
                  const MARKET_META = {
                    all: { iso: null,  label: 'All',       full: 'All Marketplaces', tint: '#475569' },
                    IN:  { iso: 'in',  label: 'India',     full: 'amazon.in',        tint: '#FF6F61' },
                    US:  { iso: 'us',  label: 'USA',       full: 'amazon.com',       tint: '#2563EB' },
                    UK:  { iso: 'gb',  label: 'UK',        full: 'amazon.co.uk',     tint: '#DC2626' },
                    DE:  { iso: 'de',  label: 'Germany',   full: 'amazon.de',        tint: '#F59E0B' },
                    CA:  { iso: 'ca',  label: 'Canada',    full: 'amazon.ca',        tint: '#EF4444' },
                    AU:  { iso: 'au',  label: 'Australia', full: 'amazon.com.au',    tint: '#0EA5E9' },
                    AE:  { iso: 'ae',  label: 'UAE',       full: 'amazon.ae',        tint: '#10B981' },
                  };
                  const opts = [
                    { code: 'all', ...MARKET_META.all, count: allCategories.length },
                    ...Object.entries(availableMarketplaces).map(([code, count]) => ({
                      code, count,
                      ...(MARKET_META[code] || { iso: null, label: code, full: code, tint: '#64748b' }),
                    })),
                  ];
                  return (
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 px-1">Marketplace</span>
                      <div
                        className="inline-flex items-center gap-1 p-1 rounded-xl bg-white border border-slate-200 shadow-sm"
                        data-testid="mr-marketplace-toggle"
                        role="tablist"
                      >
                        {opts.map(opt => {
                          const active = marketplaceFilter === opt.code;
                          return (
                            <button
                              key={opt.code}
                              type="button"
                              onClick={() => {
                                setMarketplaceFilter(opt.code);
                                if (opt.code !== 'all') {
                                  setSelectedCategories(prev => {
                                    const next = new Set();
                                    prev.forEach(name => {
                                      const cat = allCategories.find(c => c.name === name);
                                      if (cat && (cat.marketplace || 'IN').toUpperCase() === opt.code) next.add(name);
                                    });
                                    return next;
                                  });
                                }
                              }}
                              data-testid={`mr-marketplace-btn-${opt.code}`}
                              role="tab"
                              aria-selected={active}
                              title={`${opt.full} · ${opt.count} categor${opt.count === 1 ? 'y' : 'ies'}`}
                              className={`group/mp relative inline-flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${
                                active
                                  ? 'bg-gradient-to-br from-white to-slate-50 shadow-md scale-[1.04]'
                                  : 'hover:bg-slate-50/80'
                              }`}
                              style={active ? { boxShadow: `0 4px 12px -3px ${opt.tint}33, 0 0 0 1.5px ${opt.tint}55` } : {}}
                            >
                              {/* Flag with subtle ring on active — crisp circular SVG via circle-flags CDN */}
                              {opt.iso ? (
                                <span
                                  className={`relative inline-flex items-center justify-center transition-transform duration-200 ${active ? 'scale-110' : 'group-hover/mp:scale-105'}`}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    filter: active ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' : 'grayscale(35%) opacity(0.8)',
                                  }}
                                >
                                  <img
                                    src={`https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/${opt.iso}.svg`}
                                    alt={`${opt.label} flag`}
                                    width={18}
                                    height={18}
                                    draggable={false}
                                    className="w-full h-full rounded-full select-none"
                                    style={active ? { boxShadow: `0 0 0 1.5px ${opt.tint}66` } : {}}
                                  />
                                </span>
                              ) : (
                                <Globe
                                  className={`transition-transform duration-200 ${active ? 'scale-110' : 'group-hover/mp:scale-105'}`}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    color: active ? opt.tint : '#94a3b8',
                                    filter: active ? `drop-shadow(0 1px 2px ${opt.tint}55)` : 'none',
                                  }}
                                  strokeWidth={2.2}
                                />
                              )}
                              <div className="flex items-baseline gap-1.5">
                                <span
                                  className={`text-xs font-bold tracking-wide transition-colors ${
                                    active ? '' : 'text-slate-600 group-hover/mp:text-slate-900'
                                  }`}
                                  style={active ? { color: opt.tint } : {}}
                                >
                                  {opt.label}
                                </span>
                                <span
                                  className={`text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded transition-colors ${
                                    active ? 'text-white' : 'bg-slate-100 text-slate-500'
                                  }`}
                                  style={active ? { background: opt.tint } : {}}
                                >
                                  {opt.count}
                                </span>
                              </div>
                              {/* Active indicator dot at bottom */}
                              {active && (
                                <span
                                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                  style={{ background: opt.tint, boxShadow: `0 0 6px ${opt.tint}` }}
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
              {categoryList.length === 0 ? (
                <div className="text-[11px] text-slate-400 py-1">No categories yet — run a Market Research scrape.</div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }} data-testid="mr-category-card-row">
                  {(() => {
                    const active = selectedCategories.size === 0;
                    return (
                      <button
                        type="button"
                        onClick={() => setSelectedCategories(new Set())}
                        className={`shrink-0 w-40 text-left p-3 rounded-xl border transition-all duration-200 ${
                          active
                            ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-white ring-2 ring-amber-200 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-md hover:-translate-y-0.5'
                        }`}
                        data-testid="mr-category-card-all"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${
                            active ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-sm' : 'bg-amber-50 text-amber-600'
                          }`}>
                            <Sparkles className="w-3 h-3" />
                          </span>
                          {active && <Check className="w-3.5 h-3.5 text-amber-600" />}
                        </div>
                        <div className="text-sm font-bold text-slate-800">All</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono tabular-nums">{categoryList.length} cats · {data.products}</div>
                      </button>
                    );
                  })()}
                  {(() => {
                    const maxCatProducts = Math.max(1, ...mergedCardList.map(c => c.products));
                    return mergedCardList.map(c => {
                      const active = selectedCategories.has(c.key);
                      const isGrp = c.kind === 'group';
                      const ci = isGrp ? null : getCategoryIcon(c.name);
                      const Ic = isGrp ? Layers : ci.icon;
                      const pct = (c.products / maxCatProducts) * 100;
                      // Violet for research groups (distinct from categories' amber)
                      const theme = isGrp
                        ? {
                            ringActive: 'border-violet-500 bg-gradient-to-br from-violet-50 to-white ring-2 ring-violet-200',
                            ringHover: 'border-slate-200 bg-white hover:border-violet-300',
                            iconActive: 'bg-gradient-to-br from-violet-500 to-indigo-600 text-white',
                            iconIdle: 'bg-violet-50 text-violet-600',
                            check: 'text-violet-600',
                            barActive: 'bg-gradient-to-r from-violet-500 to-indigo-600',
                            barIdle: 'bg-violet-400',
                          }
                        : {
                            ringActive: 'border-amber-500 bg-gradient-to-br from-amber-50 to-white ring-2 ring-amber-200',
                            ringHover: 'border-slate-200 bg-white hover:border-amber-300',
                            iconActive: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white',
                            iconIdle: 'bg-amber-50 text-amber-600',
                            check: 'text-amber-600',
                            barActive: 'bg-gradient-to-r from-amber-500 to-amber-600',
                            barIdle: 'bg-amber-400',
                          };
                      return (
                        <div
                          key={c.key}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleCategory(c.key)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(c.key); } }}
                          className={`shrink-0 w-40 text-left p-3 rounded-xl border transition-all duration-200 cursor-pointer ${
                            active ? `${theme.ringActive} shadow-sm` : `${theme.ringHover} hover:shadow-md hover:-translate-y-0.5`
                          }`}
                          data-testid={`mr-${isGrp ? 'group' : 'category'}-card-${c.key}`}
                          data-active={active}
                          title={`${c.products} products · ${isGrp ? 'research group' : `${c.runs} run${c.runs !== 1 ? 's' : ''}`}`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${
                              active ? `${theme.iconActive} shadow-sm` : theme.iconIdle
                            }`}>
                              <Ic className="w-3 h-3" />
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleStar(c.key); }}
                                className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                  starredCategories.has(c.key)
                                    ? 'text-amber-500 hover:bg-amber-50'
                                    : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50'
                                }`}
                                title={starredCategories.has(c.key) ? "Unstar (removes from top)" : "Star to pin to top"}
                                data-testid={`mr-star-${c.key}`}
                              >
                                <Star className={`w-3 h-3 ${starredCategories.has(c.key) ? 'fill-amber-400' : ''}`} />
                              </button>
                              <span className="text-[10px] font-mono text-slate-400 tabular-nums">×{c.products}</span>
                              {active && <Check className={`w-3.5 h-3.5 ${theme.check}`} />}
                              {!isGrp && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); requestDelete({ category: c.name, scope: "category" }); }}
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                  title={`Delete all data in "${c.name}"`}
                                  data-testid={`mr-category-delete-${c.name}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                              {isGrp && (
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(`Delete research group "${c.name}" and its scrape data?`)) return;
                                    try {
                                      await axios.delete(`${API}/research-groups/${c.group_id}`);
                                      toast.success(`Deleted "${c.name}"`);
                                      refetchResearchGroups();
                                      // also clear any selection that pointed at this group
                                      setSelectedCategories(prev => { const n = new Set(prev); n.delete(c.group_id); return n; });
                                    } catch {
                                      toast.error("Delete failed");
                                    }
                                  }}
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                  title={`Delete research group "${c.name}"`}
                                  data-testid={`mr-group-delete-${c.group_id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="text-sm font-bold text-slate-800 truncate" title={c.name}>{c.name}</div>
                          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${active ? theme.barActive : theme.barIdle}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-1.5">
                            <span className="text-[10px] font-mono text-slate-400 tabular-nums">
                              {isGrp ? <span className="text-violet-600 font-semibold">GROUP</span> : `${c.runs} run${c.runs !== 1 ? 's' : ''}`}
                            </span>
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold border border-slate-200"
                              title={`Amazon marketplace · ${c.marketplace || 'IN'}`}
                            >
                              <img
                                src={`https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/${({IN:'in',US:'us',UK:'gb',DE:'de',CA:'ca',AU:'au',AE:'ae'}[c.marketplace] || 'in')}.svg`}
                                alt={c.marketplace || 'IN'}
                                width={10}
                                height={10}
                                draggable={false}
                                className="rounded-full select-none"
                                style={{ width: '10px', height: '10px' }}
                              />
                              {c.marketplace || 'IN'}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Brands row */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2 justify-between flex-wrap">
                <div className="flex items-center gap-2">
                  <Trophy className="w-3 h-3 text-indigo-500" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Brands</span>
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums">
                    · {visibleBrands.length}{selectedCategories.size > 0 ? ` in ${selectedCategories.size === 1 ? 'selected category' : `${selectedCategories.size} categories`}` : ''}
                  </span>
                </div>
                {selectedBrands.size > 0 && (
                  <span className="text-[10px] font-mono text-indigo-500 tabular-nums">{selectedBrands.size} selected</span>
                )}
              </div>
              {visibleBrands.length === 0 ? (
                <div className="text-[11px] text-slate-400 py-1">
                  {selectedCategories.size > 0 ? 'No brands captured for the selected categor(ies).' : 'No brand data yet.'}
                </div>
              ) : (
                <div
                  className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1"
                  style={{ scrollbarWidth: 'thin' }}
                  data-testid="mr-brand-card-row"
                >
                  {/* All brands card */}
                  {(() => {
                    const allActive = selectedBrands.size === 0;
                    return (
                      <button
                        type="button"
                        onClick={() => setSelectedBrands(new Set())}
                        className={`shrink-0 w-40 text-left p-3 rounded-xl border transition-all duration-200 ${
                          allActive
                            ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-white ring-2 ring-indigo-200 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md hover:-translate-y-0.5'
                        }`}
                        data-testid="mr-brand-card-all"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${
                            allActive ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm' : 'bg-indigo-50 text-indigo-600'
                          }`}>
                            <LayoutGrid className="w-3 h-3" />
                          </span>
                          {allActive && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                        </div>
                        <div className="text-sm font-bold text-slate-800">All brands</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono tabular-nums">{visibleBrands.length} brands</div>
                      </button>
                    );
                  })()}

                  {(() => {
                    const maxBrandCount = Math.max(1, ...visibleBrands.map(b => b.count));
                    return visibleBrands.map((b, idx) => {
                      const hasDrill = !!brandDrilldown[b.label];
                      const active = selectedBrands.has(b.label);
                      const pct = (b.count / maxBrandCount) * 100;
                      return (
                        <div
                          key={b.label}
                          className={`shrink-0 w-40 p-3 rounded-xl border transition-all duration-200 cursor-pointer relative ${
                            active
                              ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-white ring-2 ring-indigo-200 shadow-sm'
                              : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md hover:-translate-y-0.5'
                          }`}
                          onClick={() => toggleBrand(b.label)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBrand(b.label); } }}
                          data-testid={`mr-brand-card-${b.label}`}
                          data-active={active}
                          title={`${b.label} · ×${b.count}`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-bold tabular-nums ${
                              active ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm' : 'bg-indigo-50 text-indigo-600'
                            }`}>
                              #{idx + 1}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-mono text-slate-400 tabular-nums">×{b.count}</span>
                              {active && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                            </div>
                          </div>
                          <div className="text-sm font-bold text-slate-800 truncate" title={b.label}>{b.label}</div>
                          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${active ? 'bg-gradient-to-r from-indigo-500 to-violet-500' : 'bg-indigo-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="mt-1.5 flex items-center justify-between">
                            <span className={`text-[10px] font-medium ${active ? 'text-indigo-700' : 'text-slate-500'}`}>
                              {active ? 'Selected' : 'Click to filter'}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditingBrandAlias(b.label); }}
                                className="opacity-0 group-hover:opacity-100 hover:opacity-100 w-5 h-5 rounded-full bg-white/80 hover:bg-indigo-100 flex items-center justify-center text-indigo-400 hover:text-indigo-600 transition-colors"
                                title={`Standardize "${b.label}"`}
                                data-testid={`mr-brand-alias-edit-${b.label}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              {hasDrill && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setActiveBrand(b.label); }}
                                  className="w-5 h-5 rounded-full bg-white/80 hover:bg-indigo-100 flex items-center justify-center text-indigo-500 transition-colors"
                                  title="View per-category drilldown"
                                  data-testid={`mr-brand-info-${b.label}`}
                                >
                                  <Info className="w-3 h-3" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // If exactly one category is selected, delete brand only inside that category.
                                  // Otherwise let the confirm modal explain the global scope.
                                  if (selectedCategories.size === 1) {
                                    const cat = Array.from(selectedCategories)[0];
                                    requestDelete({ category: cat, brand: b.label, scope: "brand_in_category" });
                                  } else {
                                    requestDelete({ brand: b.label, scope: "brand_global" });
                                  }
                                }}
                                className="w-5 h-5 rounded-full bg-white/80 hover:bg-rose-100 flex items-center justify-center text-slate-300 hover:text-rose-600 transition-colors"
                                title={selectedCategories.size === 1
                                  ? `Delete "${b.label}" in ${Array.from(selectedCategories)[0]}`
                                  : `Delete "${b.label}" across all categories`}
                                data-testid={`mr-brand-delete-${b.label}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* HERO: Flavour + Item Form side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <ValueFilterPanel
              title="Flavours"
              subtitle={`${filteredView.flavours.length} unique · click a row to view contributing ASINs`}
              icon={Droplets}
              iconBgClass="bg-gradient-to-br from-emerald-500 to-emerald-600"
              iconColorClass="text-white"
              barClass="bg-emerald-400"
              values={filteredView.flavours}
              onRowClick={(v) => openAsinModal('flavour', v, 'emerald')}
              getRowIcon={getFlavourIcon}
              rowIconBgClass="bg-emerald-50"
              rowIconColorClass="text-emerald-600"
              showAll={showAllFlavours}
              setShowAll={setShowAllFlavours}
              testIdPrefix="mr-flavour-panel"
              aliasAttribute="flavour"
              aliasCategory={selectedCategories.size === 1 ? Array.from(selectedCategories)[0] : null}
              onAliasSaved={fetchData}
              emptyHint={selectedBrands.size > 0 || selectedCategories.size > 0
                ? 'No flavour data for the current filter set.'
                : 'No flavour data yet.'}
            />
            <ValueFilterPanel
              title="Item Form"
              subtitle={`${filteredView.itemForms.length} unique · click a row to view contributing ASINs`}
              icon={FlaskConical}
              iconBgClass="bg-gradient-to-br from-orange-500 to-amber-600"
              iconColorClass="text-white"
              barClass="bg-amber-400"
              values={filteredView.itemForms}
              onRowClick={(v) => openAsinModal('Item Form', v, 'amber')}
              getRowIcon={getItemFormIcon}
              rowIconBgClass="bg-amber-50"
              rowIconColorClass="text-amber-600"
              showAll={showAllItemForms}
              setShowAll={setShowAllItemForms}
              testIdPrefix="mr-itemform-panel"
              aliasAttribute="item_form"
              aliasCategory={selectedCategories.size === 1 ? Array.from(selectedCategories)[0] : null}
              onAliasSaved={fetchData}
              emptyHint={selectedBrands.size > 0 || selectedCategories.size > 0
                ? 'No Item Form data for the current filter set.'
                : 'No Item Form data yet.'}
            />
          </div>

          {/* ── INLINE DEEP DIVE ─ shown when exactly one category/group is selected ── */}
          {selectedCategories.size === 1 && (() => {
            const onlyCat = Array.from(selectedCategories)[0];
            const brandList = Array.from(selectedBrands);
            // For research groups, swap the UUID for the human-readable name.
            const onlyCatDisplay = isGroupKey(onlyCat) ? (groupMetaByKey(onlyCat)?.name || onlyCat) : onlyCat;
            const dividerSuffix =
              brandList.length === 0 ? onlyCatDisplay
              : brandList.length === 1 ? `${onlyCatDisplay} · ${brandList[0]}`
              : `${onlyCatDisplay} · ${brandList.length} brands`;
            return (
              <div className="mb-6" data-testid="mr-inline-deepdive">
                <div className="flex items-center gap-3 mb-3 px-1">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-500">Deep Dive · {dividerSuffix}</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
                </div>
                <CategoryDeepDive
                  key={`${onlyCat}|${brandList.join(',')}|${marketplaceFilter}`}
                  category={onlyCat}
                  brands={brandList}
                  marketplace={marketplaceFilter === 'all' ? undefined : marketplaceFilter}
                  days={days}
                  platform={isGroupKey(onlyCat) ? 'amazon-research-group' : 'amazon-bestseller'}
                  displayName={isGroupKey(onlyCat) ? groupMetaByKey(onlyCat)?.name : undefined}
                  inline
                  onAliasSaved={fetchData}
                />
              </div>
            );
          })()}

          {selectedCategories.size === 0 && (
            <div className="mb-6 px-1 py-6 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/40 text-center" data-testid="mr-inline-deepdive-empty">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-500 mb-1">Deep Dive</div>
              <div className="text-sm text-slate-600">
                Select <span className="font-semibold text-slate-800">one category</span> above to see its full strategic deep-dive analysis right here.
              </div>
            </div>
          )}

          {selectedCategories.size > 1 && (
            <div className="mb-6 px-1 py-6 rounded-2xl border border-dashed border-amber-200 bg-amber-50/40 text-center" data-testid="mr-inline-deepdive-too-many">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-600 mb-1">Deep Dive</div>
              <div className="text-sm text-slate-600">
                You have <span className="font-semibold text-slate-800">{selectedCategories.size} categories</span> selected. Narrow it to a single category to load its deep dive here.
              </div>
            </div>
          )}

          {/* ASIN list modal — opens when a Flavour / Item-Form row is clicked */}
          {editingBrandAlias && (
            <AliasEditModal
              attribute="brand"
              category={selectedCategories.size === 1 ? Array.from(selectedCategories)[0] : null}
              initialRaw={editingBrandAlias}
              initialDisplay={editingBrandAlias}
              onClose={() => setEditingBrandAlias(null)}
              onSaved={() => { setEditingBrandAlias(null); fetchData(); }}
            />
          )}
          {asinModal && (() => {
            const a = asinModal;
            const accentMap = {
              emerald: { ring: 'ring-emerald-200', text: 'text-emerald-600', bg: 'bg-emerald-50', grad: 'from-emerald-500 to-emerald-600' },
              amber:   { ring: 'ring-amber-200',   text: 'text-amber-600',   bg: 'bg-amber-50',   grad: 'from-orange-500 to-amber-600' },
            }[a.accent] || { ring: 'ring-slate-200', text: 'text-slate-600', bg: 'bg-slate-50', grad: 'from-slate-500 to-slate-600' };
            const ModalIcon = a.attrKey === 'flavour' ? getFlavourIcon(a.attrValue) : getItemFormIcon(a.attrValue);
            const ctxChips = [];
            if (a.categories) a.categories.split(',').filter(Boolean).forEach(c => ctxChips.push({ icon: LayoutGrid, label: c, cls: 'bg-amber-100 text-amber-700' }));
            if (a.brands) a.brands.split(',').filter(Boolean).forEach(b => ctxChips.push({ icon: Trophy, label: b, cls: 'bg-indigo-100 text-indigo-700' }));
            return (
              <div
                className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={closeAsinModal}
                data-testid="mr-asin-modal"
              >
                <div
                  className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accentMap.grad} shadow-sm flex items-center justify-center`}>
                        <ModalIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{a.attrKey === 'flavour' ? 'Flavour' : a.attrKey}</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-900 tracking-tight">{a.attrValue}</h3>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {a.loading
                            ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
                            : <span className="font-mono tabular-nums"><span className="font-bold text-slate-700">{a.count}</span> ASIN{a.count !== 1 ? 's' : ''}</span>
                          }
                          {ctxChips.length > 0 && <span className="text-slate-300">·</span>}
                          {ctxChips.map((c, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${c.cls} text-[9.5px] font-medium`}>
                              <c.icon className="w-2.5 h-2.5" /> {c.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={closeAsinModal}
                      className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors shrink-0"
                      data-testid="mr-asin-modal-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-5 py-3">
                    {a.loading ? (
                      <div className="text-center py-12 text-sm text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Fetching ASINs…
                      </div>
                    ) : a.asins.length === 0 ? (
                      <div className="text-center py-12 text-sm text-slate-400">
                        No ASINs match these filters.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {a.asins.map(p => (
                          <a
                            key={p.asin}
                            href={p.url}
                            target="_blank"
                            rel="noreferrer"
                            className="group flex items-center gap-3 px-2 py-2 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 transition-all"
                            data-testid={`mr-asin-modal-row-${p.asin}`}
                          >
                            {p.rank && (
                              <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md ${accentMap.bg} text-[10px] font-bold tabular-nums ${accentMap.text}`}>
                                #{p.rank}
                              </span>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[12px] font-bold text-indigo-600 group-hover:underline">{p.asin}</span>
                                {p.brand && <span className="text-[11px] text-slate-500 truncate">· {p.brand}</span>}
                              </div>
                              <div className="text-[10.5px] text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="inline-flex items-center gap-1"><LayoutGrid className="w-2.5 h-2.5" /> {p.category}</span>
                                {p.flavour && <span className="inline-flex items-center gap-1 text-emerald-600"><Droplets className="w-2.5 h-2.5" /> {p.flavour}</span>}
                                {p.item_form && <span className="inline-flex items-center gap-1 text-amber-600"><FlaskConical className="w-2.5 h-2.5" /> {p.item_form}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {p.price && <span className="text-[11px] font-mono tabular-nums text-slate-700">₹{p.price}</span>}
                              {p.rating && (
                                <span className="text-[10px] font-mono tabular-nums text-amber-600 inline-flex items-center gap-0.5">
                                  <Sparkles className="w-2.5 h-2.5" /> {p.rating}
                                </span>
                              )}
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all" />
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Category Deep Dive is now rendered inline above (no modal). */}


          {/* Brand Drilldown Modal */}
          {activeBrand && brandDrilldown[activeBrand] && (
            <div
              className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
              onClick={() => setActiveBrand(null)}
              data-testid="mr-brand-drilldown-modal"
            >
              <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-indigo-500" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{activeBrand}</h3>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {brandDrilldown[activeBrand].total} listings · across {Object.keys(brandDrilldown[activeBrand].categories).length} categor{Object.keys(brandDrilldown[activeBrand].categories).length === 1 ? 'y' : 'ies'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveBrand(null)}
                    className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                    data-testid="mr-brand-drilldown-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="px-5 py-4 overflow-y-auto space-y-3">
                  {Object.entries(brandDrilldown[activeBrand].categories).map(([cat, info]) => (
                    <div key={cat} className="border border-gray-100 rounded-lg p-3" data-testid={`mr-drill-cat-${cat}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-800">{cat}</div>
                        <div className="text-[11px] text-gray-500">{info.count} product{info.count !== 1 ? 's' : ''}</div>
                      </div>
                      {info.top_flavours.length === 0 ? (
                        <div className="text-[10px] text-gray-400">No flavour info on these ASINs.</div>
                      ) : (
                        <div className="space-y-1">
                          {info.top_flavours.map(f => (
                            <div key={f.label} className="flex items-center justify-between text-[11px]">
                              <span className="truncate text-gray-700">{f.label}</span>
                              <span className="font-mono text-gray-500">×{f.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {info.attributes && Object.keys(info.attributes).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
                          {Object.entries(info.attributes).filter(([k]) => k !== 'Dosage Form').map(([attrKey, vals]) => (
                            <div key={attrKey} className="text-[10.5px]">
                              <div className="font-semibold text-gray-600">{attrKey}:</div>
                              <div className="text-gray-500 mt-0.5">
                                {(vals || []).map(v => `${v.label} ×${v.count}`).join(' · ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Smart delete: confirmation modal with row-count preview ─── */}
      {deleteRequest && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeDeleteModal} data-testid="mr-delete-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
              <span className="shrink-0 w-9 h-9 rounded-full bg-rose-50 inline-flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-600" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-900">
                  Delete {deleteRequest.brand && deleteRequest.category
                    ? <>brand <span className="text-rose-600">{deleteRequest.brand}</span> in <span className="text-rose-600">{deleteRequest.category}</span></>
                    : deleteRequest.brand
                      ? <>brand <span className="text-rose-600">{deleteRequest.brand}</span> across all categories</>
                      : <>category <span className="text-rose-600">{deleteRequest.category}</span></>}?
                </div>
                <div className="text-[11.5px] text-gray-500 mt-0.5">
                  This permanently removes matching rows from Market Research analytics. This cannot be undone.
                </div>
              </div>
              <button onClick={closeDeleteModal} className="shrink-0 w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-testid="mr-delete-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
              {deletePreviewLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Counting matching rows…
                </div>
              ) : deletePreview ? (
                <div className="space-y-2 text-xs" data-testid="mr-delete-preview">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Rows to delete</span>
                    <span className="font-bold text-gray-900 tabular-nums text-base">{deletePreview.rows_affected.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Scrape jobs touched</span>
                    <span className="font-semibold text-gray-700 tabular-nums">{deletePreview.jobs_affected}</span>
                  </div>
                  {deletePreview.jobs_emptied === 0 ? null : (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Jobs that will be fully removed</span>
                      <span className="font-semibold text-rose-600 tabular-nums">{deletePreview.jobs_emptied}</span>
                    </div>
                  )}
                  {!deleteRequest.brand && !deleteRequest.category ? null : null}
                  {deleteRequest.brand && !deleteRequest.category && deletePreview.sample_categories.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Affects categories</div>
                      <div className="text-[11px] text-gray-700">{deletePreview.sample_categories.join(', ')}{deletePreview.sample_categories.length >= 10 ? '…' : ''}</div>
                    </div>
                  )}
                  {deleteRequest.category && !deleteRequest.brand && deletePreview.sample_brands.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Affects brands</div>
                      <div className="text-[11px] text-gray-700 truncate" title={deletePreview.sample_brands.join(', ')}>
                        {deletePreview.sample_brands.slice(0, 6).join(', ')}{deletePreview.sample_brands.length > 6 ? `, +${deletePreview.sample_brands.length - 6} more` : ''}
                      </div>
                    </div>
                  )}
                  {deletePreview.rows_affected === 0 && (
                    <div className="pt-2 mt-2 border-t border-gray-200 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                      Nothing to delete — no matching rows in the database.
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-400">Calculating preview…</div>
              )}
            </div>
            {/* Brand-in-category quick-toggle so the user can flip scope without closing */}
            {deleteRequest.brand && deleteRequest.category && deletePreview && (
              <div className="px-5 py-3 border-b border-gray-100 bg-amber-50/50">
                <button
                  type="button"
                  onClick={() => requestDelete({ brand: deleteRequest.brand, category: null, scope: "brand_global" })}
                  className="text-[11px] text-amber-700 hover:text-amber-900 underline decoration-dotted"
                  data-testid="mr-delete-scope-global"
                >
                  Or delete <span className="font-semibold">{deleteRequest.brand}</span> across <span className="font-semibold">all categories</span> instead →
                </button>
              </div>
            )}
            {deleteRequest.brand && !deleteRequest.category && (selectedCategories.size === 1) && deletePreview && (
              <div className="px-5 py-3 border-b border-gray-100 bg-amber-50/50">
                <button
                  type="button"
                  onClick={() => requestDelete({ brand: deleteRequest.brand, category: Array.from(selectedCategories)[0], scope: "brand_in_category" })}
                  className="text-[11px] text-amber-700 hover:text-amber-900 underline decoration-dotted"
                  data-testid="mr-delete-scope-cat"
                >
                  Or only delete <span className="font-semibold">{deleteRequest.brand}</span> in <span className="font-semibold">{Array.from(selectedCategories)[0]}</span> →
                </button>
              </div>
            )}
            <div className="px-5 py-3 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="mr-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting || deletePreviewLoading || !deletePreview || deletePreview.rows_affected === 0}
                className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="mr-delete-confirm"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {deleting ? "Deleting…" : `Delete ${deletePreview?.rows_affected || 0} row${deletePreview?.rows_affected !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Manage data panel: inventory of categories & brands with per-row delete ─── */}
      {managePanelOpen && createPortal(
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={closeManagePanel} data-testid="mr-manage-panel">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 shrink-0">
              <span className="w-9 h-9 rounded-full bg-slate-100 inline-flex items-center justify-center">
                <Settings className="w-4 h-4 text-slate-700" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">Manage Market Research data</div>
                <div className="text-[11.5px] text-gray-500">Click the trash icon next to any row to delete it.</div>
              </div>
              <button onClick={() => loadManageInventory()} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" title="Refresh inventory" data-testid="mr-manage-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${manageLoading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={closeManagePanel} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-testid="mr-manage-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Categories list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Categories</div>
                  <div className="text-[10px] font-mono text-gray-400 tabular-nums">{manageInventory?.categories?.length || 0}</div>
                </div>
                <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">
                  {manageLoading && !manageInventory ? (
                    <div className="p-4 text-center text-xs text-gray-400 inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </div>
                  ) : !manageInventory || manageInventory.categories.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">No categories yet</div>
                  ) : (
                    manageInventory.categories.map((c) => (
                      <div key={c.name} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50/60 transition-colors" data-testid={`mr-manage-cat-${c.name}`}>
                        <span className="flex-1 truncate text-xs text-gray-700 font-medium" title={c.name}>{c.name}</span>
                        <span className="text-[10px] font-mono text-gray-500 tabular-nums shrink-0">×{c.rows.toLocaleString('en-IN')}</span>
                        <button
                          onClick={() => requestDelete({ category: c.name, scope: "category" })}
                          className="shrink-0 w-6 h-6 rounded-full text-slate-300 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
                          title={`Delete all data in "${c.name}"`}
                          data-testid={`mr-manage-cat-delete-${c.name}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {/* Brands list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">Brands</div>
                  <div className="text-[10px] font-mono text-gray-400 tabular-nums">{manageInventory?.brands?.length || 0}</div>
                </div>
                <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">
                  {manageLoading && !manageInventory ? (
                    <div className="p-4 text-center text-xs text-gray-400 inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </div>
                  ) : !manageInventory || manageInventory.brands.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">No brands yet</div>
                  ) : (
                    manageInventory.brands.map((b) => (
                      <div key={b.name} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50/60 transition-colors" data-testid={`mr-manage-brand-${b.name}`}>
                        <span className="flex-1 truncate text-xs text-gray-700 font-medium" title={b.name}>{b.name}</span>
                        <span className="text-[10px] font-mono text-gray-500 tabular-nums shrink-0">×{b.rows.toLocaleString('en-IN')}</span>
                        <button
                          onClick={() => requestDelete({ brand: b.name, scope: "brand_global" })}
                          className="shrink-0 w-6 h-6 rounded-full text-slate-300 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
                          title={`Delete "${b.name}" across all categories`}
                          data-testid={`mr-manage-brand-delete-${b.name}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


// ─── OFFLINE MASTER DATABASE & ANALYTICS ───
// ─── Cleaned-data visualizations: India choropleth, Zone donut, Keyword heatmap ───
const INDIA_TOPO_URL = "/india-states.topo.json";

// Brand teal #2EB4AC — 6-step ramp (light → dark) for choropleth & accents
const BRAND = "#2EB4AC";
const BRAND_DARK = "#1f8079";
const _rampColor = (t) => {
  if (t <= 0) return "#f3f4f6";  // gray-100
  const stops = [
    [0.05, "#e6f7f6"],
    [0.20, "#bce8e4"],
    [0.40, "#86d6cf"],
    [0.60, "#56c2b8"],
    [0.80, "#2EB4AC"],  // brand
    [1.00, "#1f8079"],  // brand-dark
  ];
  for (const [bound, color] of stops) if (t <= bound) return color;
  return stops[stops.length - 1][1];
};
// Build a light-to-dark ramp from any base hex colour. Used by the India
// choropleth when a brand filter is active — the map tints in that brand's
// palette (Wellcore=blue, Dynamite=red, YouWefit=amber, OSMO=dark blue).
// Interpolates from near-white at t=0.05 to a darker shade at t=1.0 so
// state-level intensity still communicates store density.
const _hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0,2), 16), parseInt(h.slice(2,4), 16), parseInt(h.slice(4,6), 16)];
};
const _mix = (a, b, t) => Math.round(a + (b - a) * t);
const _rgb = (r, g, b) => `rgb(${r}, ${g}, ${b})`;
const _brandRampColor = (t, baseHex) => {
  if (t <= 0) return "#f3f4f6";
  const [br, bg, bb] = _hexToRgb(baseHex);
  // Light end — mix base with white 88%. Dark end — darken base by 25%.
  const stops = [
    [0.05, _rgb(_mix(br, 255, 0.88), _mix(bg, 255, 0.88), _mix(bb, 255, 0.88))],
    [0.20, _rgb(_mix(br, 255, 0.72), _mix(bg, 255, 0.72), _mix(bb, 255, 0.72))],
    [0.40, _rgb(_mix(br, 255, 0.50), _mix(bg, 255, 0.50), _mix(bb, 255, 0.50))],
    [0.60, _rgb(_mix(br, 255, 0.28), _mix(bg, 255, 0.28), _mix(bb, 255, 0.28))],
    [0.80, baseHex],
    [1.00, _rgb(_mix(br, 0, 0.25), _mix(bg, 0, 0.25), _mix(bb, 0, 0.25))],
  ];
  for (const [bound, color] of stops) if (t <= bound) return color;
  return stops[stops.length - 1][1];
};

// ─── India-map gradient mode toggle ───
// ─── GradientModeToggle ─────────────────────────────────────────────
// Compact pill toggle rendered in the map card header (top-right) for
// high-visibility. Switches the choropleth color driver AND constrains
// which brands the user can filter by:
//   • Contacted mode (internal key: 'relative') — Wellversed-only view.
//     Shade scales by CA count vs top state, i.e. "how many stores did
//     we scrape here?". Contacted dropdown (Any/Yes/No) still narrows.
//   • Penetration mode (internal key: 'coverage') — all sub-brands
//     available (Wellcore, Dynamite, YouWefit, OSMO). Shade scales by
//     each state's share of India-wide contacted stores. Best for
//     "where has each brand penetrated?".
const GradientModeToggle = ({ mode, onChange, brandColor }) => {
  const [showInfo, setShowInfo] = useState(false);
  const OPTIONS = [
    { key: 'relative', label: 'Contacted',   hint: 'Overall stores view (Wellversed only) — shade by CA count vs top state' },
    { key: 'coverage', label: 'Penetration', hint: 'Per-brand penetration — shade by each state\'s share of India-wide contacted stores' },
  ];
  return (
    <div
      className="inline-flex items-center gap-1.5 relative"
      data-testid="india-map-gradient-toggle"
    >
      <span className="text-[9.5px] font-semibold uppercase tracking-wider text-gray-500 hidden sm:inline">Shading</span>

      {/* Info tooltip — explains what each mode means */}
      <button
        type="button"
        onMouseEnter={() => setShowInfo(true)}
        onMouseLeave={() => setShowInfo(false)}
        onFocus={() => setShowInfo(true)}
        onBlur={() => setShowInfo(false)}
        onClick={() => setShowInfo((v) => !v)}
        className="text-gray-400 hover:text-[#2EB4AC] focus:text-[#2EB4AC] transition-colors focus:outline-none"
        data-testid="india-map-gradient-info-btn"
        aria-label="What do these shading modes mean?"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {showInfo && (
        <div
          role="tooltip"
          className="absolute top-full right-0 mt-2 z-50 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-3 text-left"
          data-testid="india-map-gradient-info-tooltip"
        >
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Map shading modes</div>
          <div className="space-y-2.5">
            <div>
              <div className="inline-flex items-center gap-1.5 mb-0.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: brandColor || BRAND }}
                />
                <span className="text-[12px] font-semibold text-gray-900">Contacted</span>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                <span className="font-semibold">Wellversed umbrella only</span> — sub-brand chips are locked. Colour scales by
                <span className="font-semibold"> Contact-Available (CA)</span> stores in each state, so the top state is darkest and every other state shades proportionally against it. Pair with the Contacted dropdown (Any / Yes / No) to see how many overall stores have or haven't been outreached.
              </p>
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 mb-0.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: brandColor || BRAND }}
                />
                <span className="text-[12px] font-semibold text-gray-900">Penetration</span>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                <span className="font-semibold">All sub-brands available</span> (Wellcore, Dynamite, YouWefit, OSMO). Colour scales by each state's <span className="font-semibold">share of India-wide contacted stores</span> so a state accounting for 50% of outreach gets the darkest fill. Best for tracking a brand's contribution to national coverage.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
        {OPTIONS.map((opt) => {
          const active = mode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${
                active ? "text-white" : "text-gray-500 hover:text-gray-700"
              }`}
              style={active ? { background: brandColor || BRAND } : {}}
              data-testid={`gradient-mode-${opt.key}`}
              title={opt.hint}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── (Removed: CoverageSubModeToggle — Coverage now always uses India-share) ───
// ─── StateGridChoropleth ─────────────────────────────────────────────
// Country-agnostic fallback for non-IN choropleths. Renders every state as
// a coloured rectangle in a responsive grid, using the SAME shading logic
// as the India SVG choropleth (relative / coverage modes, brand tinting)
// so the UX reads identically across countries without needing per-country
// TopoJSON boundaries. Rectangles are click-to-drill, same as SVG regions.
const StateGridChoropleth = ({ states, selected, onSelect, brandColor = null, gradientMode = 'relative', countryName = '' }) => {
  const ramp = useCallback(
    (t) => brandColor ? _brandRampColor(t, brandColor) : _rampColor(t),
    [brandColor],
  );
  const totalContacted = useMemo(
    () => (states || []).reduce((sum, s) => sum + (s?.penetrated ?? s?.contacted ?? 0), 0),
    [states],
  );
  const valueOf = useCallback((s) => {
    if (!s) return 0;
    if (gradientMode === 'coverage') {
      const c = s.penetrated ?? s.contacted ?? 0;
      if (c <= 0) return 0;
      return totalContacted > 0 ? c / totalContacted : 0;
    }
    return s.ca || 0;
  }, [gradientMode, totalContacted]);
  const max = Math.max(gradientMode === 'coverage' ? 0.0001 : 1, ...(states || []).map(valueOf));
  const tFor = useCallback((v) => (v <= 0 ? 0 : v / max), [max]);
  // Skip "Unknown" — it clutters the grid and can't be drilled meaningfully
  const visible = (states || []).filter((s) => s.label && s.label !== "Unknown");
  return (
    <div className="w-full h-full flex flex-col" data-testid="country-state-grid">
      {visible.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
          <MapPin className="w-8 h-8 text-gray-300 mb-2" />
          <div className="text-sm font-medium text-gray-500">
            No state data for {countryName || 'this country'} yet
          </div>
          <div className="text-[11px] text-gray-400 mt-1">
            Upload a CSV with a filled <span className="font-mono">State</span> column to light up this view.
          </div>
        </div>
      ) : (
        <div className="grid gap-2 flex-1 auto-rows-min" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {visible.map((s) => {
            const t = tFor(valueOf(s));
            const isActive = selected === s.label;
            const fill = t === 0 ? '#f3f4f6' : ramp(t);
            // Pick readable text colour based on shade intensity
            const light = t < 0.5;
            return (
              <button
                key={s.label}
                type="button"
                onClick={() => onSelect(isActive ? null : s.label)}
                className={`text-left rounded-lg px-3 py-2.5 border transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isActive ? 'ring-2 ring-offset-1 ring-[#2EB4AC] border-[#2EB4AC]' : 'border-gray-200'
                }`}
                style={{ background: fill }}
                data-testid={`state-tile-${s.label}`}
                title={`${s.label} · ${s.count} stores · CA ${s.ca || 0} · Contacted ${s.contacted || 0}`}
              >
                <div className={`text-[11px] font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>
                  {s.label}
                </div>
                <div className={`text-[10px] mt-0.5 tabular-nums ${light ? 'text-gray-600' : 'text-white/85'}`}>
                  {s.count.toLocaleString()} stores
                </div>
                <div className={`text-[9px] mt-0.5 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/75'}`}>
                  CA {s.ca || 0} · Ctd {s.contacted || 0}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── CountryChoropleth ───────────────────────────────────────────────
// Real SVG choropleth for non-IN countries. Uses per-country TopoJSON
// pulled from a CDN so the map reads/behaves exactly like the India
// SVG — hover tooltip, click-to-drill, brand tinting, gradient modes,
// state-name matching. Falls back to StateGridChoropleth when we don't
// have a TopoJSON URL for the country yet, so no country is ever left
// without a map view.
const COUNTRY_MAP_CONFIG = {
  // US — us-atlas ships a canonical states TopoJSON with `properties.name`
  // carrying human state names ("California", "New York", …) which matches
  // the labels our backend emits from row.state directly.
  US: {
    url: "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json",
    nameKey: "name",
    projection: "geoAlbersUsa",
    projectionConfig: { scale: 900 },
    viewport: { width: 900, height: 550 },
  },
  // Nepal — 7 provinces. mesaugat/geoJSON-Nepal only stores numeric codes
  // (`ADM1_PCODE` = "NP01"…"NP07"), so `resolveName` maps each code to its
  // official 2020 rename. Users' CSV can then use the human name (e.g.
  // "Bagmati") and it'll match the polygon correctly.
  NP: {
    url: "https://cdn.jsdelivr.net/gh/mesaugat/geoJSON-Nepal@master/nepal-states.geojson",
    nameKey: "ADM1_PCODE",
    resolveName: (props) => ({
      NP01: "Koshi",
      NP02: "Madhesh",
      NP03: "Bagmati",
      NP04: "Gandaki",
      NP05: "Lumbini",
      NP06: "Karnali",
      NP07: "Sudurpashchim",
    })[(props?.ADM1_PCODE || "").toUpperCase()] || "",
    projection: "geoMercator",
    projectionConfig: { scale: 5500, center: [84, 28.3] },
    viewport: { width: 900, height: 500 },
  },
  // Canada — deldersveld's provinces topojson. Property is `name` (English).
  CA: {
    url: "https://cdn.jsdelivr.net/gh/deldersveld/topojson@master/countries/canada/canada-provinces.json",
    nameKey: "name",
    projection: "geoAzimuthalEqualArea",
    projectionConfig: { scale: 500, center: [-96, 62], rotate: [96, 0, 0] },
    viewport: { width: 900, height: 550 },
  },
  // United Kingdom — regions (England/Scotland/Wales/NI). Property `NAME_1`.
  GB: {
    url: "https://cdn.jsdelivr.net/gh/deldersveld/topojson@master/countries/united-kingdom/uk-counties.json",
    nameKey: "NAME_2",
    projection: "geoMercator",
    projectionConfig: { scale: 2200, center: [-2, 54] },
    viewport: { width: 700, height: 700 },
  },
  // Australia states — property `STATE_NAME`.
  AU: {
    url: "https://cdn.jsdelivr.net/gh/deldersveld/topojson@master/countries/australia/australia-states.json",
    nameKey: "STATE_NAME",
    projection: "geoMercator",
    projectionConfig: { scale: 700, center: [135, -28] },
    viewport: { width: 900, height: 700 },
  },
  // Germany states — property `NAME_1`.
  DE: {
    url: "https://cdn.jsdelivr.net/gh/deldersveld/topojson@master/countries/germany/germany-regions.json",
    nameKey: "NAME_1",
    projection: "geoMercator",
    projectionConfig: { scale: 2800, center: [10, 51] },
    viewport: { width: 700, height: 700 },
  },
  // France regions — property `nom`.
  FR: {
    url: "https://cdn.jsdelivr.net/gh/deldersveld/topojson@master/countries/france/france-departments.json",
    nameKey: "NAME_2",
    projection: "geoMercator",
    projectionConfig: { scale: 2400, center: [2, 46] },
    viewport: { width: 700, height: 700 },
  },
};

// Cheap normalizer so "New York " / "new york" / "New-York" all match.
const _normCountryState = (name) =>
  (name || "").toString().trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");

const CountryChoropleth = ({
  country,
  states,
  selected,
  onSelect,
  brandColor = null,
  gradientMode = 'relative',
  countryName = '',
}) => {
  const config = COUNTRY_MAP_CONFIG[country];
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const ramp = useCallback(
    (t) => brandColor ? _brandRampColor(t, brandColor) : _rampColor(t),
    [brandColor],
  );
  const totalContacted = useMemo(
    () => (states || []).reduce((sum, s) => sum + (s?.penetrated ?? s?.contacted ?? 0), 0),
    [states],
  );
  const valueOf = useCallback((s) => {
    if (!s) return 0;
    if (gradientMode === 'coverage') {
      const c = s.penetrated ?? s.contacted ?? 0;
      if (c <= 0) return 0;
      return totalContacted > 0 ? c / totalContacted : 0;
    }
    return s.ca || 0;
  }, [gradientMode, totalContacted]);
  const max = Math.max(gradientMode === 'coverage' ? 0.0001 : 1, ...(states || []).map(valueOf));
  const tFor = useCallback((v) => (v <= 0 ? 0 : v / max), [max]);

  // Lookup map: normalized state name -> {label, count, ca, contacted}
  const stateByNorm = useMemo(() => {
    const m = {};
    (states || []).forEach((s) => {
      if (s?.label) m[_normCountryState(s.label)] = s;
    });
    return m;
  }, [states]);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  // No topojson for this country yet → grid fallback so UX still works
  if (!config) {
    return (
      <StateGridChoropleth
        states={states}
        selected={selected}
        onSelect={onSelect}
        brandColor={brandColor}
        gradientMode={gradientMode}
        countryName={countryName}
      />
    );
  }

  return (
    <div className="relative flex flex-col h-full" data-testid={`country-choropleth-${country}`}>
      <div
        ref={containerRef}
        className="flex-1 min-h-0 relative"
        onMouseMove={handleMouseMove}
      >
        <div className="absolute inset-0">
          <ComposableMap
            projection={config.projection}
            projectionConfig={config.projectionConfig}
            width={config.viewport.width}
            height={config.viewport.height}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: "100%", height: "100%" }}
          >
            <Geographies geography={config.url}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const rawName = config.resolveName
                    ? config.resolveName(geo.properties || {})
                    : (geo.properties?.[config.nameKey] || geo.properties?.name || "");
                  const norm = _normCountryState(rawName);
                  const s = stateByNorm[norm];
                  const t = s ? tFor(valueOf(s)) : 0;
                  const isSelected = selected && _normCountryState(selected) === norm;
                  const fill = isSelected ? "#1f8079" : (t === 0 ? "#f3f4f6" : ramp(t));
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#ffffff"
                      strokeWidth={0.6}
                      style={{
                        default: { outline: "none" },
                        hover:   { fill: isSelected ? "#1f8079" : (t === 0 ? "#e5e7eb" : ramp(Math.min(1, t + 0.15))), outline: "none", cursor: s ? "pointer" : "default" },
                        pressed: { outline: "none" },
                      }}
                      onMouseEnter={() => setTooltip({ name: rawName, state: s })}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={() => {
                        if (!s) return;
                        onSelect(selected === s.label ? null : s.label);
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
        </div>

        {tooltip && (
          <div
            className="pointer-events-none absolute z-10 rounded-lg border border-gray-200 bg-white/95 backdrop-blur-sm px-3 py-2 shadow-lg"
            style={{ left: Math.min(pos.x + 14, (containerRef.current?.clientWidth || 600) - 220), top: Math.min(pos.y + 14, (containerRef.current?.clientHeight || 400) - 90) }}
          >
            <div className="text-[12px] font-semibold text-gray-900">{tooltip.name || "—"}</div>
            {tooltip.state ? (
              <div className="text-[10.5px] text-gray-600 mt-0.5 space-x-1.5">
                <span className="tabular-nums font-semibold text-gray-700">{tooltip.state.count}</span>
                <span className="text-gray-400">stores</span>
                <span className="text-gray-300">·</span>
                <span className="tabular-nums">CA {tooltip.state.ca || 0}</span>
                <span className="text-gray-300">·</span>
                <span className="tabular-nums">Ctd {tooltip.state.contacted || 0}</span>
              </div>
            ) : (
              <div className="text-[10.5px] text-gray-400 mt-0.5">No data</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};




const IndiaChoropleth = ({ states, selected, onSelect, mode = 'stores', fullscreen = false, brandColor = null, gradientMode = 'relative' }) => {
  // Ramp function — brand-tinted when a brand filter is active, else teal.
  const ramp = useCallback(
    (t) => brandColor ? _brandRampColor(t, brandColor) : _rampColor(t),
    [brandColor],
  );
  const stateMap = useMemo(() => {
    const m = {};
    (states || []).forEach((s) => { m[s.label] = s; });
    return m;
  }, [states]);

  // India-wide penetrated total (used for "india_share" coverage sub-mode).
  // Penetrated = rows where contacted=Yes AND a Wellversed sub-brand flag=Yes.
  // Falls back to the raw contacted count when the backend hasn't been
  // redeployed yet so the UI never shows a blank map.
  const totalContactedIndia = useMemo(
    () => (states || []).reduce((sum, s) => sum + (s?.penetrated ?? s?.contacted ?? 0), 0),
    [states],
  );

  // Colour driver depends on gradient mode:
  //   • 'relative'  → state.ca — scaled against the current top state.
  //     Useful for ranking data density.
  //   • 'coverage'  → state.penetrated / total_penetrated_across_India.
  //     Emphasises each state's CONTRIBUTION to India-wide brand
  //     penetration (contacted AND actually carrying). A state
  //     accounting for 50% of all India-wide penetrated stores gets the
  //     darkest fill. States with 0 penetrated render empty.
  const valueOf = useCallback((s) => {
    if (!s) return 0;
    if (gradientMode === 'coverage') {
      const pen = s.penetrated ?? s.contacted ?? 0;
      if (pen <= 0) return 0;
      return totalContactedIndia > 0 ? pen / totalContactedIndia : 0;
    }
    return s.ca || 0;
  }, [gradientMode, totalContactedIndia]);
  const max = Math.max(gradientMode === 'coverage' ? 0.0001 : 1, ...(states || []).map(valueOf));

  // Map a raw driver value → `t` value in [0,1] against the current max.
  //   • Coverage mode: t = ratio / max_ratio, so a state at 100% contacted
  //     always sits at the darkest step (never floors to 0.05).
  //   • Relative mode: t = ca / max_ca (unchanged).
  const tFor = useCallback((v) => {
    if (v <= 0) return 0;
    return v / max;
  }, [max]);

  const [hover, setHover] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  // Google-Maps style smooth zoom + pan. Coordinates are [lng, lat] center.
  const [zoomState, setZoomState] = useState({ coordinates: [82, 23], zoom: 1 });
  // Geographies are loaded async by react-simple-maps. Capture them by name so
  // we can auto-zoom to the selected state without depending on a click event.
  const geoMapRef = useRef({});
  const [geoReady, setGeoReady] = useState(false);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const avgCaPct = useMemo(() => {
    const withData = (states || []).filter((s) => s.count > 0);
    if (!withData.length) return 0;
    const ca = withData.reduce((a, b) => a + b.ca, 0);
    const total = withData.reduce((a, b) => a + b.count, 0);
    return total ? (ca / total) * 100 : 0;
  }, [states]);

  // When a state is selected, smoothly zoom to it. When cleared, snap back.
  const zoomToState = useCallback((geo) => {
    if (!geo) { setZoomState({ coordinates: [82, 23], zoom: 1 }); return; }
    try {
      const bounds = geoBounds(geo); // [[minLng, minLat], [maxLng, maxLat]]
      const cx = (bounds[0][0] + bounds[1][0]) / 2;
      const cy = (bounds[0][1] + bounds[1][1]) / 2;
      const dx = bounds[1][0] - bounds[0][0];
      const dy = bounds[1][1] - bounds[0][1];
      // Empirical scale for India's mercator-projected viewport. Keep a generous
      // padding so users can still see the state's neighbours for spatial context
      // (Google-Maps-style framing).
      const zoom = fullscreen
        ? Math.max(1.8, Math.min(5.5, 13 / Math.max(dx, dy)))
        : Math.max(1.6, Math.min(8, 18 / Math.max(dx, dy)));
      setZoomState({ coordinates: [cx, cy], zoom });
    } catch (e) {
      setZoomState({ coordinates: [82, 23], zoom: 1 });
    }
  }, [fullscreen]);

  // Auto-zoom whenever `selected` or `fullscreen` changes. Inline maps stay
  // un-zoomed — clicking a state opens the fullscreen modal, so the inline
  // map should always show all of India.
  useEffect(() => {
    if (!fullscreen) { setZoomState({ coordinates: [82, 23], zoom: 1 }); return; }
    if (!selected) { setZoomState({ coordinates: [82, 23], zoom: 1 }); return; }
    if (!geoReady) return;
    const geo = geoMapRef.current[selected];
    if (geo) zoomToState(geo);
  }, [selected, fullscreen, geoReady, zoomToState]);

  const fmt = (v) => `${v}`;
  // Untapped-territory grey — states with stores but 0 CA.
  const UNTAPPED_FILL = "#9CA3AF"; // gray-400

  // Vertical color scale legend (0 → max), 8 stops for a smooth ramp
  const scaleStops = useMemo(() => {
    const stops = [];
    for (let i = 8; i >= 0; i--) {
      const t = i / 8;
      stops.push({ t, color: t === 0 ? "#f3f4f6" : ramp(Math.max(0.05, t)), value: t * max });
    }
    return stops;
  }, [max, ramp]);

  return (
    <div className="relative flex flex-col h-full" data-testid="india-choropleth">
      <div
        ref={containerRef}
        className="flex-1 min-h-0 relative"
        onMouseMove={handleMouseMove}
      >
        <div className="absolute inset-0">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 1200, center: [82, 23] }}
            width={800}
            height={720}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: "100%", height: "100%" }}
          >
            <defs>
              {/* Soft drop-shadow that gives each state a gentle 3D lift */}
              <filter id="state-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
                <feOffset dx="0" dy="1.5" result="offsetblur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.22" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="state-shadow-strong" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2.4" />
                <feOffset dx="0" dy="2.5" result="offsetblur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.45" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <ZoomableGroup
              zoom={zoomState.zoom}
              center={zoomState.coordinates}
              onMoveEnd={(z) => setZoomState(z)}
              minZoom={1}
              maxZoom={10}
              translateExtent={[[-200, -200], [1000, 920]]}
            >
            <Geographies geography={INDIA_TOPO_URL}>
              {({ geographies }) => {
                const labelSeen = {};
                let captured = false;
                geographies.forEach((g) => {
                  const nm = g?.properties?.st_nm;
                  if (!nm) return;
                  if (!geoMapRef.current[nm]) {
                    geoMapRef.current[nm] = g;
                    captured = true;
                  }
                  const cur = labelSeen[nm];
                  const bbox = (g.geometry?.coordinates && g.geometry.coordinates[0]) || [];
                  const approxArea = Array.isArray(bbox) ? bbox.length : 0;
                  if (!cur || approxArea > cur.area) {
                    labelSeen[nm] = { geo: g, area: approxArea };
                  }
                });
                if (captured && !geoReady) {
                  // Defer state update out of render cycle
                  setTimeout(() => setGeoReady(true), 0);
                }
                return (
                <>
                  {geographies
                    .filter((g) => g.properties && g.properties.st_nm)
                    .map((geo) => {
                      const name = geo.properties.st_nm;
                      const data = stateMap[name];
                      const count = data ? data.count : 0;
                      const ca = data ? (data.ca || 0) : 0;
                      const driverValue = valueOf(data);
                      const t = tFor(driverValue);
                      // Fill rules differ by mode:
                      //   RELATIVE  — driver=CA:
                      //     • CA>0    → teal ramp, intensity by t
                      //     • CA=0 but stores>0 → dark grey (untapped)
                      //     • no stores → very light
                      //   COVERAGE — driver=contacted/CA ratio:
                      //     • ratio>0 → teal ramp, intensity by ratio
                      //     • ratio=0 → very light (no contacts made yet).
                      //       We deliberately do NOT paint dark grey here
                      //       because 0% coverage should look empty, not
                      //       "untapped opportunity". The gradient itself
                      //       communicates outreach depth.
                      let fill = "#f3f4f6";
                      if (driverValue > 0) fill = ramp(Math.max(0.05, t));
                      else if (gradientMode === 'relative' && count > 0) fill = UNTAPPED_FILL;
                      const isSelected = selected && selected === name;
                      const isFiltered = selected && !isSelected;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={isFiltered ? "#f3f4f6" : fill}
                          stroke={isSelected ? (brandColor || BRAND_DARK) : "#ffffff"}
                          strokeWidth={isSelected ? 1.4 : 0.35}
                          strokeOpacity={isSelected ? 1 : 0.7}
                          filter={isSelected ? "url(#state-shadow-strong)" : "url(#state-shadow)"}
                          onMouseEnter={() => setHover({
                            name, count,
                            ca: data?.ca || 0,
                            cna: data?.cna || 0,
                            contacted: data?.contacted || 0,
                            // Share of India-wide contacted stores — matches the
                            // Coverage shading driver so the tooltip and the map
                            // color reference the same metric.
                            indiaShare: totalContactedIndia > 0
                              ? ((data?.contacted || 0) / totalContactedIndia) * 100
                              : 0,
                            caPct: count ? ((data.ca / count) * 100) : 0,
                          })}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => {
                            if (!onSelect) return;
                            if (fullscreen) {
                              // In modal: switch to a different state with data
                              if (count > 0 && selected !== name) onSelect(name);
                            } else {
                              // Inline map: clicking opens the fullscreen drill-down
                              if (count > 0) onSelect(name);
                            }
                          }}
                          style={{
                            default: { outline: "none", cursor: count ? "pointer" : "default", transition: "fill 0.25s ease" },
                            hover: { fill: count ? BRAND_DARK : (isFiltered ? "#e5e7eb" : fill), outline: "none", transition: "fill 0.2s ease" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })}
                  {Object.entries(labelSeen).map(([name, { geo }]) => {
                    const centroid = geoCentroid(geo);
                    if (!centroid || !Number.isFinite(centroid[0])) return null;
                    const data = stateMap[name];
                    const hasData = !!(data && data.count > 0);
                    if (!hasData) return null;
                    return (
                      <Marker key={`label-${name}`} coordinates={centroid}>
                        <text
                          textAnchor="middle"
                          y={2}
                          style={{
                            fontFamily: "system-ui, -apple-system, sans-serif",
                            fontSize: 9,
                            fontWeight: 700,
                            fill: "#1f2937",
                            paintOrder: "stroke",
                            stroke: "#ffffff",
                            strokeWidth: 2.2,
                            strokeLinejoin: "round",
                            pointerEvents: "none",
                            letterSpacing: 0.2,
                          }}
                        >
                          {name.toUpperCase()}
                        </text>
                      </Marker>
                    );
                  })}
                </>
                );
              }}
            </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {/* Back-to-India button (fullscreen mode only — inline maps don't zoom) */}
          {fullscreen && zoomState.zoom > 1.05 && (
            <button
              onClick={() => { if (onSelect) onSelect(null); zoomToState(null); }}
              className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 bg-white/95 backdrop-blur border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 z-10"
              data-testid="map-back-india"
            >
              <ArrowLeft className="w-3 h-3" /> Back to India
            </button>
          )}
        </div>

        {hover && (
          <div
            className="absolute bg-white/95 backdrop-blur rounded-lg border border-gray-200 shadow-lg px-3 py-2 text-xs pointer-events-none z-20 max-w-[220px]"
            style={{
              left: Math.min(pos.x + 14, (containerRef.current?.clientWidth || 800) - 230),
              top: Math.max(pos.y - 10, 4),
            }}
          >
            <div className="font-semibold text-gray-800">{hover.name}</div>
            {hover.count > 0 ? (
              <>
                <div className="text-gray-500 mt-1 tabular-nums">
                  <span className="font-semibold text-gray-800">{hover.count}</span> stores
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px]">
                  <span className="text-emerald-700 font-semibold">CA {hover.ca}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-amber-700 font-semibold">CNA {hover.cna}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-indigo-700 font-semibold">
                    Contacted {hover.contacted}
                    {hover.contacted > 0 && (
                      <span
                        className="text-indigo-500 font-medium"
                        title="Share of India-wide contacted stores — matches Coverage shading"
                      > ({hover.indiaShare.toFixed(1)}% of India)</span>
                    )}
                  </span>
                </div>
                <div className="mt-1 text-[10px] text-gray-500">
                  Contact Available rate: <span className={`font-semibold tabular-nums ${hover.caPct >= avgCaPct ? 'text-emerald-700' : 'text-amber-700'}`}>{hover.caPct.toFixed(1)}%</span>
                  <span className="text-gray-400 ml-1">(avg {avgCaPct.toFixed(1)}%)</span>
                </div>
                {!selected && !fullscreen && <div className="text-[10px] text-[#2EB4AC] mt-1 font-semibold">→ Click to open map view</div>}
                {fullscreen && selected !== hover.name && <div className="text-[10px] text-[#2EB4AC] mt-1 font-semibold">→ Click to switch</div>}
                {fullscreen && selected === hover.name && <div className="text-[10px] text-gray-500 mt-1">Currently viewing</div>}
              </>
            ) : (
              <div className="text-[10px] text-gray-400 mt-1">No data</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Google-Maps style FULLSCREEN drill-down for a selected state. The header has
// a back arrow + breadcrumb + summary stats. The left rail is a searchable
// pincode list, the right pane shows either the choropleth (state outline) OR
// the Leaflet pin map (real OSM tiles with geocoded store markers).
const StateMapModal = ({
  state,
  states,
  pincodes,
  pincodesLoading,
  mapMode,
  setMapMode,
  onSelectState,
  onClose,
  flagFilters,
  brandColor,
  gradientMode = 'relative',
}) => {
  const [search, setSearch] = useState("");
  // 'choropleth' = vector state map (analytics view), 'pins' = Leaflet pin map (discovery view)
  const [viewMode, setViewMode] = useState("choropleth");
  const [enrichStatus, setEnrichStatus] = useState(null);

  const stateData = useMemo(
    () => (states || []).find((s) => s.label === state),
    [states, state]
  );
  const caPct = stateData?.count ? Math.round((stateData.ca / stateData.count) * 100) : 0;

  const filteredPincodes = useMemo(() => {
    const list = pincodes?.pincodes || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    return list.filter((p) =>
      p.pincode.includes(q) ||
      (p.stores || []).some((s) => (s || "").toLowerCase().includes(q)) ||
      (p.categories || []).some((c) => (c || "").toLowerCase().includes(q))
    );
  }, [pincodes, search]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Reset the search every time the state changes (user navigates via map)
  useEffect(() => { setSearch(""); }, [state]);

  // Poll enrichment status while user is in Pin Map view — refreshes the
  // coverage banner so the user sees the geocoder making progress in real-time.
  const loadEnrichStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/offline-cleaned/pincode-enrich/status`);
      setEnrichStatus(r.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    if (viewMode !== "pins") return;
    loadEnrichStatus();
    const id = setInterval(loadEnrichStatus, 5000);
    return () => clearInterval(id);
  }, [viewMode, loadEnrichStatus]);

  const startEnrichment = useCallback(async () => {
    try {
      await axios.post(`${API}/offline-cleaned/pincode-enrich`);
      toast.success("Geocoding started — markers will appear as pincodes resolve");
      loadEnrichStatus();
    } catch (e) {
      toast.error("Failed to start geocoding");
    }
  }, [loadEnrichStatus]);

  return createPortal(
    <div
      className="fixed inset-0 z-[70] bg-white flex flex-col"
      data-testid="state-map-modal"
    >
      {/* Top bar — Google Maps inspired: back arrow, breadcrumb, quick stats */}
      <div className="h-14 px-3 sm:px-4 flex items-center gap-2 sm:gap-3 border-b border-gray-200 bg-white shrink-0" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="state-map-modal-back"
          title="Back to India"
        >
          <ArrowLeft className="w-4 h-4 text-gray-700" />
        </button>
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <span
            className="text-gray-500 hover:text-gray-800 cursor-pointer transition-colors"
            onClick={onClose}
          >
            India
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
          <span className="font-semibold text-gray-900 truncate" data-testid="state-map-modal-title">{state}</span>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs text-gray-500 ml-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND }} />
            <span className="font-semibold text-gray-900 tabular-nums">{(stateData?.count || 0).toLocaleString('en-IN')}</span>
            <span className="text-gray-500">stores</span>
          </span>
          <span className="text-gray-300">·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="font-semibold text-emerald-700 tabular-nums">{caPct}%</span>
            <span className="text-gray-500">contactable</span>
          </span>
          <span className="text-gray-300">·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="font-semibold text-gray-900 tabular-nums">{pincodes?.total_pincodes || 0}</span>
            <span className="text-gray-500">pincodes</span>
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Choropleth (analytics overview) ↔ Pin Map (Google-Maps-style discovery) */}
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" data-testid="state-map-modal-view">
            <button
              onClick={() => setViewMode('choropleth')}
              className={`px-2.5 py-1 text-[10.5px] font-semibold rounded transition-colors ${viewMode === 'choropleth' ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
              style={viewMode === 'choropleth' ? { background: "#111827" } : {}}
              data-testid="state-map-modal-view-choropleth"
              title="Vector state outline · CA% / store-count heat"
            >Analytics</button>
            <button
              onClick={() => setViewMode('pins')}
              className={`px-2.5 py-1 text-[10.5px] font-semibold rounded transition-colors ${viewMode === 'pins' ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
              style={viewMode === 'pins' ? { background: "#111827" } : {}}
              data-testid="state-map-modal-view-pins"
              title="OpenStreetMap tiles · pin per pincode · click for stores & directions"
            >Pin map</button>
          </div>
          {viewMode === 'choropleth' && (
            <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" data-testid="state-map-modal-mode">
              <button
                onClick={() => setMapMode('stores')}
                className={`px-2 py-1 text-[10.5px] font-semibold rounded transition-colors ${mapMode === 'stores' ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
                style={mapMode === 'stores' ? { background: BRAND } : {}}
                data-testid="state-map-modal-mode-stores"
              >Stores</button>
              <button
                onClick={() => setMapMode('ca_pct')}
                className={`px-2 py-1 text-[10.5px] font-semibold rounded transition-colors ${mapMode === 'ca_pct' ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
                style={mapMode === 'ca_pct' ? { background: BRAND } : {}}
                data-testid="state-map-modal-mode-ca"
              >Contact %</button>
            </div>
          )}
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="state-map-modal-close"
            title="Close"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Body: left rail + map */}
      <div className="flex-1 flex min-h-0">
        {/* Left search/results rail — Google Maps style */}
        <div className="w-full md:w-[400px] shrink-0 border-r border-gray-200 flex flex-col bg-white">
          <div className="p-3 border-b border-gray-100 shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search pincodes, stores, categories in ${state}`}
                className="w-full pl-8 pr-8 py-2 text-xs rounded-full border border-gray-300 focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/20 transition-shadow"
                data-testid="state-map-modal-search"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600"
                  data-testid="state-map-modal-search-clear"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
              <span>
                <span className="font-semibold text-gray-800 tabular-nums">{filteredPincodes.length}</span>
                {search ? ` of ${pincodes?.total_pincodes || 0}` : ''} pincode{filteredPincodes.length !== 1 ? 's' : ''}
              </span>
              {pincodesLoading && <span className="text-gray-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="state-map-modal-list">
            {pincodesLoading ? (
              <div className="p-6 text-center text-xs text-gray-400">Loading pincodes…</div>
            ) : filteredPincodes.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-400">
                {search ? `No pincodes match “${search}”` : `No pincode data in ${state}`}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredPincodes.map((p) => {
                  const cp = p.count ? Math.round((p.ca / p.count) * 100) : 0;
                  const tier = cp >= 80
                    ? { bg: "#e6f7f6", text: BRAND_DARK }
                    : cp >= 50
                      ? { bg: "#fffbeb", text: "#b45309" }
                      : { bg: "#fef2f2", text: "#be123c" };
                  return (
                    <div
                      key={p.pincode}
                      className="px-3 py-2.5 hover:bg-gray-50 transition-colors"
                      data-testid={`state-map-modal-pincode-${p.pincode}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="shrink-0 w-9 h-9 rounded-full bg-[#e6f7f6] inline-flex items-center justify-center" style={{ color: BRAND_DARK }}>
                          <MapPin className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-mono text-[13px] font-bold text-gray-900 tabular-nums">{p.pincode}</div>
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums"
                              style={{ background: tier.bg, color: tier.text }}
                            >{cp}%</span>
                          </div>
                          <div className="mt-0.5 text-[11.5px] text-gray-700 truncate font-medium" title={(p.stores || []).join(' · ')}>
                            {(p.stores || []).length > 0 ? p.stores.join(' · ') : '—'}
                          </div>
                          {(p.categories || []).length > 0 && (
                            <div className="text-[10.5px] text-gray-400 truncate mt-0.5">{p.categories.join(', ')}</div>
                          )}
                          <div className="mt-1 flex items-center gap-2 text-[10.5px]">
                            <span className="text-gray-500"><span className="font-semibold text-gray-800 tabular-nums">{p.count}</span> stores</span>
                            <span className="text-gray-300">·</span>
                            <span style={{ color: BRAND_DARK }} className="font-semibold tabular-nums">CA {p.ca}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-amber-700 font-semibold tabular-nums">CNA {p.cna}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Map area — Choropleth (analytics) or Pin Map (discovery) */}
        <div className="flex-1 relative bg-[#eef3f7]" data-testid="state-map-modal-map">
          {viewMode === 'choropleth' ? (
            <IndiaChoropleth
              states={states}
              selected={state}
              onSelect={onSelectState}
              mode={mapMode}
              fullscreen={true}
              brandColor={brandColor}
              gradientMode={gradientMode}
            />
          ) : (
            <StorePinMap
              state={state}
              apiBase={API}
              enrichmentStatus={enrichStatus}
              onEnrichmentRefresh={startEnrichment}
              flagFilters={flagFilters}
              brandColor={brandColor}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};


// Regional split — 4 KPI tiles (one per zone). Each tile shows the zone's
// count, its share of the total, the contact rate, and a stacked CA/CNA bar.
// In Penetration shade mode the big number swaps to the zone's weighted
// brand-touch count so the tiles align with the Penetration map shading.
// Clicking a tile toggles the zone filter on the parent state.
const ZoneDonut = ({ zones, selected, onSelect, filterParams, gradientMode = 'relative' }) => {
  const isPenetration = gradientMode === 'coverage';
  const contactedFilter = String(filterParams?.contacted || "").toLowerCase();
  // Which metric drives each tile:
  //   • Penetration mode → zone.penetrated (brand-weighted touches)
  //   • Contacted=Yes    → zone.contacted  (raw contacted-yes store count)
  //   • Contacted=No     → zone.count      (every passing row is contacted=No)
  //   • No filter        → zone.count      (total rows)
  const zoneValue = (z) => {
    if (isPenetration) return z?.penetrated ?? z?.contacted ?? 0;
    if (contactedFilter === "yes") return z?.contacted ?? z?.count ?? 0;
    return z?.count ?? 0;
  };
  const total = (zones || []).reduce((a, b) => a + zoneValue(b), 0);
  const colors = {
    North:   { dot: "#6366f1", bg: "#eef2ff", text: "#4338ca" },
    South:   { dot: "#2EB4AC", bg: "#e6f7f6", text: "#1f8079" },
    East:    { dot: "#f59e0b", bg: "#fffbeb", text: "#b45309" },
    West:    { dot: "#ec4899", bg: "#fdf2f8", text: "#be185d" },
    Central: { dot: "#06b6d4", bg: "#ecfeff", text: "#0e7490" },
    Unknown: { dot: "#9ca3af", bg: "#f9fafb", text: "#4b5563" },
  };
  const handleClick = (label) => {
    if (!onSelect) return;
    onSelect(selected === label ? null : label);
  };
  // Trigger a CSV download of pincodes classified in this zone. Runs
  // through window.open so the browser handles filename + Content-Disposition
  // (avoids blob/JSON round-trip). Filter params carry through so the
  // export matches whatever KPIs the user is looking at.
  const handleExport = (e, label) => {
    e.stopPropagation();
    const qs = new URLSearchParams();
    Object.entries(filterParams || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, v);
    });
    const q = qs.toString();
    const url = `${API}/offline-cleaned/zone/${encodeURIComponent(label)}/pincodes.csv${q ? `?${q}` : ""}`;
    // Force download in a hidden anchor so `Content-Disposition` is respected
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.download = `pincodes_${label}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast?.success?.(`Exporting pincodes for ${label} zone…`);
  };
  if (!zones || zones.length === 0) {
    return <div className="text-xs text-gray-400 text-center py-6" data-testid="zone-donut">No regional data</div>;
  }
  return (
    <div className="space-y-3 w-full" data-testid="zone-donut">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            {isPenetration ? "Brand touches by region"
              : contactedFilter === "yes" ? "Contacted stores by region"
              : contactedFilter === "no" ? "Not-contacted stores by region"
              : "Stores across regions"}
          </div>
          <div className="mt-0.5 text-3xl font-bold text-gray-900 tabular-nums">{total.toLocaleString('en-IN')}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">{zones.length} regions</div>
          <div className="mt-0.5 text-[11px] text-gray-500">click a tile to filter</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {zones.map((z) => {
          const c = colors[z.label] || colors.Unknown;
          const metric = zoneValue(z);
          const share = total ? (metric / total) * 100 : 0;
          const caPct = z.count ? (z.ca / z.count) * 100 : 0;
          const caShare = z.count ? (z.ca / z.count) : 0;
          const isActive = selected === z.label;
          const isDim = selected && !isActive;
          const metricLabel = isPenetration ? "touches" : "stores";
          return (
            <button
              key={z.label}
              onClick={() => handleClick(z.label)}
              className={`relative text-left rounded-xl p-4 border transition-all ${
                isActive
                  ? 'border-gray-900 shadow-md -translate-y-0.5'
                  : isDim
                    ? 'border-gray-200 opacity-50 hover:opacity-80'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5'
              }`}
              style={{ background: isActive ? c.bg : "#ffffff" }}
              data-testid={`zone-row-${z.label}`}
            >
              {/* Top accent bar in zone color */}
              <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-xl" style={{ background: c.dot }} />

              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.dot }} />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: c.text }}>{z.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-400 tabular-nums">{share.toFixed(0)}%</span>
                  <button
                    type="button"
                    onClick={(e) => handleExport(e, z.label)}
                    className="p-1 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                    title={`Export pincodes in ${z.label} zone as CSV`}
                    data-testid={`zone-export-${z.label}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-4xl font-bold text-gray-900 tabular-nums leading-none">{metric.toLocaleString('en-IN')}</span>
                <span className="text-[11px] text-gray-400">{metricLabel}</span>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                  <div style={{ width: `${caShare * 100}%`, background: "#2EB4AC" }} className="h-full" />
                  <div style={{ width: `${(1 - caShare) * 100}%`, background: "#f59e0b" }} className="h-full" />
                </div>
                <span className="text-[11px] font-semibold tabular-nums text-gray-700">{caPct.toFixed(0)}%</span>
              </div>

              <div className="mt-2 flex items-center gap-2.5 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#2EB4AC" }} />CA <span className="text-gray-800 font-semibold tabular-nums">{z.ca}</span></span>
                <span className="text-gray-300">·</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />CNA <span className="text-gray-800 font-semibold tabular-nums">{z.cna}</span></span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const KeywordCategoryHeatmap = ({ data }) => {
  const { keywords, categories, rows } = data;
  // global max for color scale
  const max = Math.max(1, ...rows.flatMap((r) => r.cells));

  return (
    <div className="overflow-x-auto" data-testid="keyword-category-heatmap">
      <table className="text-xs border-separate border-spacing-1 mx-auto">
        <thead>
          <tr>
            <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-1 pr-2"></th>
            {categories.map((c) => (
              <th key={c} className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-1 px-1 align-bottom">
                <div className="whitespace-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", height: 84 }} title={c}>{c}</div>
              </th>
            ))}
            <th className="text-[10px] font-semibold text-gray-400 pb-1 pl-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.keyword}>
              <td className="text-xs text-gray-700 pr-2 max-w-[140px] truncate" title={r.keyword}>{r.keyword}</td>
              {r.cells.map((v, i) => {
                const t = v / max;
                const bg = _rampColor(t);
                const dark = t > 0.55;
                return (
                  <td key={i} className="w-10 h-10 text-center align-middle">
                    <div
                      className={`w-9 h-9 rounded-md flex items-center justify-center text-[11px] font-semibold tabular-nums transition-colors ${dark ? "text-white" : "text-gray-700"}`}
                      style={{ background: v === 0 ? "#f9fafb" : bg }}
                      title={`${r.keyword} × ${categories[i]} = ${v}`}
                    >
                      {v === 0 ? <span className="text-gray-300">·</span> : v}
                    </div>
                  </td>
                );
              })}
              <td className="text-[11px] text-gray-500 font-semibold tabular-nums pl-2 text-right">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
// ─── END Cleaned-data visualizations ───

// ─── Brand multi-select dropdown ───
// Compact button that opens a checkbox menu listing "Wellversed" (umbrella,
// clears others = show all) + the 4 sub-brands (Yes-flag filter). Picking
// any sub-brand removes Wellversed. Empty selection collapses back to
// Wellversed so the map is never empty.
const BrandMultiSelect = ({ options, selected, onToggle, brandColors, lockToUmbrella = false }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isUmbrella = selected.has("wellversed");
  const activeBrands = Array.from(selected).filter((k) => k !== "wellversed");
  const label = isUmbrella
    ? "Wellversed"
    : activeBrands.length === 1
      ? options.find((o) => o.key === activeBrands[0])?.label || activeBrands[0]
      : `${activeBrands.length} brands`;
  const chipColor = !isUmbrella && activeBrands.length === 1
    ? brandColors[activeBrands[0]]
    : null;

  return (
    <label className="relative inline-flex items-center gap-1.5 text-[11px] text-gray-600">
      <span className={`font-semibold ${isUmbrella ? "text-gray-600" : "text-[#2EB4AC]"}`}>Brand</span>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded border border-gray-200 bg-white hover:border-[#2EB4AC] focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none cursor-pointer min-w-[120px] justify-between"
        data-testid="brand-filter-btn"
      >
        <span className="inline-flex items-center gap-1.5">
          {chipColor && (
            <span className="w-2 h-2 rounded-full" style={{ background: chipColor }} />
          )}
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 mt-1 z-30 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg py-1"
          data-testid="brand-filter-menu"
        >
          {lockToUmbrella && (
            <div
              className="mx-1 mb-1 px-2 py-1.5 rounded-md bg-amber-50 border border-amber-100 text-[10.5px] text-amber-800 leading-snug"
              data-testid="brand-filter-locked-banner"
            >
              Contacted mode locks the brand to <b>Wellversed</b>. Switch shading to <b>Penetration</b> to pick sub-brands.
            </div>
          )}
          {options.map((opt) => {
            const checked = selected.has(opt.key);
            const dotColor = brandColors[opt.key] || "#64748B";
            const isLocked = lockToUmbrella && opt.key !== "wellversed";
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => { if (!isLocked) onToggle(opt.key); }}
                disabled={isLocked}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors ${
                  isLocked
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-gray-50 cursor-pointer"
                } ${checked ? "bg-[#F0FDFA]" : ""}`}
                data-testid={`brand-filter-option-${opt.key}`}
                title={isLocked ? "Available only in Penetration shading mode" : undefined}
              >
                <span
                  className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                    checked
                      ? "bg-[#2EB4AC] border-[#2EB4AC]"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {checked && (
                    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white fill-none stroke-current" strokeWidth="2.5">
                      <path d="M2 6l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {!opt.isUmbrella && (
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor }} />
                )}
                <span className={`flex-1 ${opt.isUmbrella ? "font-semibold text-gray-800" : "text-gray-700"}`}>
                  {opt.label}
                </span>
                {opt.isUmbrella && (
                  <span className="text-[9px] uppercase tracking-wider text-gray-400">All</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </label>
  );
};

const OfflineCleanedLibrary = () => {
  // Cleaned-data library: users upload pre-validated store rosters here.
  // Kept STRICTLY separate from live-scraped data so analytics on cleaned
  // uploads aren't polluted with un-verified scraper output.
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRunDetail, setActiveRunDetail] = useState(null);
  const [activeRunLoading, setActiveRunLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [uploadMode, setUploadMode] = useState("append"); // 'append' | 'replace'
  const [analytics, setAnalytics] = useState(null);
  // Drill-down: clicking a state on the India map filters every aggregate
  // to that state. Null = pan-India view.
  const [selectedState, setSelectedState] = useState(null);
  // Map color metric: 'stores' (count) or 'ca_pct' (contact-availability rate)
  const [mapMode, setMapMode] = useState('stores');
  // Gradient mode for the India map choropleth:
  //   • 'relative' — shade scales against the top state's CA count (dynamic;
  //     current default; great for at-a-glance ranking).
  //   • 'coverage' — shade scales against fixed CA-count bands (1-49, 50-199,
  //     200-499, 500-999, 1k-2k, 2k+); stable across filter changes; great
  //     for reading absolute magnitude.
  const [gradientMode, setGradientMode] = useState('relative');
  // Zone donut filter — clicking a zone slice filters the All-Data table by region
  const [selectedZone, setSelectedZone] = useState(null);
  // "All Data" panel — flattened, paginated, searchable table across all uploads
  const [allRows, setAllRows] = useState([]);
  const [allRowsTotal, setAllRowsTotal] = useState(0);
  const [allRowsLoading, setAllRowsLoading] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [allStatus, setAllStatus] = useState("");
  const [allCategory, setAllCategory] = useState("");
  const [allKeyword, setAllKeyword] = useState("");
  const [allSource, setAllSource] = useState("");
  const [allPage, setAllPage] = useState(0);
  // Yes/No column filters that scope BOTH the map + the All-Data table.
  // Empty string = "don't care" (default). Values are canonical "Yes" / "No".
  // Brand flags (Wellcore/Dynamite/YouWefit/OSMO) are handled separately
  // via `brandFilter` (multi-select) below.
  const [flagFilters, setFlagFilters] = useState({ contacted: "" });
  // Multi-select brand filter. Wellversed = umbrella (no filter, show all).
  // Sub-brands OR-filter to rows where that brand's flag = Yes.
  const BRAND_OPTIONS = [
    { key: "wellversed", label: "Wellversed", isUmbrella: true },
    { key: "wellcore",   label: "Wellcore" },
    { key: "dynamite",   label: "Dynamite" },
    { key: "youwefit",   label: "YouWefit" },
    { key: "osmo",       label: "OSMO" },
  ];
  const [brandFilter, setBrandFilter] = useState(new Set(["wellversed"]));
  // Multi-select toggle. Picking Wellversed clears all sub-brands (= show all).
  // Picking any sub-brand removes Wellversed from the set.
  const toggleBrand = (key) => {
    setBrandFilter((prev) => {
      const next = new Set(prev);
      if (key === "wellversed") {
        return new Set(["wellversed"]);
      }
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        next.delete("wellversed");
      }
      // If user deselected everything, fall back to Wellversed (show all).
      if (next.size === 0) next.add("wellversed");
      return next;
    });
  };
  const activeFlagCount = Object.values(flagFilters).filter(Boolean).length
    + (brandFilter.has("wellversed") ? 0 : brandFilter.size);
  const clearFlagFilters = () => {
    setFlagFilters({ contacted: "" });
    setBrandFilter(new Set(["wellversed"]));
  };
  // Brand color palette used when a single brand filter is active. Each key
  // is the row-flag field (matches OFFLINE_CLEANED_YESNO_FIELDS on the
  // backend). Whenever exactly one sub-brand is selected, the India
  // choropleth switches from teal to that brand's colour so the map reads
  // as "Wellcore view", "Dynamite view", etc.
  const BRAND_COLORS = {
    wellcore: "#3B82F6",   // Blue
    dynamite: "#EF4444",   // Red
    youwefit: "#F59E0B",   // Yellow/Amber
    osmo:     "#1E3A8A",   // Dark Blue
  };
  const activeBrand = (() => {
    if (brandFilter.has("wellversed")) return null;
    if (brandFilter.size === 1) {
      const [only] = brandFilter;
      return BRAND_COLORS[only] ? only : null;
    }
    return null;
  })();
  const brandColor = activeBrand ? BRAND_COLORS[activeBrand] : null;
  // Build the params object that downstream axios calls will merge in.
  // `brands` is comma-joined; Wellversed (umbrella) means no brand param.
  const brandsParamValue = brandFilter.has("wellversed")
    ? ""
    : Array.from(brandFilter).join(",");
  const filterParams = useMemo(() => {
    const p = { ...flagFilters };
    if (brandsParamValue) p.brands = brandsParamValue;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagFilters.contacted, brandsParamValue]);
  // Distinct values for the dropdown filters (loaded once + after each upload)
  const [columns, setColumns] = useState({ categories: [], states: [], sources: [], keywords: [] });
  // State-drilldown pincode list (loaded when a state is selected)
  const [statePincodes, setStatePincodes] = useState(null);
  const [statePincodesLoading, setStatePincodesLoading] = useState(false);
  // ─── Country scoping ───────────────────────────────────────────────
  // Cleaned uploads are country-tagged (defaults to IN for pre-Country
  // uploads). The country tab strip above the analytics scopes every
  // aggregate to a single ISO alpha-2 code. Only countries with data
  // surface as tabs; India is always the default on load (no persistence
  // per user request — see /memory/PRD.md v6 planning).
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [countries, setCountries] = useState([]);
  const loadCountries = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/offline-cleaned/countries`);
      setCountries(r.data?.countries || []);
    } catch { /* silent — tabs just won't render */ }
  }, []);
  const ALL_PAGE_SIZE = 25;
  const fileRef = useRef(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/offline-cleaned/runs`);
      setRuns(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load uploads");
    }
    setLoading(false);
  }, []);

  const loadAnalytics = useCallback(async (stateFilter, zoneFilter, flags, countryCode) => {
    try {
      const params = { ...(flags || {}) };
      // Strip empty values so we don't send them to the backend as noise.
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      if (stateFilter) params.state = stateFilter;
      if (zoneFilter) params.zone = zoneFilter;
      if (countryCode) params.country = countryCode;
      const r = await axios.get(`${API}/offline-cleaned/analytics`, { params });
      setAnalytics(r.data || null);
    } catch (e) { /* noop */ }
  }, []);

  const loadAllRows = useCallback(async ({ search, status, state, zone, category, keyword, source, page, flags, country }) => {
    setAllRowsLoading(true);
    try {
      const params = {
        search: search || undefined,
        status: status || undefined,
        state: state || undefined,
        zone: zone || undefined,
        category: category || undefined,
        keyword: keyword || undefined,
        source: source || undefined,
        country: country || undefined,
        skip: (page || 0) * ALL_PAGE_SIZE,
        limit: ALL_PAGE_SIZE,
        ...(flags || {}),
      };
      // Drop any blank/undefined so axios doesn't send them.
      Object.keys(params).forEach((k) => { if (params[k] === undefined || params[k] === "") delete params[k]; });
      const r = await axios.get(`${API}/offline-cleaned/rows`, { params });
      setAllRows(r.data?.rows || []);
      setAllRowsTotal(r.data?.total || 0);
    } catch (e) { /* noop */ }
    setAllRowsLoading(false);
  }, []);

  const loadColumns = useCallback(async (countryCode) => {
    try {
      const params = countryCode ? { country: countryCode } : {};
      const r = await axios.get(`${API}/offline-cleaned/columns`, { params });
      setColumns(r.data || { categories: [], states: [], sources: [], keywords: [] });
    } catch (e) { /* noop */ }
  }, []);

  // Inline-edit a mutable field on one row. Optimistic update — table
  // reflects the new value instantly; if the backend rejects we roll back
  // and toast the error. Guards against a stale row where run_id / index
  // are missing (older responses that predate row_index).
  const patchCleanedRow = useCallback(async (row, field, value) => {
    if (!row?.run_id || row?.row_index == null) {
      toast.error("This row can't be edited — reload the table and retry.");
      return;
    }
    const prev = row[field];
    setAllRows((rows) => rows.map((r) =>
      (r.run_id === row.run_id && r.row_index === row.row_index)
        ? { ...r, [field]: value }
        : r
    ));
    try {
      await axios.patch(
        `${API}/offline-cleaned/runs/${row.run_id}/rows/${row.row_index}`,
        { [field]: value },
      );
    } catch (e) {
      setAllRows((rows) => rows.map((r) =>
        (r.run_id === row.run_id && r.row_index === row.row_index)
          ? { ...r, [field]: prev }
          : r
      ));
      toast.error(e?.response?.data?.detail || `Failed to save ${field}`);
    }
  }, []);

  useEffect(() => { loadRuns(); loadCountries(); }, [loadRuns, loadCountries]);
  useEffect(() => { loadColumns(selectedCountry); }, [loadColumns, selectedCountry]);
  useEffect(() => { loadAnalytics(selectedState, selectedZone, filterParams, selectedCountry); }, [loadAnalytics, selectedState, selectedZone, filterParams, selectedCountry]);
  // Reset drill-down state whenever the country changes so we don't
  // carry a stale state selection into the new country's view.
  useEffect(() => { setSelectedState(null); setSelectedZone(null); }, [selectedCountry]);
  // Load drill-down pincode list when a state is selected (or filters change)
  useEffect(() => {
    if (!selectedState) { setStatePincodes(null); return; }
    setStatePincodesLoading(true);
    const params = { ...filterParams };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    if (selectedCountry) params.country = selectedCountry;
    axios.get(`${API}/offline-cleaned/state/${encodeURIComponent(selectedState)}/pincodes`, { params })
      .then((r) => setStatePincodes(r.data || null))
      .catch(() => setStatePincodes(null))
      .finally(() => setStatePincodesLoading(false));
  }, [selectedState, filterParams, selectedCountry]);
  // Debounced reload for All-Data panel whenever any filter changes
  useEffect(() => {
    const t = setTimeout(() => {
      loadAllRows({
        search: allSearch, status: allStatus,
        state: selectedState, zone: selectedZone,
        category: allCategory, keyword: allKeyword, source: allSource,
        page: allPage, flags: filterParams, country: selectedCountry,
      });
    }, 250);
    return () => clearTimeout(t);
  }, [loadAllRows, allSearch, allStatus, selectedState, selectedZone, allCategory, allKeyword, allSource, allPage, filterParams, selectedCountry]);
  useEffect(() => { setAllPage(0); }, [allSearch, allStatus, selectedState, selectedZone, allCategory, allKeyword, allSource, filterParams, selectedCountry]);

  const downloadTemplate = () => {
    window.open(`${API}/offline-cleaned/sample-template`, "_blank");
  };

  const doUpload = async (file) => {
    if (!file) return;
    // Guard: if user selected replace mode and they already have data, confirm.
    if (uploadMode === "replace" && runs.length > 0) {
      if (!window.confirm(`This will DELETE all ${runs.length} existing upload(s) — including uploads made by other users — and replace them with the new file. This affects everyone using the shared cleaned library. Continue?`)) {
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", uploadMode);
    if (label.trim()) fd.append("label", label.trim());
    try {
      const r = await axios.post(`${API}/offline-cleaned/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const replacedNote = r.data.replaced ? ` · replaced ${r.data.replaced} prior upload(s)` : "";
      toast.success(`Uploaded ${r.data.total_rows} rows · CA ${r.data.ca_count} · CNA ${r.data.cna_count}${replacedNote}`);
      setLabel("");
      setActiveRunId(r.data.run_id);
      setActiveRunDetail(r.data);
      loadRuns();
      loadCountries();
      loadAnalytics(selectedState, selectedZone, filterParams, selectedCountry);
      loadColumns(selectedCountry);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      toast.error(detail.length > 200 ? detail.slice(0, 200) + "…" : detail);
    }
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
  };

  const openRun = async (runId) => {
    setActiveRunId(runId);
    setActiveRunLoading(true);
    setActiveRunDetail(null);
    try {
      const r = await axios.get(`${API}/offline-cleaned/runs/${runId}`);
      setActiveRunDetail(r.data);
    } catch (e) {
      toast.error("Failed to load upload");
      setActiveRunId(null);
    }
    setActiveRunLoading(false);
  };

  const deleteRun = async (runId) => {
    if (!window.confirm("Delete this upload? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/offline-cleaned/runs/${runId}`);
      toast.success("Upload deleted");
      if (activeRunId === runId) { setActiveRunId(null); setActiveRunDetail(null); }
      loadRuns();
      loadCountries();
      loadAnalytics(selectedState, selectedZone, filterParams, selectedCountry);
      loadColumns(selectedCountry);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportRun = (runId) => {
    window.open(`${API}/offline-cleaned/runs/${runId}/export`, "_blank");
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="offline-cleaned-library">
      {/* Upload card — full onboarding panel when there's no data yet,
          compact toolbar once the user has uploads (keeps the analytics front-and-center) */}
      {runs.length === 0 ? (
        <div className="dashboard-card">
          <div className="dashboard-card-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#2EB4AC]" />
              <span className="font-semibold text-sm text-gray-700">Upload Cleaned Data</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#1f8079]" style={{ background: "#e6f7f6", border: "1px solid #bce8e4" }}>START HERE</span>
            </div>
            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
              data-testid="offline-cleaned-template-btn"
            >
              <Download className="w-3 h-3" /> Sample Template
            </button>
          </div>
          <div className="dashboard-card-body">
            <div className="flex flex-col items-center text-center gap-4 py-2">
              <div className="text-xs text-gray-500 leading-relaxed max-w-2xl">
                Upload a CSV of <span className="font-semibold text-gray-700">pre-validated store data</span>. Required headers (case-insensitive):
                <span className="ml-1 font-mono text-[10.5px] text-gray-700">Keyword, Store Name, Pincode, Category, Contact Number, Status, Contacted, Wellcore, Dynamite, YouWefit, OSMO</span>.
                <span className="block mt-1 text-gray-400">Status must be <span className="font-semibold">CA</span> (Contact Available) or <span className="font-semibold">CNA</span> (Contact Not Available).</span>
              </div>
              <input
                type="file"
                ref={fileRef}
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => doUpload(e.target.files?.[0])}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold text-white rounded-xl hover:opacity-90 disabled:opacity-50 shadow-sm hover:shadow-md transition-all"
                style={{ background: "#2EB4AC" }}
                data-testid="offline-cleaned-upload-btn"
              >
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload CSV"}
              </button>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Optional: add a batch label (e.g. June 2026 — Delhi NCR)"
                className="w-full max-w-md text-xs text-center px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 placeholder:text-gray-400 focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none"
                data-testid="offline-cleaned-label-input"
              />
            </div>
          </div>
        </div>
      ) : (
        // Compact toolbar — sits above the analytics block as a small action row
        <div className="flex flex-wrap items-center gap-2" data-testid="offline-cleaned-toolbar">
          <input
            type="file"
            ref={fileRef}
            accept=".csv,.txt"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-md hover:opacity-90 disabled:opacity-50 shadow-sm transition-all"
            style={{ background: "#2EB4AC" }}
            data-testid="offline-cleaned-upload-btn"
          >
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload CSV"}
          </button>
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
            data-testid="offline-cleaned-template-btn"
          >
            <Download className="w-3 h-3" /> Sample
          </button>
          {/* Inline label + mode toggle (kept compact) */}
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Optional batch label"
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-700 placeholder:text-gray-400 focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none w-56"
            data-testid="offline-cleaned-label-input"
          />
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" data-testid="upload-mode-toggle">
            <button
              type="button"
              onClick={() => setUploadMode("append")}
              className={`px-2.5 py-1 text-[10.5px] font-semibold rounded transition-colors ${uploadMode === "append" ? "bg-gray-800 text-white" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="upload-mode-append"
              title="Adds the new file alongside existing uploads"
            >
              Append
            </button>
            <button
              type="button"
              onClick={() => setUploadMode("replace")}
              className={`px-2.5 py-1 text-[10.5px] font-semibold rounded transition-colors ${uploadMode === "replace" ? "bg-rose-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="upload-mode-replace"
              title="Deletes ALL existing uploads, then imports the new file"
            >
              Replace all
            </button>
          </div>
          {uploadMode === "replace" && (
            <span className="text-[10px] text-rose-600 font-semibold">⚠ Will delete all existing uploads</span>
          )}
        </div>
      )}

      {/* ─── Country tabs ────────────────────────────────────────────
          Shown as soon as at least ONE country has data. Tabs auto-adapt
          to whatever ISO codes appear in uploads — India is always the
          default (per user preference — no session persistence). Picking
          a country cascades to every downstream API call and swaps the
          map component (India ↦ SVG choropleth; other ↦ state grid). */}
      {countries.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap px-1" data-testid="country-tabs">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mr-2">Country</span>
          {countries.map((c) => {
            const isActive = c.code === selectedCountry;
            const flag = c.code
              .toUpperCase()
              .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
            return (
              <button
                key={c.code}
                onClick={() => setSelectedCountry(c.code)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-all ${
                  isActive
                    ? "bg-[#2EB4AC] text-white border-[#2EB4AC] shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                }`}
                data-testid={`country-tab-${c.code}`}
              >
                <span className="text-sm leading-none">{flag}</span>
                <span>{c.name}</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${
                  isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                }`}>{c.count.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Analytics block — shown only when there's data */}
      {analytics && analytics.total_rows > 0 && (
        <div className="space-y-3" data-testid="offline-cleaned-analytics">
          {/* Active filter pill — combines state + zone drill-downs */}
          {(selectedState || selectedZone) && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/60 flex-wrap" data-testid="state-filter-pill">
              <MapPin className="w-3.5 h-3.5 text-emerald-700" />
              <span className="text-xs text-gray-600">Filtering by</span>
              {selectedState && (
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800">
                  {selectedState}
                  <button onClick={() => setSelectedState(null)} className="text-emerald-600 hover:text-emerald-900" data-testid="clear-state-only">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedZone && (
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800">
                  <span className="text-xs text-emerald-500">·</span>
                  {selectedZone} zone
                  <button onClick={() => setSelectedZone(null)} className="text-emerald-600 hover:text-emerald-900" data-testid="clear-zone-only">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={() => { setSelectedState(null); setSelectedZone(null); }}
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
                data-testid="clear-state-filter-btn"
              >
                <X className="w-3 h-3" /> Clear all
              </button>
            </div>
          )}

          {/* Hero summary card — consolidates total, CA progress, and quick stats */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12">
              {/* Left: Total stores + CA progress */}
              <div className="md:col-span-7 p-5 border-b md:border-b-0 md:border-r border-gray-100">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                      {(() => {
                        // Hero label logic (two independent switches):
                        //   • In Penetration shade mode → "Brand touches" so
                        //     the user understands the number is weighted
                        //     by how many brands each store carries.
                        //   • In Contacted shade mode → raw store KPI label.
                        const cf = (flagFilters?.contacted || "").toLowerCase();
                        const isPenetration = gradientMode === 'coverage';
                        if (isPenetration) {
                          if (cf === "no") return selectedState ? `${selectedState} · not-contacted touches` : "Not-contacted brand touches";
                          return selectedState ? `${selectedState} · brand touches` : "Contacted brand touches";
                        }
                        if (cf === "yes") return selectedState ? `${selectedState} · contacted` : "Contacted stores";
                        if (cf === "no")  return selectedState ? `${selectedState} · not contacted` : "Not-contacted stores";
                        return selectedState ? `${selectedState} · stores` : "Total stores";
                      })()}
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-gray-900 tabular-nums">
                        {(() => {
                          const cf = (flagFilters?.contacted || "").toLowerCase();
                          const isPenetration = gradientMode === 'coverage';
                          // In Penetration mode we display the sum of
                          // per-state `penetrated` (brand-weighted) so the
                          // hero matches the map shading. Fall back to the
                          // raw contacted count if the backend hasn't been
                          // redeployed yet (older payloads lack `penetrated`).
                          if (isPenetration) {
                            const pen = (analytics?.top_states || []).reduce(
                              (sum, s) => sum + (s?.penetrated ?? 0), 0
                            );
                            if (pen > 0) return pen.toLocaleString('en-IN');
                            // No penetration data → fallback keeps the KPI alive.
                            return (analytics.contacted_yes || 0).toLocaleString('en-IN');
                          }
                          const n = cf === "yes"
                            ? (analytics.contacted_yes || 0)
                            : cf === "no"
                              ? (analytics.contacted_no || 0)
                              : (analytics.total_rows || 0);
                          return n.toLocaleString('en-IN');
                        })()}
                      </span>
                      <span className="text-xs text-gray-400">
                        {(() => {
                          const cf = (flagFilters?.contacted || "").toLowerCase();
                          const isPenetration = gradientMode === 'coverage';
                          if (isPenetration) return `touches · ${analytics.unique_stores.toLocaleString('en-IN')} unique stores`;
                          if (cf) return `stores · ${analytics.unique_stores.toLocaleString('en-IN')} unique`;
                          return `rows · ${analytics.unique_stores.toLocaleString('en-IN')} unique`;
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">Contacted rate</div>
                    <div className="mt-1 text-3xl font-bold text-emerald-700 tabular-nums">{analytics.contacted_pct ?? 0}<span className="text-base text-emerald-500">%</span></div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden flex" title={`${analytics.contacted_yes || 0} contacted touches · ${analytics.contacted_no || 0} not-yet touches`}>
                    <div style={{ width: `${analytics.contacted_pct || 0}%` }} className="h-full bg-emerald-500 transition-all" />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-gray-500">Contacted</span>
                      <span className="font-semibold text-gray-800 tabular-nums">{(analytics.contacted_yes || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-gray-300" />
                      <span className="text-gray-500">Not yet</span>
                      <span className="font-semibold text-gray-800 tabular-nums">{(analytics.contacted_no || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: 4 quick-stat cells */}
              <div className="md:col-span-5 grid grid-cols-2 divide-x divide-y divide-gray-100">
                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">States</div>
                  <div className="mt-1 text-xl font-bold text-gray-800 tabular-nums">{analytics.unique_states}</div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Pincodes</div>
                  <div className="mt-1 text-xl font-bold text-gray-800 tabular-nums">{analytics.unique_pincodes}</div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Categories</div>
                  <div className="mt-1 text-xl font-bold text-gray-800 tabular-nums">{analytics.unique_categories}</div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Uploads</div>
                  <div className="mt-1 text-xl font-bold text-gray-800 tabular-nums">{analytics.total_uploads}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Geography row — 50/50: India map on the left, Regional split + Top states stacked on the right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="dashboard-card flex flex-col" style={{ minHeight: 760 }}>
              <div className="dashboard-card-header flex items-center justify-between gap-3 shrink-0">
                <div className="inline-flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-700" data-testid="map-country-title">
                    {selectedCountry === "IN"
                      ? "India coverage"
                      : `${((countries.find(c => c.code === selectedCountry) || {}).name || selectedCountry)} coverage`}
                  </span>
                  {activeBrand && (
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: `${brandColor}15`, color: brandColor, border: `1px solid ${brandColor}40` }}
                      data-testid="map-brand-tint-badge"
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: brandColor }} />
                      {activeBrand.charAt(0).toUpperCase() + activeBrand.slice(1)} view
                    </span>
                  )}
                </div>
                {/* Gradient shading toggle — moved to header for high visibility.
                    • Switching to Contacted mode snaps the brand filter back to
                      Wellversed-only (the "overall stores" view is umbrella-only).
                    • Switching to Penetration mode promotes any blank Contacted
                      filter to "Yes" since Penetration = brands contacted yes/no
                      (no "Any" option in the dropdown for that mode). */}
                <GradientModeToggle
                  mode={gradientMode}
                  onChange={(m) => {
                    setGradientMode(m);
                    if (m === 'relative') {
                      setBrandFilter(new Set(["wellversed"]));
                    } else if (m === 'coverage') {
                      setFlagFilters((prev) => (
                        prev.contacted === '' ? { ...prev, contacted: 'Yes' } : prev
                      ));
                    }
                  }}
                  brandColor={brandColor}
                />
              </div>

              {/* Filters — Contacted (Yes/No/Any) + Brand (multi-select).
                  Scope the map + drilldown + All-Data table + KPIs. */}
              <div
                className="px-3 py-2 border-b border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1.5 shrink-0"
                data-testid="map-flag-filters"
              >
                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-gray-500">Filters</span>

                {/* Contacted single-select.
                    • In Contacted shading mode: Any / Yes / No — user wants to see
                      how many overall stores have been contacted vs not.
                    • In Penetration shading mode: Yes / No ONLY — the view is
                      "brands contacted yes-or-no", so "Any" doesn't fit. Mode
                      switch auto-promotes a blank filter to "Yes". */}
                <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                  <span className={`font-semibold ${flagFilters.contacted ? "text-[#2EB4AC]" : "text-gray-600"}`}>Contacted</span>
                  <select
                    value={flagFilters.contacted || (gradientMode === 'coverage' ? 'Yes' : '')}
                    onChange={(e) => setFlagFilters((prev) => ({ ...prev, contacted: e.target.value }))}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-white hover:border-[#2EB4AC] focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none cursor-pointer"
                    data-testid="map-flag-filter-contacted"
                  >
                    {gradientMode !== 'coverage' && <option value="">Any</option>}
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </label>

                {/* Brand multi-select — Wellversed (all) + 4 sub-brands (Yes flag).
                    Sub-brand chips are DISABLED in Contacted (relative) mode
                    since that view is Wellversed-umbrella-only by design. */}
                <BrandMultiSelect
                  options={BRAND_OPTIONS}
                  selected={brandFilter}
                  onToggle={toggleBrand}
                  brandColors={BRAND_COLORS}
                  lockToUmbrella={gradientMode === 'relative'}
                />

                {activeFlagCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFlagFilters}
                    className="ml-auto text-[10.5px] font-semibold text-gray-500 hover:text-rose-600 inline-flex items-center gap-1"
                    data-testid="map-flag-filters-reset"
                    title="Clear all filters"
                  >
                    <X className="w-3 h-3" /> Clear ({activeFlagCount})
                  </button>
                )}
              </div>

              <div className="dashboard-card-body flex-1 flex flex-col min-h-0">
                {selectedCountry === "IN" ? (
                  <IndiaChoropleth
                    states={analytics.top_states}
                    selected={selectedState}
                    onSelect={setSelectedState}
                    mode={mapMode}
                    brandColor={brandColor}
                    gradientMode={gradientMode}
                  />
                ) : (
                  <CountryChoropleth
                    country={selectedCountry}
                    states={analytics.top_states}
                    selected={selectedState}
                    onSelect={setSelectedState}
                    brandColor={brandColor}
                    gradientMode={gradientMode}
                    countryName={(countries.find(c => c.code === selectedCountry) || {}).name || selectedCountry}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3" style={{ minHeight: 760 }}>
              <div className="dashboard-card flex flex-col">
                <div className="dashboard-card-header flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-700">Regional split</span>
                  <span className="text-[10px] text-gray-400">click a tile to filter</span>
                </div>
                <div className="dashboard-card-body">
                  <ZoneDonut zones={analytics.by_zone} selected={selectedZone} onSelect={setSelectedZone} filterParams={filterParams} gradientMode={gradientMode} />
                </div>
              </div>
              <div className="dashboard-card flex flex-col flex-1 min-h-0">
                <div className="dashboard-card-header flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-700">Top states</span>
                  <span className="text-[10px] text-gray-400">click to open map</span>
                </div>
                <div className="dashboard-card-body p-2 space-y-1.5 overflow-y-auto flex-1">
                  {analytics.top_states.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center py-6">No state data</div>
                  ) : (
                    analytics.top_states.slice(0, 15).map((s) => {
                      const max = Math.max(1, ...analytics.top_states.map(x => x.count));
                      const pct = (s.count / max) * 100;
                      const isActive = selectedState === s.label;
                      return (
                        <button
                          key={s.label}
                          onClick={() => setSelectedState(isActive ? null : s.label)}
                          className={`w-full flex items-center gap-2 text-left rounded-md p-1.5 transition-colors ${isActive ? 'bg-[#e6f7f6]' : 'hover:bg-gray-50'}`}
                          data-testid={`state-row-${s.label}`}
                        >
                          <div className="w-28 text-xs text-gray-700 truncate" title={s.label}>{s.label}</div>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div style={{ width: `${pct}%`, background: isActive ? BRAND_DARK : BRAND }} className="h-full transition-all" />
                          </div>
                          <div className="w-8 text-right text-[11px] font-semibold tabular-nums text-gray-700">{s.count}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Fullscreen Google-Maps-style state drill-down modal */}
          {selectedState && (
            <StateMapModal
              state={selectedState}
              states={analytics.top_states}
              pincodes={statePincodes}
              pincodesLoading={statePincodesLoading}
              mapMode={mapMode}
              setMapMode={setMapMode}
              onSelectState={setSelectedState}
              onClose={() => setSelectedState(null)}
              flagFilters={filterParams}
              brandColor={brandColor}
              gradientMode={gradientMode}
            />
          )}

          {/* Top categories + keywords + pincodes — three equal-width leaderboards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Top categories with CA% rings */}
            <div className="dashboard-card">
              <div className="dashboard-card-header flex items-center justify-between">
                <span className="font-semibold text-sm text-gray-700">Top categories</span>
                <span className="text-[10px] text-gray-400">CA %</span>
              </div>
              <div className="dashboard-card-body p-0">
                {analytics.top_categories.length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-6">No category data</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {analytics.top_categories.map((c, idx) => {
                      const caPct = c.count ? Math.round((c.ca / c.count) * 100) : 0;
                      // Color tier badge — high = teal, mid = amber, low = rose
                      const tier = caPct >= 80
                        ? { bg: "#e6f7f6", text: BRAND_DARK, bar: BRAND }
                        : caPct >= 50
                          ? { bg: "#fffbeb", text: "#b45309", bar: "#f59e0b" }
                          : { bg: "#fef2f2", text: "#be123c", bar: "#fb7185" };
                      return (
                        <div key={c.label} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50/60 transition-colors">
                          <div className="w-5 text-center text-[10.5px] font-bold text-gray-400 tabular-nums">#{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-800 truncate" title={c.label}>{c.label}</div>
                            <div className="mt-0.5 text-[10.5px] text-gray-500 flex items-center gap-1.5">
                              <span className="font-semibold text-gray-700 tabular-nums">{c.count}</span>
                              <span className="text-gray-300">·</span>
                              <span style={{ color: BRAND_DARK }} className="font-semibold tabular-nums">CA {c.ca}</span>
                              <span className="text-gray-300">·</span>
                              <span className="text-amber-700 font-semibold tabular-nums">CNA {c.cna}</span>
                            </div>
                          </div>
                          {/* CA% pill badge with mini fill bar — clearer than a cramped ring */}
                          <div className="shrink-0 flex flex-col items-end gap-1">
                            <span
                              className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full"
                              style={{ background: tier.bg, color: tier.text }}
                            >{caPct}%</span>
                            <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div style={{ width: `${caPct}%`, background: tier.bar }} className="h-full transition-all" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Top keywords */}
            <div className="dashboard-card">
              <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Top keywords</span></div>
              <div className="dashboard-card-body p-0">
                {analytics.top_keywords.length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-6">No keyword data</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {analytics.top_keywords.map((k, idx) => {
                      const max = Math.max(1, ...analytics.top_keywords.map(x => x.count));
                      const pct = (k.count / max) * 100;
                      return (
                        <div key={k.label} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50/60 transition-colors">
                          <div className="w-5 text-center text-[10.5px] font-bold text-gray-400 tabular-nums">#{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-800 truncate" title={k.label}>{k.label}</div>
                            <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div style={{ width: `${pct}%`, background: BRAND }} className="h-full" />
                            </div>
                          </div>
                          <div className="text-sm font-bold tabular-nums text-gray-900 w-7 text-right">{k.count}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Top pincodes */}
            <div className="dashboard-card">
              <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Top pincodes</span></div>
              <div className="dashboard-card-body p-0">
                {analytics.top_pincodes.length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-6">No pincode data</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {analytics.top_pincodes.map((p, idx) => {
                      const max = Math.max(1, ...analytics.top_pincodes.map(x => x.count));
                      const pct = (p.count / max) * 100;
                      return (
                        <div key={p.label} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50/60 transition-colors">
                          <div className="w-5 text-center text-[10.5px] font-bold text-gray-400 tabular-nums">#{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-800 tabular-nums font-mono">{p.label}</div>
                            <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div style={{ width: `${pct}%`, background: "#0ea5e9" }} className="h-full" />
                            </div>
                          </div>
                          <div className="text-sm font-bold tabular-nums text-gray-900 w-7 text-right">{p.count}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* All Data — combined, paginated, searchable view across every upload */}
          <div className="dashboard-card" data-testid="offline-cleaned-all-data">
            <div className="dashboard-card-header flex flex-wrap items-center gap-2 justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-gray-700">All data</span>
                <span className="text-[11px] text-gray-400 tabular-nums">
                  {allRowsTotal.toLocaleString('en-IN')} row{allRowsTotal === 1 ? '' : 's'}
                  {selectedState && <span className="ml-1">in {selectedState}</span>}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={allSearch}
                    onChange={(e) => setAllSearch(e.target.value)}
                    placeholder="Search store, keyword, pincode…"
                    className="text-xs pl-7 pr-3 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none w-64"
                    data-testid="all-data-search"
                  />
                </div>
                {(allSearch || allStatus || allCategory || allKeyword || allSource) && (
                  <button
                    onClick={() => { setAllSearch(""); setAllStatus(""); setAllCategory(""); setAllKeyword(""); setAllSource(""); }}
                    className="text-[11px] text-gray-500 hover:text-gray-800 font-semibold inline-flex items-center gap-1"
                    data-testid="all-data-reset-filters"
                    title="Clear all column filters"
                  >
                    <X className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
            </div>

            {/* Per-column filter row */}
            <div className="px-3 py-2 border-b border-gray-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="all-data-column-filters">
              <select
                value={allKeyword}
                onChange={(e) => setAllKeyword(e.target.value)}
                className="text-[11px] px-2 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] outline-none bg-white"
                data-testid="all-data-keyword-filter"
              >
                <option value="">All keywords</option>
                {columns.keywords.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <select
                value={allCategory}
                onChange={(e) => setAllCategory(e.target.value)}
                className="text-[11px] px-2 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] outline-none bg-white"
                data-testid="all-data-category-filter"
              >
                <option value="">All categories</option>
                {columns.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={selectedState || ""}
                onChange={(e) => setSelectedState(e.target.value || null)}
                className="text-[11px] px-2 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] outline-none bg-white"
                data-testid="all-data-state-filter"
              >
                <option value="">All states</option>
                {columns.states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={allStatus}
                onChange={(e) => setAllStatus(e.target.value)}
                className="text-[11px] px-2 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] outline-none bg-white"
                data-testid="all-data-status-filter"
              >
                <option value="">All status</option>
                <option value="CA">CA only</option>
                <option value="CNA">CNA only</option>
              </select>
              <select
                value={allSource}
                onChange={(e) => setAllSource(e.target.value)}
                className="text-[11px] px-2 py-1.5 rounded-md border border-gray-200 focus:border-[#2EB4AC] outline-none bg-white"
                data-testid="all-data-source-filter"
              >
                <option value="">All sources</option>
                {columns.sources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="dashboard-card-body p-0">
              {allRowsLoading && allRows.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-400">Loading…</div>
              ) : allRows.length === 0 ? (
                <div className="p-10 text-center text-xs text-gray-400">
                  {allSearch || allStatus || selectedState
                    ? "No rows match the current filters. Try clearing them."
                    : "No data yet — upload a CSV to populate this view."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="all-data-table">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2">Store</th>
                        <th className="px-3 py-2">Keyword</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Pincode</th>
                        <th className="px-3 py-2">State</th>
                        <th className="px-3 py-2">Contact</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Contacted</th>
                        <th className="px-3 py-2 text-[#2EB4AC]">Wellcore</th>
                        <th className="px-3 py-2">Dynamite</th>
                        <th className="px-3 py-2">YouWefit</th>
                        <th className="px-3 py-2">OSMO</th>
                        <th className="px-3 py-2">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {allRows.map((r, i) => {
                        const rowKey = `${r.run_id}-${r.row_index ?? i}`;
                        const cellSelect = (field, options) => (
                          <select
                            value={r[field] || ""}
                            onChange={(e) => patchCleanedRow(r, field, e.target.value)}
                            className="text-[11px] px-1.5 py-1 rounded border border-gray-200 bg-white hover:border-[#2EB4AC] focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#bce8e4] outline-none cursor-pointer"
                            data-testid={`row-${rowKey}-${field}`}
                            aria-label={`${field} for row ${i + 1}`}
                          >
                            {options.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        );
                        const yesNoOptions = [
                          { value: "",    label: "—" },
                          { value: "Yes", label: "Yes" },
                          { value: "No",  label: "No" },
                        ];
                        const statusOptions = [
                          { value: "",    label: "—" },
                          { value: "CA",  label: "CA" },
                          { value: "CNA", label: "CNA" },
                        ];
                        return (
                          <tr key={rowKey} className="hover:bg-gray-50/60">
                            <td className="px-3 py-2 text-gray-800 font-medium">{r.store_name || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{r.keyword || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{r.category || "—"}</td>
                            <td className="px-3 py-2 text-gray-600 tabular-nums font-mono">{r.pincode || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{r.state || "—"}</td>
                            <td className="px-3 py-2 text-gray-600 tabular-nums">{r.contact_number || "—"}</td>
                            <td className="px-3 py-2">{cellSelect("status", statusOptions)}</td>
                            <td className="px-3 py-2">{cellSelect("contacted", yesNoOptions)}</td>
                            <td className="px-3 py-2">{cellSelect("wellcore", yesNoOptions)}</td>
                            <td className="px-3 py-2">{cellSelect("dynamite", yesNoOptions)}</td>
                            <td className="px-3 py-2">{cellSelect("youwefit", yesNoOptions)}</td>
                            <td className="px-3 py-2">{cellSelect("osmo", yesNoOptions)}</td>
                            <td className="px-3 py-2 text-[11px] text-gray-400 truncate max-w-[180px]" title={r.run_label}>{r.run_label || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {allRowsTotal > ALL_PAGE_SIZE && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">
                  <span>
                    Showing <span className="tabular-nums font-semibold text-gray-700">{allPage * ALL_PAGE_SIZE + 1}–{Math.min((allPage + 1) * ALL_PAGE_SIZE, allRowsTotal)}</span> of <span className="tabular-nums font-semibold text-gray-700">{allRowsTotal.toLocaleString('en-IN')}</span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setAllPage(p => Math.max(0, p - 1))}
                      disabled={allPage === 0}
                      className="px-2 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                      data-testid="all-data-prev"
                    >Prev</button>
                    <span className="tabular-nums px-2">{allPage + 1} / {Math.max(1, Math.ceil(allRowsTotal / ALL_PAGE_SIZE))}</span>
                    <button
                      onClick={() => setAllPage(p => p + 1)}
                      disabled={(allPage + 1) * ALL_PAGE_SIZE >= allRowsTotal}
                      className="px-2 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                      data-testid="all-data-next"
                    >Next</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History list + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: history */}
        <div className="lg:col-span-5">
          <div className="dashboard-card">
            <div className="dashboard-card-header flex items-center justify-between">
              <span className="font-semibold text-sm text-gray-700">Your Uploads</span>
              <span className="text-[11px] text-gray-400 tabular-nums">{runs.length}</span>
            </div>
            <div className="dashboard-card-body p-0">
              {loading ? (
                <div className="p-6 text-center text-xs text-gray-400">Loading…</div>
              ) : runs.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-400">No uploads yet. Use the panel above to import your first cleaned CSV.</div>
              ) : (
                <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto" data-testid="offline-cleaned-runs-list">
                  {runs.map((r) => (
                    <div
                      key={r.run_id}
                      onClick={() => openRun(r.run_id)}
                      className={`p-3 cursor-pointer transition-colors ${activeRunId === r.run_id ? "bg-emerald-50/60" : "hover:bg-gray-50"}`}
                      data-testid={`offline-cleaned-run-${r.run_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{r.label || r.file_name}</div>
                          <div className="text-[11px] text-gray-400 mt-0.5">{fmtDate(r.created_at)} · {r.file_name}</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteRun(r.run_id); }}
                          className="text-gray-300 hover:text-rose-500 transition-colors p-1"
                          title="Delete"
                          data-testid={`offline-cleaned-delete-${r.run_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 tabular-nums">{r.total_rows} rows</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 tabular-nums">CA {r.ca_count}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 tabular-nums">CNA {r.cna_count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: preview */}
        <div className="lg:col-span-7">
          <div className="dashboard-card">
            <div className="dashboard-card-header flex items-center justify-between">
              <span className="font-semibold text-sm text-gray-700">
                {activeRunDetail ? (activeRunDetail.label || activeRunDetail.file_name) : "Preview"}
              </span>
              {activeRunDetail && (
                <button
                  onClick={() => exportRun(activeRunDetail.run_id)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  data-testid="offline-cleaned-export-btn"
                >
                  <Download className="w-3 h-3" /> Export CSV
                </button>
              )}
            </div>
            <div className="dashboard-card-body p-0">
              {activeRunLoading ? (
                <div className="p-6 text-center text-xs text-gray-400">Loading…</div>
              ) : !activeRunDetail ? (
                <div className="p-10 text-center text-xs text-gray-400">Select an upload from the left to preview its rows.</div>
              ) : (
                <div className="max-h-[420px] overflow-auto" data-testid="offline-cleaned-preview-table">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2">Keyword</th>
                        <th className="px-3 py-2">Store Name</th>
                        <th className="px-3 py-2">Pincode</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Contact</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(activeRunDetail.rows || []).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/60">
                          <td className="px-3 py-2 text-gray-600">{row.keyword || "—"}</td>
                          <td className="px-3 py-2 text-gray-800 font-medium">{row.store_name || "—"}</td>
                          <td className="px-3 py-2 text-gray-600 tabular-nums">{row.pincode || "—"}</td>
                          <td className="px-3 py-2 text-gray-600">{row.category || "—"}</td>
                          <td className="px-3 py-2 text-gray-600 tabular-nums">{row.contact_number || "—"}</td>
                          <td className="px-3 py-2">
                            {row.status === "CA" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">CA</span>}
                            {row.status === "CNA" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">CNA</span>}
                            {!row.status && <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sticky freshness ticker ───
// One-line marquee that scrolls "Last updated N hours ago · U uploads ·
// R total stores" repeatedly across the top of the Offline Intelligence
// page. Sits directly below the sticky AppHeader (top-14) so it stays in
// view as the user scrolls. Auto-hides when there's no data yet.
const LastUpdatedTicker = ({ meta }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Re-render every 30s so the relative time chip stays fresh even when
    // no upload has happened.
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  if (!meta || !meta.last_updated_at) return null;

  const relTime = (iso) => {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "just now";
    const diffSec = Math.max(0, Math.floor((now - t) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
    const mo = Math.floor(d / 30);
    return `${mo} month${mo === 1 ? "" : "s"} ago`;
  };
  const absTime = (() => {
    try {
      return new Date(meta.last_updated_at).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return meta.last_updated_at; }
  })();

  const rel = relTime(meta.last_updated_at);
  const uploads = (meta.total_uploads || 0).toLocaleString("en-IN");
  const rows = (meta.total_rows || 0).toLocaleString("en-IN");

  // A single "block" of ticker content, rendered as inline JSX (not a
  // nested component — hoisted per React rules). We render it multiple
  // times so the CSS translateX(-50%) loop is seamless regardless of
  // viewport width.
  const block = (
    <span className="inline-flex items-center gap-3 pr-10 whitespace-nowrap">
      <span className="inline-flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-emerald-700">Live</span>
      </span>
      <span className="text-gray-300">·</span>
      <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-700">
        <Clock className="w-3.5 h-3.5 text-[#32B4AC]" />
        <span className="font-semibold text-gray-900">Last updated</span>
        <span className="text-[#32B4AC] font-semibold">{rel}</span>
        <span className="text-gray-400 text-[11px]">({absTime} IST)</span>
      </span>
      <span className="text-gray-300">·</span>
      <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-700">
        <Upload className="w-3.5 h-3.5 text-indigo-500" />
        <span className="font-semibold text-gray-900 tabular-nums">{uploads}</span>
        <span className="text-gray-500">upload{meta.total_uploads === 1 ? "" : "s"}</span>
      </span>
      <span className="text-gray-300">·</span>
      <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-700">
        <Layers className="w-3.5 h-3.5 text-amber-500" />
        <span className="font-semibold text-gray-900 tabular-nums">{rows}</span>
        <span className="text-gray-500">total stores</span>
      </span>
    </span>
  );

  return (
    <>
      <div
        className="fixed left-0 right-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-sm overflow-hidden shadow-sm"
        style={{ top: "56px" }}
        data-testid="offline-freshness-ticker"
        aria-label="Store data freshness ticker"
      >
        <style>{`
          @keyframes horizonTickerScroll {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          .horizon-ticker-track {
            display: inline-flex;
            align-items: center;
            animation: horizonTickerScroll 30s linear infinite;
            will-change: transform;
          }
          .horizon-ticker-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="py-2 whitespace-nowrap">
          <div className="horizon-ticker-track" data-testid="offline-ticker-track">
            {/* Rendered as two identical halves so translateX(-50%) creates a
                seamless loop (the second half fills the gap left behind). */}
            {[0, 1, 2].map((k) => <React.Fragment key={`a-${k}`}>{block}</React.Fragment>)}
            {[0, 1, 2].map((k) => <React.Fragment key={`b-${k}`}>{block}</React.Fragment>)}
          </div>
        </div>
      </div>
      {/* Spacer so downstream content isn't hidden behind the fixed ticker */}
      <div aria-hidden="true" style={{ height: "36px" }} data-testid="offline-ticker-spacer" />
    </>
  );
};

const OfflineMasterAnalytics = () => {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  // Master Database has two sources of truth — pre-validated CSV uploads
  // (cleaned, default) and accumulated live scrapes (live). The toggle below
  // routes between them without affecting the Scraper tab.
  const [masterDataMode, setMasterDataMode] = useState("cleaned"); // 'cleaned' | 'live'

  // Freshness meta for the sticky ticker — fetched on mount, refreshed
  // every 60s. Cheap call (analytics endpoint returns 3 tiny fields on top
  // of the aggregation it's already doing).
  const [tickerMeta, setTickerMeta] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchMeta = async () => {
      try {
        const r = await axios.get(`${API}/offline-cleaned/analytics`);
        if (!cancelled) setTickerMeta({
          last_updated_at: r.data?.last_updated_at,
          total_uploads: r.data?.total_uploads || 0,
          total_rows: r.data?.total_rows || 0,
        });
      } catch { /* ticker hides on failure */ }
    };
    fetchMeta();
    const t = setInterval(fetchMeta, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [caFilter, setCaFilter] = useState("");           // '' | 'CA' | 'CNA'
  const [kwFilter, setKwFilter] = useState("");
  const [pinFilter, setPinFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const fileRef = useRef(null);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (searchDebounced.trim()) p.search = searchDebounced.trim();
    if (caFilter) p.ca_status = caFilter;
    if (kwFilter.trim()) p.keyword = kwFilter.trim();
    if (pinFilter.trim()) p.pincode = pinFilter.trim();
    if (start) p.start = start;
    if (end) p.end = end;
    return p;
  }, [page, searchDebounced, caFilter, kwFilter, pinFilter, start, end]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/offline/master`, { params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load master");
    }
    setLoading(false);
  }, [params]);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/offline/master/stats`, { params: { days: 30 } });
      setStats(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [searchDebounced, caFilter, kwFilter, pinFilter, start, end]);

  const doExport = async () => {
    try {
      const qs = new URLSearchParams();
      if (searchDebounced.trim()) qs.set("search", searchDebounced.trim());
      if (caFilter) qs.set("ca_status", caFilter);
      if (kwFilter.trim()) qs.set("keyword", kwFilter.trim());
      if (pinFilter.trim()) qs.set("pincode", pinFilter.trim());
      if (start) qs.set("start", start);
      if (end) qs.set("end", end);
      window.open(`${API}/offline/master/export?${qs.toString()}`, "_blank");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/offline/master/sample-template`, "_blank");
  };

  const doImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/offline/master/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${r.data?.imported} new, skipped ${r.data?.skipped_duplicates} duplicates`);
      loadList();
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const t = stats?.totals || { unique_stores: 0, ca: 0, cna: 0, ca_pct: 0 };
  const daily = stats?.daily || [];
  const maxDaily = Math.max(1, ...daily.map(d => d.count));
  const topPins = stats?.top_pincodes || [];
  const topKws = stats?.top_keywords || [];
  const cov = stats?.coverage || { pincodes: [], keywords: [], matrix: [] };

  // Coverage matrix intensity helper
  let covMax = 0;
  (cov.matrix || []).forEach(r => Object.values(r.cells || {}).forEach(v => { if (v > covMax) covMax = v; }));
  const covColor = (v) => {
    if (!v) return 'bg-gray-50 text-gray-300';
    const pct = v / Math.max(covMax, 1);
    if (pct > 0.66) return 'bg-[#32B4AC] text-white';
    if (pct > 0.33) return 'bg-[#32B4AC]/50 text-white';
    return 'bg-[#32B4AC]/20 text-[#1f7a74]';
  };

  // Contribution-grid (calendar-style last 30 days, 7 rows x ~5 cols)
  const gridIntensity = (c) => {
    if (!c) return 'bg-gray-100';
    const pct = c / Math.max(maxDaily, 1);
    if (pct > 0.66) return 'bg-emerald-500';
    if (pct > 0.33) return 'bg-emerald-400';
    return 'bg-emerald-200';
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="offline-master-analytics">
      {/* Sticky freshness ticker — one continuous marquee of "Last updated N ago" */}
      <LastUpdatedTicker meta={tickerMeta} />

      {/* Master DB source sub-toggle: Cleaned (default, CSV uploads) vs Live (accumulated scrapes) */}
      <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-gradient-to-r from-emerald-50/60 via-white to-amber-50/40" data-testid="master-data-mode-bar">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-200">
            <LayoutGrid className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Data source</div>
            <div className="text-xs text-gray-700 leading-snug">
              {masterDataMode === 'cleaned'
                ? 'Pre-validated store rosters uploaded via CSV.'
                : 'Stores accumulated from live Maps/Offline scrapes (results need validation over time).'}
            </div>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shrink-0" data-testid="master-data-mode-toggle">
          <button
            type="button"
            onClick={() => setMasterDataMode('cleaned')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
              masterDataMode === 'cleaned' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="master-mode-cleaned"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Cleaned Data
          </button>
          <button
            type="button"
            onClick={() => setMasterDataMode('live')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
              masterDataMode === 'live' ? 'bg-gray-800 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="master-mode-live"
          >
            <Activity className="w-3.5 h-3.5" /> Live Data
            <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-400 text-amber-900 ml-0.5">BETA</span>
          </button>
        </div>
      </div>

      {masterDataMode === 'cleaned' ? (
        <OfflineCleanedLibrary />
      ) : (
      <>
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="dashboard-card"><div className="dashboard-card-body">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Unique stores</div>
          <div className="text-2xl font-bold text-gray-800 mt-1 tabular-nums" data-testid="master-stat-total">{t.unique_stores.toLocaleString('en-IN')}</div>
        </div></div>
        <div className="dashboard-card"><div className="dashboard-card-body">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">Contact Available (CA)</div>
          <div className="text-2xl font-bold text-gray-800 mt-1 tabular-nums">{t.ca.toLocaleString('en-IN')}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">{t.ca_pct}% of master</div>
        </div></div>
        <div className="dashboard-card"><div className="dashboard-card-body">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">Contact Not Available</div>
          <div className="text-2xl font-bold text-gray-800 mt-1 tabular-nums">{t.cna.toLocaleString('en-IN')}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">{(100 - t.ca_pct).toFixed(1)}% need phone</div>
        </div></div>
        <div className="dashboard-card"><div className="dashboard-card-body">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600">Added today</div>
          <div className="text-2xl font-bold text-gray-800 mt-1 tabular-nums">{(daily[daily.length - 1]?.count || 0).toLocaleString('en-IN')}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">Yesterday: {(daily[daily.length - 2]?.count || 0)}</div>
        </div></div>
      </div>

      {/* Daily log: sparkline + calendar grid */}
      <div className="dashboard-card">
        <div className="dashboard-card-header flex items-center justify-between">
          <span className="font-semibold text-sm text-gray-700">Daily unique entries — last 30 days</span>
          <span className="text-[10px] text-gray-400">{daily.reduce((a, b) => a + b.count, 0)} added in window</span>
        </div>
        <div className="dashboard-card-body space-y-3">
          {/* Sparkline bars */}
          <div className="flex items-end gap-1 h-20" data-testid="master-daily-spark">
            {daily.map((d, i) => (
              <div
                key={d.date}
                className="flex-1 bg-emerald-200 hover:bg-emerald-400 rounded-t transition-colors group relative"
                style={{ height: `${Math.max(4, (d.count / maxDaily) * 100)}%`, minHeight: 4 }}
                title={`${d.date}: ${d.count} new`}
              >
                {d.count > 0 && (
                  <span className="opacity-0 group-hover:opacity-100 absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-gray-700 whitespace-nowrap">{d.count}</span>
                )}
              </div>
            ))}
          </div>
          {/* Calendar / contribution grid */}
          <div className="flex gap-0.5" data-testid="master-daily-grid">
            {daily.map((d) => (
              <div
                key={`g-${d.date}`}
                className={`w-3 h-3 rounded-sm ${gridIntensity(d.count)}`}
                title={`${d.date}: ${d.count} new`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            <span>Less</span>
            <span className="w-3 h-3 rounded-sm bg-gray-100"></span>
            <span className="w-3 h-3 rounded-sm bg-emerald-200"></span>
            <span className="w-3 h-3 rounded-sm bg-emerald-400"></span>
            <span className="w-3 h-3 rounded-sm bg-emerald-500"></span>
            <span>More</span>
          </div>
        </div>
      </div>

      {/* Coverage matrix + top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pincode × Keyword coverage heatmap */}
        <div className="dashboard-card lg:col-span-2">
          <div className="dashboard-card-header flex items-center justify-between">
            <span className="font-semibold text-sm text-gray-700">Coverage heatmap</span>
            <span className="text-[10px] text-gray-400">Top {(cov.pincodes || []).length} pincodes × top {(cov.keywords || []).length} keywords</span>
          </div>
          <div className="dashboard-card-body overflow-auto">
            {cov.pincodes?.length && cov.keywords?.length ? (
              <table className="text-[11px] w-full" data-testid="master-coverage-matrix">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2">Pincode</th>
                    {cov.keywords.map(k => (
                      <th key={k} className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2 px-1 max-w-[70px]"><div className="truncate" title={k}>{k}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cov.matrix.map(row => (
                    <tr key={row.pincode}>
                      <td className="font-mono text-gray-700 py-0.5 pr-2 tabular-nums">{row.pincode}</td>
                      {cov.keywords.map(k => {
                        const v = row.cells[k] || 0;
                        return (
                          <td key={k} className="p-0.5 text-center">
                            <div className={`rounded h-6 flex items-center justify-center font-semibold tabular-nums ${covColor(v)}`}>{v || ''}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center text-[12px] text-gray-400 py-8">Coverage matrix builds as you scrape more keywords across pincodes.</div>
            )}
          </div>
        </div>

        {/* Top-N lists */}
        <div className="space-y-4">
          <div className="dashboard-card">
            <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Top pincodes</span></div>
            <div className="dashboard-card-body space-y-1.5">
              {(topPins || []).slice(0, 10).map(p => (
                <div key={p.pincode} className="flex items-center justify-between text-[12px] py-0.5">
                  <span className="font-mono text-gray-700">{p.pincode}</span>
                  <span className="font-semibold text-[#32B4AC] tabular-nums">{p.count}</span>
                </div>
              ))}
              {!topPins.length && <div className="text-[11px] text-gray-400 text-center py-2">No data yet</div>}
            </div>
          </div>
          <div className="dashboard-card">
            <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Top keywords</span></div>
            <div className="dashboard-card-body space-y-1.5">
              {(topKws || []).slice(0, 10).map(k => (
                <div key={k.keyword} className="flex items-center justify-between text-[12px] py-0.5">
                  <span className="text-gray-700 truncate pr-2" title={k.keyword}>{k.keyword}</span>
                  <span className="font-semibold text-[#32B4AC] tabular-nums">{k.count}</span>
                </div>
              ))}
              {!topKws.length && <div className="text-[11px] text-gray-400 text-center py-2">No data yet</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Filters + actions toolbar */}
      <div className="dashboard-card">
        <div className="dashboard-card-body flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">Search</label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Store name, address, phone..." data-testid="master-search" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">Status</label>
            <select value={caFilter} onChange={(e) => setCaFilter(e.target.value)} className="h-9 px-3 text-[12px] border border-gray-200 rounded-md bg-white" data-testid="master-ca-filter">
              <option value="">All</option>
              <option value="CA">CA only</option>
              <option value="CNA">CNA only</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">Keyword</label>
            <Input value={kwFilter} onChange={(e) => setKwFilter(e.target.value)} placeholder="e.g. protein shop" className="w-44" data-testid="master-keyword-filter" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">Pincode</label>
            <Input value={pinFilter} onChange={(e) => setPinFilter(e.target.value)} placeholder="110001" className="w-32" data-testid="master-pincode-filter" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">From</label>
            <Input type="date" value={start} max={today} onChange={(e) => setStart(e.target.value)} className="w-36" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-1">To</label>
            <Input type="date" value={end} max={today} onChange={(e) => setEnd(e.target.value)} className="w-36" />
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:border-gray-300 transition-colors" data-testid="master-template-btn">
              <Download className="w-3.5 h-3.5" /> Sample
            </button>
            <input type="file" ref={fileRef} accept=".csv,.txt" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-[#32B4AC] rounded-md hover:bg-[#2a9d96] transition-colors" data-testid="master-import-btn">
              <Upload className="w-3.5 h-3.5" /> Import CSV
            </button>
            <button onClick={doExport} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors" data-testid="master-export-btn">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Master table */}
      <div className="dashboard-card">
        <div className="dashboard-card-header flex items-center justify-between">
          <span className="font-semibold text-sm text-gray-700">Master database <span className="text-[11px] text-gray-400 font-normal ml-2">{total.toLocaleString('en-IN')} unique stores</span></span>
          <span className="text-[10px] text-gray-400">Page {page} of {totalPages}</span>
        </div>
        <div className="dashboard-card-body p-0">
          <div className="overflow-auto" style={{ maxHeight: 540 }}>
            <table className="w-full text-[12px]" data-testid="master-table">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {["Date Scraped","User","Keyword","Pincode","Store Name","Address","Phone","CA/CNA"].map(h => (
                    <th key={h} className="text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-gray-400 py-8">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-gray-400 py-8">No matching stores. Try clearing filters.</td></tr>
                )}
                {rows.map((r, idx) => (
                  <tr key={`${r.phone || r.store_name}-${idx}`} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-1.5 whitespace-nowrap text-gray-600 tabular-nums">{r.date_scraped ? r.date_scraped.slice(0, 10) : '—'}</td>
                    <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate" title={r.user_name}>{r.user_name || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-700 max-w-[140px] truncate" title={r.keyword}>{r.keyword || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-700">{r.pincode || '—'}</td>
                    <td className="px-3 py-1.5 font-medium text-gray-800 max-w-[220px] truncate" title={r.store_name}>{r.store_name}</td>
                    <td className="px-3 py-1.5 text-gray-600 max-w-[260px] truncate" title={r.address}>{r.address || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-700 whitespace-nowrap">{r.phone || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.ca_status === 'CA' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.ca_status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Next</button>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};


// ─── BUY BOX MODULE ───
const BUYBOX_COLUMNS = [
  { id: 'asin', label: 'Product', default: true },
  { id: 'product_name', label: 'Name', default: true },
  { id: 'price', label: 'Selling Price', default: true },
  { id: 'mrp', label: 'MRP', default: false },
  { id: 'seller_name', label: 'Seller (Buy Box)', default: true },
  { id: 'scraped_at', label: 'Scraped At', default: true },
  { id: 'status', label: 'Status', default: false },
];


// ─── Amazon Market Research module ─────────────────────────────────────────
// Submits one or more Amazon bestseller URLs, polls the job, and renders ASIN /
// product name / flavour live in a compact table with CSV export. The job
// type is `amazon-bestseller` on the backend; results flow through the same
// scrape_jobs collection so the global job queue still shows them.
const MARKET_RESEARCH_COLUMNS = [
  { id: 'rank',         label: 'Rank',         default: true,  width: 60 },
  { id: 'asin',         label: 'ASIN',         default: true,  width: 110 },
  { id: 'product_name', label: 'Product',      default: true },
  { id: 'flavour',      label: 'Flavour',      default: true,  width: 200 },
  { id: 'flavour_source', label: 'Source',     default: false, width: 80 },
  { id: 'product_url',  label: 'URL',          default: false },
  { id: 'source_url',   label: 'List URL',     default: false },
];

// Sub-tab pills shown at the top of the Market Research tab. Lets the user
// flip between the classic bestseller-URL scraper and the new ASIN-list
// "Research Groups" workflow without leaving the Market Research context.
const MarketResearchSubTabs = ({ active, onChange }) => {
  const tabs = [
    { key: 'bestsellers', label: 'Bestseller URLs', sub: 'Top-N scrape per Amazon bestseller list' },
    { key: 'groups',      label: 'Research Groups', sub: 'Custom ASIN collections · cross-category' },
    { key: 'schedules',   label: 'Schedule Runs',   sub: 'Automated recurring scrapes for saved categories' },
  ];
  return (
    <div className="inline-flex bg-gray-100 rounded-xl p-1" data-testid="mr-sub-tabs">
      {tabs.map(t => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              isActive
                ? 'bg-white shadow-sm text-purple-700'
                : 'text-gray-500 hover:text-gray-800'
            }`}
            data-testid={`mr-sub-tab-${t.key}`}
            title={t.sub}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

const MarketResearchModule = ({
  isLoading, setIsLoading,
  results, setResults,
  stats, setStats,
  startTime, setStartTime,
  endTime, setEndTime,
  handleExport, addTerminalLog,
  playCompletedChimeOnce, alertedJobsRef,
  formatDuration,
}) => {
  const [urls, setUrls] = useState('');
  const [maxPerUrl, setMaxPerUrl] = useState(50);
  const [engine, setEngine] = useState(() => {
    try { return localStorage.getItem('mr_engine') || 'apify'; }
    catch { return 'apify'; }
  });
  const [availableAttrs, setAvailableAttrs] = useState([]);
  const [selectedAttrs, setSelectedAttrs] = useState(() => {
    try {
      const raw = localStorage.getItem('mr_selected_attrs');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // "Dosage Form" was retired — strip it out for older saved selections.
          return parsed.filter(k => k !== 'Dosage Form');
        }
      }
    } catch {}
    return ['Item Form'];
  });
  const [activeJobId, setActiveJobId] = useState(null);

  useEffect(() => {
    // Load available attributes (only meaningful for the Apify engine, which
    // captures the full PDP attributes table).
    if (engine !== 'apify') { setAvailableAttrs([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/amazon/bestseller/available-attributes?days=90`);
        const list = (r.data?.attributes || []).filter(a => a !== 'Dosage Form');
        setAvailableAttrs(list);
      } catch { /* swallow — picker just won't populate */ }
    })();
  }, [engine]);

  const toggleAttr = (key) => {
    if (key === 'Dosage Form') return; // Retired
    setSelectedAttrs(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { localStorage.setItem('mr_selected_attrs', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [progressMessage, setProgressMessage] = useState('');
  // Local terminal log feed — mirrors the look used by Delivery Tracker so users
  // get the same "scrape is alive" feedback. Only this module reads it.
  const [terminalLogs, setTerminalLogs] = useState([]);
  const pushLocalLog = useCallback((message, type = 'info') => {
    const time = new Date().toLocaleTimeString();
    setTerminalLogs(prev => [...prev.slice(-99), { time, message, type }]);
  }, []);

  // Progress % derived from rows arrived vs expected total
  const progress = stats && stats.total ? Math.min(100, Math.round((stats.returned / stats.total) * 100)) : 0;

  const handleStart = async () => {
    const urlList = urls.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urlList.length) return toast.error('Paste at least one bestseller URL');
    const bad = urlList.find(u => !/^https?:\/\/(www\.)?amazon\.[a-z.]+\//i.test(u));
    if (bad) return toast.error(`Invalid Amazon URL: ${bad.slice(0, 60)}…`);

    setIsLoading(true);
    setResults([]);
    setStats(null);
    setStartTime(Date.now());
    setEndTime(null);
    setProgressMessage('');
    setTerminalLogs([]);
    pushLocalLog(`Starting Market Research scan — ${urlList.length} URL${urlList.length !== 1 ? 's' : ''}`, 'system');
    addTerminalLog(`[SYSTEM] Starting Market Research scan — ${urlList.length} URL${urlList.length !== 1 ? 's' : ''}`, 'system');

    try {
      const r = await axios.post(`${API}/scrape/bestseller`, {
        urls: urlList,
        max_products_per_url: Math.max(1, Math.min(parseInt(maxPerUrl, 10) || 50, 100)),
        engine,
        selected_attributes: selectedAttrs,
      });
      const jobId = r.data?.job_id;
      setActiveJobId(jobId);
      pushLocalLog(`[JOB] Started ${jobId}`, 'info');
      addTerminalLog(`[JOB] Started ${jobId}`, 'info');

      // Poll every 3s
      let pollFailures = 0;
      let lastSeenRowCount = 0;
      let lastSeenEnrichedCount = 0;
      let lastSeenProgressMsg = '';
      let lastProgressAt = Date.now();
      // 8 min — must be > backend's 6-min Pass-2 hard ceiling so the frontend
      // never cries "stalled" before the server finalises the job.
      const STALL_MS = 8 * 60 * 1000;
      const poll = setInterval(async () => {
        try {
          const jr = await axios.get(`${API}/jobs/${jobId}`, { timeout: 15000 });
          const job = jr.data;
          pollFailures = 0;
          if (job.progress_message && job.progress_message !== progressMessage) {
            setProgressMessage(job.progress_message);
          }
          // Any change in progress_message counts as live progress — Pass-2
          // updates this per-row ("Enriching 12/50 — ok=8 blocked=4") even
          // when row count is unchanged (existing rows being enriched).
          if (job.progress_message && job.progress_message !== lastSeenProgressMsg) {
            lastSeenProgressMsg = job.progress_message;
            lastProgressAt = Date.now();
          }
          const rows = job.results || [];
          setResults(rows);
          // Log incremental rows into the terminal — gives the user real-time
          // feedback that scraping is alive (mirrors Delivery Tracker behaviour).
          if (rows.length > lastSeenRowCount) {
            for (let i = lastSeenRowCount; i < rows.length; i++) {
              const row = rows[i];
              if (row.status === 'success') {
                pushLocalLog(`[OK] ${(row.product_name || row.asin || '').slice(0, 60)}…`, 'success');
              } else if (row.status === 'error') {
                pushLocalLog(`[ERR] ${(row.error || 'failed').slice(0, 60)}`, 'error');
              }
            }
            lastSeenRowCount = rows.length;
            lastProgressAt = Date.now();
          }
          // Pass-2 enrichment progress — brand/flavour filling in on existing
          // rows is real progress even though rows.length is unchanged.
          const enrichedCount = rows.reduce(
            (acc, r) => acc + ((r.brand || r.flavour) ? 1 : 0),
            0,
          );
          if (enrichedCount > lastSeenEnrichedCount) {
            lastSeenEnrichedCount = enrichedCount;
            lastProgressAt = Date.now();
          }
          const successCount = rows.filter(x => x.status === 'success').length;
          const failedCount  = rows.filter(x => x.status === 'error').length;
          setStats({
            total: job.total || rows.length,
            success: successCount,
            failed: failedCount,
            returned: rows.length,
          });
          if (job.status && job.status !== 'processing') {
            clearInterval(poll);
            setIsLoading(false);
            setEndTime(Date.now());
            if (job.status === 'completed') {
              pushLocalLog(`[COMPLETE] ${successCount} products extracted`, 'success');
              addTerminalLog(`[COMPLETE] ${successCount} products extracted`, 'success');
              try { alertedJobsRef?.current?.add(jobId); } catch {}
              try { playCompletedChimeOnce?.(`bestseller:${jobId}`); } catch {}
              toast.success(`Done — ${successCount} products`);
            } else {
              pushLocalLog(`[${job.status.toUpperCase()}] ${job.progress_message || ''}`, 'error');
              addTerminalLog(`[${job.status.toUpperCase()}] ${job.progress_message || ''}`, 'error');
              toast.error(`Job ${job.status}`);
            }
          } else if (Date.now() - lastProgressAt > STALL_MS) {
            // Backend job is still 'processing' but no new rows AND no
            // progress_message change AND no enrichment growth in 8 min —
            // backend's own 6-min Pass-2 hard cap should already have
            // finalised the job by now, so this branch is a true safety net.
            clearInterval(poll);
            setIsLoading(false);
            setEndTime(Date.now());
            pushLocalLog(`[STALLED] No progress for 8 minutes — job continues on server, refresh Job Queue to check later.`, 'error');
            toast.error('No progress for 8 min — check Job Queue for updates');
          }
        } catch {
          pollFailures++;
          if (pollFailures > 30) {
            clearInterval(poll);
            setIsLoading(false);
            toast.error('Connection lost — check Job Queue');
          }
        }
      }, 3000);
    } catch (err) {
      setIsLoading(false);
      toast.error(err?.response?.data?.detail || 'Failed to start scan');
    }
  };

  // Bespoke CSV exporter — only the attribute columns currently selected in
  // the dropdown are exported. Users can re-select attributes any time
  // without re-scraping because storage is universal on the row.
  const exportCsv = () => {
    const oks = results.filter(r => r.status === 'success');
    if (!oks.length) { toast.error('No rows to export'); return; }
    const baseCols = ['rank', 'asin', 'brand', 'product_name', 'flavour', 'flavour_source', 'category_name', 'product_url', 'source_url'];
    const cols = [...baseCols, ...selectedAttrs];
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(',')];
    oks.forEach(r => {
      const row = cols.map(c => {
        if (baseCols.includes(c)) return esc(r[c]);
        return esc((r.attributes || {})[c]);
      });
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `market_research_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${oks.length} rows`);
  };

  const successRows = results.filter(r => r.status === 'success');
  const failedRows = results.filter(r => r.status === 'error');
  const [mrFilter, setMrFilter] = useState('all'); // all / success / failed
  const visibleRows = mrFilter === 'success' ? successRows
    : mrFilter === 'failed' ? failedRows
    : results;

  // Sub-tab within Market Research: the classic Bestseller-URL scraper vs the
  // new ASIN Research Groups (custom cross-category bundles). Tab choice is
  // remembered across navigation via localStorage so the user lands back on
  // whichever workflow they were last using.
  const [mrSubTab, setMrSubTab] = useState(() => {
    try { return localStorage.getItem('mr_sub_tab') || 'bestsellers'; } catch { return 'bestsellers'; }
  });
  useEffect(() => {
    try { localStorage.setItem('mr_sub_tab', mrSubTab); } catch {}
  }, [mrSubTab]);

  if (mrSubTab === 'groups') {
    return (
      <div className="space-y-5" data-testid="marketresearch-root">
        <MarketResearchSubTabs active={mrSubTab} onChange={setMrSubTab} />
        <ResearchGroupsModule />
      </div>
    );
  }

  if (mrSubTab === 'schedules') {
    return (
      <div className="space-y-5" data-testid="marketresearch-root">
        <MarketResearchSubTabs active={mrSubTab} onChange={setMrSubTab} />
        <BestSellerSchedulesPanel />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="marketresearch-root">
      <MarketResearchSubTabs active={mrSubTab} onChange={setMrSubTab} />
      {/* Header band */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-xl px-5 py-4" data-testid="marketresearch-header">
        <div className="w-10 h-10 rounded-xl bg-white border border-purple-300 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-purple-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">Market Research</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Pull ASIN, brand, product name &amp; flavour from any Amazon bestseller list — no pincode needed.</p>
        </div>
      </div>

      {/* 2-column body — matches Delivery Tracker exactly */}
      <div className="grid grid-cols-12 gap-5 items-start">
        {/* ─── LEFT COLUMN: input panel ─── */}
        <div className="col-span-12 lg:col-span-4 space-y-4 sticky top-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="text-base font-semibold text-gray-900 mb-4">Input</div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Bestseller URLs</label>
                  <span className="text-[10px] text-gray-400">{urls.split('\n').filter(s => s.trim()).length} URL(s)</span>
                </div>
                <textarea
                  value={urls}
                  onChange={(e) => setUrls(e.target.value)}
                  placeholder={'https://www.amazon.in/gp/bestsellers/hpc/11364620031/\nhttps://www.amazon.in/...'}
                  rows={6}
                  className="w-full text-xs font-mono px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder:text-gray-300"
                  data-testid="marketresearch-urls-input"
                />
                <div className="text-[10px] text-gray-400 mt-1">One URL per line · runs in parallel</div>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1 block">Top N per URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={maxPerUrl}
                    onChange={(e) => setMaxPerUrl(Math.max(1, Math.min(parseInt(e.target.value || '0', 10) || 50, 100)))}
                    className="h-9 w-24 px-2 text-xs text-center border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-300"
                    data-testid="marketresearch-max-input"
                  />
                  <span className="text-[10px] text-gray-400">products (max 100)</span>
                </div>
                <div className="text-[10px] text-gray-400 mt-1">{maxPerUrl > 30 ? 'Fetches page 1 + page 2 to cover 31-50' : 'Page 1 covers up to 30 products'}</div>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1 block">Engine</label>
                <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs font-medium" data-testid="marketresearch-engine-toggle">
                  <button
                    type="button"
                    onClick={() => { setEngine('apify'); try { localStorage.setItem('mr_engine', 'apify'); } catch {} }}
                    className={`px-3 py-1.5 transition-colors ${engine === 'apify' ? 'bg-purple-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    data-testid="marketresearch-engine-apify"
                  >
                    Reliable · Apify
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEngine('playwright'); try { localStorage.setItem('mr_engine', 'playwright'); } catch {} }}
                    className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${engine === 'playwright' ? 'bg-purple-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    data-testid="marketresearch-engine-playwright"
                  >
                    Fast · Playwright
                  </button>
                </div>
                <div className="text-[10px] text-gray-400 mt-1">
                  {engine === 'apify'
                    ? 'Apify bypasses proxy bot-blocks — most reliable for brand & flavour'
                    : 'In-house scraper — fastest in preview, can hit bot-blocks on prod'}
                </div>
              </div>

              {engine === 'apify' && (
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1 block">
                    Columns to show & analyse <span className="text-gray-400 normal-case font-normal">· also added to CSV ({selectedAttrs.length} selected)</span>
                  </label>
                  <AttributeDropdown
                    available={availableAttrs}
                    selected={selectedAttrs}
                    onToggle={toggleAttr}
                    testId="mr-attr-dropdown"
                    placeholder="Pick attributes…"
                  />
                  <div className="text-[10px] text-gray-400 mt-1">
                    These attributes appear as extra columns in the results table, in the CSV export, and as analytics tiles. Brand & Flavour are always shown.
                  </div>
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={handleStart}
            disabled={isLoading}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white py-4 text-base font-semibold"
            data-testid="marketresearch-start-btn"
          >
            {isLoading
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scraping…</>
              : <><Play className="w-5 h-5 mr-2" /> Start Extraction</>}
          </Button>
        </div>

        {/* ─── RIGHT COLUMN: ticker + terminal + stats + results ─── */}
        <div className="col-span-12 lg:col-span-8 space-y-5 min-w-0">
          {/* Activity ticker */}
          <ActivityTicker logs={terminalLogs} isLoading={isLoading} />

          {/* Terminal log feed */}
          {(isLoading || terminalLogs.length > 0) && (
            <div className="progress-terminal" data-testid="marketresearch-terminal">
              <div className="progress-terminal-header">
                <div className="terminal-dot red" />
                <div className="terminal-dot yellow" />
                <div className="terminal-dot green" />
                <span className="text-gray-500 text-xs ml-2">horizon-extractor</span>
                {endTime && startTime && (
                  <span className="ml-auto text-[#32B4AC] text-xs font-mono">
                    <Timer className="w-3 h-3 inline mr-1" />{formatDuration ? formatDuration(endTime - startTime) : `${Math.max(1, Math.round((endTime - startTime) / 1000))}s`}
                  </span>
                )}
              </div>
              {isLoading && (
                <div className="mb-4">
                  <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 transition-all duration-300 progress-futuristic animate-data-flow" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">{Math.round(progress)}%</div>
                </div>
              )}
              <div className="space-y-1 max-h-[260px] overflow-y-auto">
                {terminalLogs.map((log, i) => (
                  <div key={i} className="terminal-line">
                    <span className="text-gray-600">{log.time}</span>
                    <span className={log.type === 'success' ? 'success' : log.type === 'error' ? 'error' : log.type === 'system' ? 'text-blue-400' : 'text-gray-400'}>
                      {log.message}
                    </span>
                  </div>
                ))}
                {isLoading && <div className="terminal-line"><span className="processing">█</span></div>}
              </div>
            </div>
          )}

          {/* Stats bar */}
          {stats && (
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-5 py-3 border border-slate-100">
              <div className="flex items-center gap-5 flex-1 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                    <Database className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums leading-none">{stats.total}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">Expected</div>
                  </div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-emerald-600 tabular-nums leading-none">{stats.success}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">Success</div>
                  </div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center">
                    <XCircle className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-red-500 tabular-nums leading-none">{stats.failed || 0}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">Failed</div>
                  </div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#E8F8F5] border border-[#32B4AC]/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#32B4AC]" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-[#32B4AC] tabular-nums leading-none">{endTime && startTime ? (formatDuration ? formatDuration(endTime - startTime) : `${Math.max(1, Math.round((endTime - startTime) / 1000))}s`) : '-'}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">Duration</div>
                  </div>
                </div>
              </div>
              {results.length > 0 && (
                <Button onClick={exportCsv} className="btn-accent text-xs gap-1.5 shrink-0" data-testid="marketresearch-export-btn">
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </Button>
              )}
            </div>
          )}

          {/* Resilience banner */}
          {results.length > 0 && (
            <div className="bg-blue-50/60 border border-blue-200/50 rounded-lg px-4 py-2 flex items-center gap-2.5">
              <Wifi className="w-3.5 h-3.5 text-blue-400 shrink-0" />
              <div className="text-[11px] text-blue-700">
                <span className="font-semibold">Connection drops?</span> Your job keeps running on the server and results are saved in real-time. Just refresh the page and check Job Queue — nothing is lost.
              </div>
            </div>
          )}

          {/* Results table */}
          {!results.length && !isLoading ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center" data-testid="marketresearch-empty">
              <TrendingUp className="w-10 h-10 text-purple-300 mx-auto mb-3" />
              <div className="text-sm font-semibold text-gray-700">Paste a bestseller URL to start</div>
              <div className="text-[11px] text-gray-400 mt-1">Example: amazon.in/gp/bestsellers/hpc/…</div>
            </div>
          ) : (!results.length && isLoading) ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 flex flex-col items-center justify-center gap-4" data-testid="marketresearch-loading">
              <HorizonPreloader size={72} />
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#32B4AC] font-semibold">Processing</div>
              <div className="text-[10px] text-gray-400">Spinning up Chromium · routing through residential proxy · fetching bestseller cards…</div>
            </div>
          ) : (
            <div className="dashboard-card" data-testid="results-card">
              <div className="dashboard-card-header flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-sm text-gray-700">Results</span>
                {results.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1" data-testid="mr-status-filter">
                      {[
                        { id: 'all', label: 'All', count: results.length },
                        { id: 'success', label: 'Success', count: successRows.length },
                        { id: 'failed', label: 'Failed', count: failedRows.length },
                      ].map(s => (
                        <button
                          key={s.id}
                          onClick={() => setMrFilter(s.id)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${mrFilter === s.id ? 'bg-[#E8F8F5] text-[#32B4AC] border border-[#32B4AC]/30' : 'text-gray-500 hover:text-gray-700 border border-gray-200'}`}
                          data-testid={`mr-filter-${s.id}`}
                        >
                          {s.label} ({s.count})
                        </button>
                      ))}
                    </div>
                    <span className="text-[11px] text-gray-400 ml-1">{visibleRows.length} records</span>
                  </div>
                )}
              </div>
              <div className="overflow-auto max-h-[640px]">
                <table className="w-full text-xs" data-testid="marketresearch-table">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 w-12">#</th>
                      <th className="text-left px-3 py-2 w-24">ASIN</th>
                      <th className="text-left px-3 py-2 w-36">Brand</th>
                      <th className="text-left px-3 py-2">Product Name</th>
                      <th className="text-left px-3 py-2 w-40">Flavour</th>
                      {selectedAttrs.map(a => (
                        <th key={a} className="text-left px-3 py-2 w-32" data-testid={`marketresearch-th-${a}`}>{a}</th>
                      ))}
                      <th className="text-left px-3 py-2 w-16">Src</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleRows.map((r, i) => (
                      <tr
                        key={`${r.asin || 'na'}-${i}`}
                        className={r.status === 'error' ? 'bg-red-50/40' : ''}
                        data-testid={`marketresearch-row-${i}`}
                      >
                        <td className="px-3 py-2 text-gray-500 font-mono">{r.rank || (i + 1)}</td>
                        <td className="px-3 py-2 font-mono text-gray-700">
                          {r.product_url ? (
                            <a href={r.product_url} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline">{r.asin || '—'}</a>
                          ) : (r.asin || '—')}
                        </td>
                        <td className="px-3 py-2 text-gray-800 truncate">
                          {r.brand
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11px]">{r.brand}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          <div className="line-clamp-2">{r.product_name || (r.error || '—')}</div>
                        </td>
                        <td className="px-3 py-2">
                          {r.flavour
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-50 border border-purple-100 text-purple-700 text-[11px]">{r.flavour}</span>
                            : r.status === 'error'
                              ? <span className="text-red-500 text-[10px]">{(r.error || '').slice(0, 40)}</span>
                              : <span className="text-gray-300">—</span>}
                        </td>
                        {selectedAttrs.map(a => {
                          const v = (r.attributes || {})[a];
                          return (
                            <td key={a} className="px-3 py-2 text-[11px] text-gray-700" data-testid={`marketresearch-cell-${a}-${i}`}>
                              {v ? v : <span className="text-gray-300">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-[10px] text-gray-400 uppercase">{r.flavour_source || ''}</td>
                      </tr>
                    ))}
                    {isLoading && (
                      <tr><td colSpan={6 + selectedAttrs.length} className="px-3 py-3 text-center text-[11px] text-gray-400">
                        <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Polling…
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const BuyBoxModule = ({ buyboxPincode, setBuyboxPincode, isLoading, setIsLoading, addTerminalLog, formatDuration, platformId, loadedRun, clearLoadedRun }) => {
  const [asins, setAsins] = useState('');
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [logs, setLogs] = useState([]);

  // Scheduling state
  const [schedules, setSchedules] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    frequency: 'daily',
    time_of_day: '09:00',
    days: [0, 1, 2, 3, 4], // Mon-Fri default (0=Mon..6=Sun)
  });

  const loadSchedules = async () => {
    try {
      const r = await axios.get(`${API}/buybox/schedules`);
      setSchedules(r.data || []);
    } catch (e) {
      // Silent fail; user may not be authed yet
    }
  };

  useEffect(() => { loadSchedules(); }, []);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev.slice(-15), { message, type, time: new Date().toLocaleTimeString() }]);
  };

  // Load run from Job Queue
  useEffect(() => {
    if (loadedRun) {
      setResults(loadedRun.results || []);
      const successCount = (loadedRun.results || []).filter(r => r.status === 'success').length;
      setStats({ total: loadedRun.total || loadedRun.results?.length || 0, success: successCount, failed: (loadedRun.results || []).filter(r => r.status === 'error').length });
      if (loadedRun.created_at) {
        setStartTime(new Date(loadedRun.created_at).getTime());
        setEndTime(loadedRun.completed_at ? new Date(loadedRun.completed_at).getTime() : Date.now());
      }
      addLog(`[LOADED] Previous Buy Box run — ${successCount} results`, 'success');
      clearLoadedRun();
    }
  }, [loadedRun]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuyboxProductsPicked = (identifiers) => {
    if (!identifiers.length) return;
    const existing = asins.split('\n').map(s => s.trim()).filter(Boolean);
    const merged = [...new Set([...existing, ...identifiers])];
    setAsins(merged.join('\n'));
    toast.success(`Added ${identifiers.length} ASIN${identifiers.length !== 1 ? 's' : ''}`);
  };

  const handleBuyBoxScrape = async () => {
    const asinList = asins.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (!asinList.length) return toast.error("Select or enter at least one ASIN");
    if (!buyboxPincode.trim()) return toast.error("Enter a pincode");

    setIsLoading(true);
    setResults([]);
    setStats(null);
    setStartTime(Date.now());
    setEndTime(null);
    setLogs([]);
    addLog('[SYSTEM] Starting Buy Box scan...', 'system');

    try {
      const response = await axios.post(`${API}/scrape`, {
        platform: "amazon-buybox",
        asins: asinList,
        pincodes: [buyboxPincode.trim()],
      });
      const jobId = response.data.job_id;
      addLog(`[JOB] Started job ${jobId} — ${asinList.length} ASINs`, 'info');

      // Poll
      let pollFailures = 0;
      const pollInterval = setInterval(async () => {
        try {
          const jr = await axios.get(`${API}/jobs/${jobId}`, { timeout: 15000 });
          const job = jr.data;
          pollFailures = 0;
          if (job.progress_message) addLog(`[PROGRESS] ${job.progress_message}`, 'info');
          if (job.status === 'completed' || job.status === 'failed' || job.status === 'stopped') {
            clearInterval(pollInterval);
            setResults(job.results || []);
            const successCount = (job.results || []).filter(r => r.status === 'success').length;
            setStats({ total: job.total || job.results?.length || 0, success: successCount, failed: (job.results || []).filter(r => r.status === 'error').length });
            setEndTime(Date.now());
            setIsLoading(false);
            if (job.status === 'completed') {
              try { alertedJobsRef.current.add(job.job_id); } catch {}
              playCompletedChimeOnce(`buybox-poller:${job.job_id}`);
              toast.success(`Buy Box scan done — ${successCount} results`);
            } else {
              toast.error(`Buy Box scan ${job.status}`);
            }
          }
        } catch {
          pollFailures++;
          if (pollFailures > 30) { clearInterval(pollInterval); setIsLoading(false); toast.error("Connection lost — check Job Queue"); }
        }
      }, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start Buy Box scan");
      setIsLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    const asinList = asins.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (!asinList.length) return toast.error("Add at least one ASIN before scheduling");
    if (!buyboxPincode.trim()) return toast.error("Enter a pincode");
    if (scheduleForm.frequency === 'weekly' && !scheduleForm.days.length) {
      return toast.error("Select at least one day for a weekly schedule");
    }
    try {
      const payload = {
        name: scheduleForm.name?.trim() || `Buy Box — ${asinList.length} ASIN${asinList.length !== 1 ? 's' : ''}`,
        asins: asinList,
        pincode: buyboxPincode.trim(),
        frequency: scheduleForm.frequency,
        time_of_day: scheduleForm.time_of_day,
        days: scheduleForm.frequency === 'weekly' ? scheduleForm.days : [],
      };
      await axios.post(`${API}/buybox/schedules`, payload);
      toast.success("Schedule created");
      setScheduleModalOpen(false);
      setScheduleForm({ name: '', frequency: 'daily', time_of_day: '09:00', days: [1, 2, 3, 4, 5] });
      loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create schedule");
    }
  };

  const handleToggleSchedule = async (sch) => {
    try {
      await axios.put(`${API}/buybox/schedules/${sch.schedule_id}`, { active: !sch.active });
      loadSchedules();
    } catch {
      toast.error("Failed to update schedule");
    }
  };

  // Two-click delete pattern (window.confirm() is blocked inside Emergent's iframe)
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const pendingDeleteTimer = useRef(null);

  const handleDeleteSchedule = async (id) => {
    if (pendingDeleteId !== id) {
      setPendingDeleteId(id);
      if (pendingDeleteTimer.current) clearTimeout(pendingDeleteTimer.current);
      pendingDeleteTimer.current = setTimeout(() => setPendingDeleteId(null), 3500);
      toast.message("Click delete again to confirm", { duration: 3000 });
      return;
    }
    if (pendingDeleteTimer.current) clearTimeout(pendingDeleteTimer.current);
    setPendingDeleteId(null);
    try {
      await axios.delete(`${API}/buybox/schedules/${id}`);
      toast.success("Schedule deleted");
      loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete schedule");
    }
  };

  const formatNextRun = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
    } catch { return '—'; }
  };

  const describeSchedule = (sch) => {
    if (sch.frequency === 'hourly') return 'Every hour';
    const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    if (sch.frequency === 'daily') return `Daily at ${sch.time_of_day} IST`;
    if (sch.frequency === 'weekly') {
      const ds = (sch.days || []).map(d => dayNames[d]).join(', ');
      return `${ds} at ${sch.time_of_day} IST`;
    }
    return sch.frequency;
  };

  // Export results as CSV
  const handleExportCsv = () => {
    if (!results.length) return;
    const headers = ['ASIN', 'Product Name', 'Selling Price', 'MRP', 'Seller (Buy Box)', 'Status', 'Scraped At'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = results.map(r => [
      r.asin || '',
      r.product_name || '',
      r.price || '',
      r.mrp || '',
      r.seller_name || '',
      r.status || '',
      r.scraped_at || '',
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `buybox_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${results.length} row${results.length !== 1 ? 's' : ''}`);
  };

  // Schedule Runs modal
  const [runsModal, setRunsModal] = useState({ open: false, schedule: null, runs: [], loading: false });

  const openRunsModal = async (sch) => {
    setRunsModal({ open: true, schedule: sch, runs: [], loading: true });
    try {
      const r = await axios.get(`${API}/buybox/schedules/${sch.schedule_id}/runs`);
      setRunsModal({ open: true, schedule: r.data.schedule || sch, runs: r.data.runs || [], loading: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load runs");
      setRunsModal({ open: false, schedule: null, runs: [], loading: false });
    }
  };

  const handleLoadRunIntoTable = async (jobId) => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}`);
      const job = r.data;
      setResults(job.results || []);
      const successCount = (job.results || []).filter(x => x.status === 'success').length;
      setStats({ total: job.total || job.results?.length || 0, success: successCount, failed: (job.results || []).filter(x => x.status === 'error').length });
      if (job.created_at) {
        setStartTime(new Date(job.created_at).getTime());
        setEndTime(job.completed_at ? new Date(job.completed_at).getTime() : Date.now());
      }
      addLog(`[LOADED] Scheduled run — ${successCount} results`, 'success');
      setRunsModal({ open: false, schedule: null, runs: [], loading: false });
      toast.success("Run loaded into table");
    } catch (err) {
      toast.error("Failed to load run");
    }
  };

  return (
    <div className="space-y-5">
      {/* Buy Box Header */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 rounded-xl px-5 py-4">
        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
          <ShoppingCart className="w-5 h-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">Buy Box Monitor</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Track which seller wins the Buy Box for your products</p>
        </div>
        <button
          onClick={() => setScheduleModalOpen(true)}
          className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md border transition-all ${
            schedules.filter(s => s.active).length > 0
              ? 'text-white bg-gradient-to-r from-orange-500 to-amber-500 border-orange-500 shadow-sm shadow-orange-200 hover:shadow-md hover:from-orange-600 hover:to-amber-600'
              : 'text-orange-700 bg-white border-orange-200 hover:bg-orange-50 hover:border-orange-300'
          }`}
          data-testid="buybox-schedule-btn"
        >
          <CalendarClock className={`w-3.5 h-3.5 ${schedules.filter(s => s.active).length > 0 ? 'animate-pulse' : ''}`} />
          Schedule
          {schedules.filter(s => s.active).length > 0 && (
            <>
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-white text-orange-600 border border-white/50 tabular-nums" data-testid="buybox-schedule-count-badge">
                {schedules.filter(s => s.active).length}
              </span>
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-white">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              </span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input */}
        <div className="lg:col-span-4 space-y-4">
          <div className="dashboard-card">
            <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Buy Box Input</span></div>
            <div className="dashboard-card-body space-y-4">
              {/* ASIN Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">ASINs</label>
                  <button onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded-md hover:bg-[#E0F7F4] hover:border-[#32B4AC]/50 transition-colors font-medium" data-testid="buybox-pick-products-btn">
                    <ListChecks className="w-3 h-3" /> Products
                  </button>
                </div>
                <Textarea
                  value={asins}
                  onChange={(e) => setAsins(e.target.value)}
                  placeholder={"B08N5WRWNW\nB09V3KXJPB"}
                  className="input-clean font-mono text-xs min-h-[100px] resize-none placeholder:text-gray-300"
                  data-testid="buybox-asin-input"
                />
              </div>
              {/* Pincode */}
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Pincode</label>
                <input
                  value={buyboxPincode}
                  onChange={(e) => setBuyboxPincode(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] font-mono"
                  placeholder="122002"
                  data-testid="buybox-pincode-input"
                />
                <span className="text-[10px] text-gray-400 mt-1 block">Single pincode for Buy Box check</span>
              </div>
              {/* Start Button */}
              <Button
                onClick={handleBuyBoxScrape}
                disabled={isLoading}
                className="w-full btn-accent py-2.5 text-sm font-semibold gap-2"
                data-testid="buybox-start-btn"
              >
                {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</> : <><ShoppingCart className="w-4 h-4" /> Scan Buy Box</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-8 space-y-4">
          {/* Live Activity Ticker */}
          <ActivityTicker logs={logs} isLoading={isLoading} />
          {/* Progress Terminal */}
          {(isLoading || logs.length > 0) && (
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isLoading ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
                  <span className="text-[11px] text-gray-400 font-mono">Buy Box Scanner</span>
                </div>
                {!isLoading && logs.length > 0 && (
                  <button onClick={() => setLogs([])} className="text-[10px] text-gray-500 hover:text-gray-300">Clear</button>
                )}
              </div>
              <div className="max-h-[160px] overflow-y-auto p-3 space-y-1 font-mono text-[11px]">
                {logs.map((log, i) => (
                  <div key={i} className={`flex items-start gap-2 ${
                    log.type === 'system' ? 'text-blue-400' : log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    <span className="text-gray-600 shrink-0">{log.time}</span>
                    <span>{log.message}</span>
                  </div>
                ))}
                {isLoading && <div className="text-green-400 animate-pulse">█</div>}
              </div>
            </div>
          )}
          {stats && (
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-5 py-3 border border-slate-100">
              <div className="flex items-center gap-5 flex-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Database className="w-4 h-4 text-slate-500" /></div>
                  <div><div className="text-lg font-bold text-slate-900 tabular-nums leading-none">{stats.total}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider">Total</div></div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-500" /></div>
                  <div><div className="text-lg font-bold text-emerald-600 tabular-nums leading-none">{stats.success}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider">Success</div></div>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#E8F8F5] border border-[#32B4AC]/20 flex items-center justify-center"><Clock className="w-4 h-4 text-[#32B4AC]" /></div>
                  <div><div className="text-lg font-bold text-[#32B4AC] tabular-nums leading-none">{endTime && startTime ? formatDuration(endTime - startTime) : '-'}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider">Duration</div></div>
                </div>
              </div>
              <button
                onClick={handleExportCsv}
                disabled={!results.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="buybox-export-csv-btn"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
          )}
          {results.length > 0 ? (
            <div className="results-table-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    {BUYBOX_COLUMNS.filter(c => c.default).map(col => (
                      <TableHead key={col.id}>{col.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r, i) => (
                    <TableRow key={i} data-testid={`buybox-row-${i}`} className="align-top">
                      <TableCell className="py-3">
                        <a href={`https://www.amazon.in/dp/${r.asin}`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono text-[#32B4AC] hover:underline whitespace-nowrap">{r.asin}</a>
                      </TableCell>
                      <TableCell className="text-xs font-medium text-gray-800 py-3 min-w-[320px] max-w-[480px]">
                        <div className="leading-snug whitespace-normal break-words" title={r.product_name || ''}>{r.product_name || '-'}</div>
                      </TableCell>
                      <TableCell className="text-xs font-semibold tabular-nums py-3 whitespace-nowrap">{r.price || '-'}</TableCell>
                      <TableCell className="py-3">
                        <span className={`text-xs font-semibold ${r.seller_name ? 'text-orange-600' : 'text-gray-400'}`}>
                          {r.seller_name || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-[10px] text-gray-400 tabular-nums whitespace-nowrap py-3">{r.scraped_at || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : !isLoading && (
            <div className="dashboard-card">
              <div className="p-12 text-center">
                <ShoppingCart className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Select ASINs and scan to see Buy Box winners</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Schedules List */}
      {schedules.length > 0 && (
        <div className="dashboard-card" data-testid="buybox-schedules-card">
          <div className="dashboard-card-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-orange-500" />
              <span className="font-semibold text-sm text-gray-700">Scheduled Buy Box Runs</span>
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{schedules.length}</span>
            </div>
          </div>
          <div className="dashboard-card-body p-0">
            <div className="divide-y divide-gray-100">
              {schedules.map(sch => (
                <div key={sch.schedule_id} className="flex items-center gap-4 px-5 py-3" data-testid={`buybox-schedule-row-${sch.schedule_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${sch.active ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                      <span className="text-sm font-semibold text-gray-800 truncate">{sch.name}</span>
                      <span className="text-[10px] text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">{sch.frequency}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span><Repeat className="w-3 h-3 inline mr-1" />{describeSchedule(sch)}</span>
                      <span>•</span>
                      <span>{sch.asins?.length || 0} ASIN{sch.asins?.length !== 1 ? 's' : ''}</span>
                      <span>•</span>
                      <span className="font-mono">{sch.pincode}</span>
                      <span>•</span>
                      <span>Next: <span className="font-medium text-gray-700">{formatNextRun(sch.next_run)}</span></span>
                    </div>
                  </div>
                  <button
                    onClick={() => openRunsModal(sch)}
                    className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 transition-colors"
                    title="View past runs"
                    data-testid={`buybox-schedule-runs-${sch.schedule_id}`}
                  >
                    <History className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleToggleSchedule(sch)}
                    className={`p-1.5 rounded-md border transition-colors ${sch.active ? 'text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-gray-400 border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
                    title={sch.active ? 'Pause schedule' : 'Resume schedule'}
                    data-testid={`buybox-schedule-toggle-${sch.schedule_id}`}
                  >
                    <Power className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteSchedule(sch.schedule_id)}
                    className={`p-1.5 rounded-md border transition-colors ${
                      pendingDeleteId === sch.schedule_id
                        ? 'border-red-400 bg-red-100 text-red-600 ring-2 ring-red-200 animate-pulse'
                        : 'border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50'
                    }`}
                    title={pendingDeleteId === sch.schedule_id ? 'Click again within 3s to confirm' : 'Delete schedule'}
                    data-testid={`buybox-schedule-delete-${sch.schedule_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Buy Box Product Picker */}
      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        platformId="amazon"
        onSelect={handleBuyboxProductsPicked}
      />

      {/* Schedule Runs Modal */}
      <Dialog open={runsModal.open} onOpenChange={(o) => !o && setRunsModal({ open: false, schedule: null, runs: [], loading: false })}>
        <DialogContent className="sm:max-w-[760px]" data-testid="buybox-runs-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-orange-500" />
              Run History — <span className="text-orange-600">{runsModal.schedule?.name || ''}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {runsModal.loading ? (
              <div className="py-12 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading runs...
              </div>
            ) : runsModal.runs.length === 0 ? (
              <div className="py-12 text-center" data-testid="buybox-runs-empty">
                <CalendarClock className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No runs yet</p>
                <p className="text-[11px] text-gray-400 mt-1">This schedule hasn't been triggered yet. Next run: <span className="font-medium text-gray-600">{formatNextRun(runsModal.schedule?.next_run)}</span></p>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 overflow-hidden max-h-[460px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr className="text-left text-gray-500">
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Started</th>
                      <th className="px-3 py-2 font-medium">Results</th>
                      <th className="px-3 py-2 font-medium">Progress / Message</th>
                      <th className="px-3 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runsModal.runs.map((run, i) => {
                      const statusBadge = run.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : run.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                        : run.status === 'processing' ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse'
                        : 'bg-gray-50 text-gray-700 border-gray-200';
                      const startedAt = run.created_at ? new Date(run.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—';
                      return (
                        <tr key={run.job_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`buybox-run-row-${i}`}>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${statusBadge}`}>{run.status}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-700 tabular-nums whitespace-nowrap">{startedAt}</td>
                          <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                            <span className="text-emerald-700 font-semibold">{run.success_count || 0}</span>
                            <span className="text-gray-400"> / {run.result_count || run.total || 0}</span>
                            {run.error_count > 0 && (
                              <span className="ml-2 text-red-600 font-semibold">{run.error_count} err</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-[280px] truncate" title={run.progress_message || ''}>{run.progress_message || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleLoadRunIntoTable(run.job_id)}
                              disabled={!run.result_count}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`buybox-run-load-${i}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setRunsModal({ open: false, schedule: null, runs: [], loading: false })}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              data-testid="buybox-runs-close-btn"
            >Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Creation Modal */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="buybox-schedule-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="w-4 h-4 text-orange-500" /> Schedule Buy Box Run
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Will run for <span className="font-semibold text-gray-800">{asins.split(/[\n,]/).map(s=>s.trim()).filter(Boolean).length}</span> ASIN{asins.split(/[\n,]/).map(s=>s.trim()).filter(Boolean).length !== 1 ? 's' : ''} · Pincode <span className="font-mono font-semibold text-gray-800">{buyboxPincode || '—'}</span>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Name (optional)</label>
              <input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                placeholder="e.g. Daily Buy Box check"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC]"
                data-testid="buybox-schedule-name-input"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Frequency</label>
              <div className="grid grid-cols-3 gap-2">
                {['hourly', 'daily', 'weekly'].map(freq => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setScheduleForm({ ...scheduleForm, frequency: freq })}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize ${
                      scheduleForm.frequency === freq
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                    }`}
                    data-testid={`buybox-schedule-freq-${freq}`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
            </div>

            {scheduleForm.frequency !== 'hourly' && (
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Time of day (IST)</label>
                <input
                  type="time"
                  value={scheduleForm.time_of_day}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, time_of_day: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] font-mono"
                  data-testid="buybox-schedule-time-input"
                />
              </div>
            )}

            {scheduleForm.frequency === 'weekly' && (
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Days of week</label>
                <div className="flex gap-1.5 flex-wrap">
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, idx) => {
                    const active = scheduleForm.days.includes(idx);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const days = active ? scheduleForm.days.filter(x => x !== idx) : [...scheduleForm.days, idx].sort();
                          setScheduleForm({ ...scheduleForm, days });
                        }}
                        className={`w-11 h-9 text-xs font-semibold rounded-lg border transition-colors ${
                          active ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                        }`}
                        data-testid={`buybox-schedule-day-${idx}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {scheduleForm.frequency === 'hourly' && (
              <div className="text-[11px] text-gray-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                Will run at the top of every hour, starting next hour.
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setScheduleModalOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              data-testid="buybox-schedule-cancel-btn"
            >Cancel</button>
            <Button
              onClick={handleSaveSchedule}
              className="btn-accent text-sm font-semibold gap-2"
              data-testid="buybox-schedule-save-btn"
            >
              <Save className="w-4 h-4" /> Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const platformId = params.platformId || location.state?.platformId;
  const [platform, setPlatform] = useState(null);
  const isOffline = platformId === 'offline' || platform?.platform_id === 'offline';
  const isFlipkart = platformId === 'flipkart' || platform?.platform_id === 'flipkart';
  const isBlinkit = platformId === 'blinkit' || platform?.platform_id === 'blinkit';
  const asinLabel = isFlipkart ? 'FSN' : 'ASIN';
  
  const [asins, setAsins] = useState("");
  const [pincodes, setPincodes] = useState("");
  const [keyword, setKeyword] = useState("");
  const [offlineEngine, setOfflineEngine] = useState("fast"); // "fast" (default) | "legacy"
  const [offlineTab, setOfflineTab] = useState("scraper"); // 'scraper' | 'analytics'
  // Default ON — Google Maps hides phone numbers on ~75-90% of feed cards, so
  // without the detail-page enrichment pass most rows come back phone-less.
  // Phone is a required output column, so we opt everyone in by default and
  // let power users turn it off if they only need names/addresses fast.
  const [offlineEnrichAddress, setOfflineEnrichAddress] = useState(true);
  const [offlineProxyRotate, setOfflineProxyRotate] = useState(true); // Fast offline: rotate proxy session per pincode (default on; mitigates Maps soft-bans on shared IPs)
  const [offlineBatchMode, setOfflineBatchMode] = useState(false); // Pan-India batch mode: split pincodes into chunks and queue serially
  const [offlineBatchSize, setOfflineBatchSize] = useState(100);   // Pincodes per batch (1..2000)
  // Amazon Delivery Tracker engine: legacy (default, full PDP+DOM) or fast (parallel HTML fetch)
  const [amazonEngine, setAmazonEngine] = useState("legacy");
  const [results, setResults] = useState([]);
  const [blinkitProducts, setBlinkitProducts] = useState([]);
  const [amazonTab, setAmazonTab] = useState('delivery'); // 'delivery' or 'buybox'
  const [buyboxPincode, setBuyboxPincode] = useState('122002');
  const [buyboxLoadedRun, setBuyboxLoadedRun] = useState(null);
  // Delivery Tracker scheduling (Amazon only)
  const [delivSchedules, setDelivSchedules] = useState([]);
  const [delivScheduleModalOpen, setDelivScheduleModalOpen] = useState(false);
  const [delivScheduleForm, setDelivScheduleForm] = useState({
    name: '', frequency: 'daily', time_of_day: '09:00', days: [0,1,2,3,4], engine: 'legacy'
  });
  const [delivPendingDeleteId, setDelivPendingDeleteId] = useState(null);
  const delivPendingDeleteTimer = useRef(null);
  const [delivRunsModal, setDelivRunsModal] = useState({ open: false, schedule: null, runs: [], loading: false });
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [terminalLogs, setTerminalLogs] = useState([]);
  const activeColumns = isOffline ? OFFLINE_COLUMNS : isBlinkit ? BLINKIT_COLUMNS : AVAILABLE_COLUMNS;
  const [selectedColumns, setSelectedColumns] = useState(
    (isBlinkit ? BLINKIT_COLUMNS : AVAILABLE_COLUMNS).filter(c => c.default).map(c => c.id)
  );

  // Reset columns when platform changes
  useEffect(() => {
    setSelectedColumns(activeColumns.filter(c => c.default).map(c => c.id));
  }, [platformId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [columnFilters, setColumnFilters] = useState({});
  const [filterSearch, setFilterSearch] = useState('');
  const [greeting] = useState(() => getGreeting(user?.name));
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSoundId, setActiveSoundId] = useState(() => getActiveSoundId());
  const [allUserJobs, setAllUserJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsViewMode, setJobsViewMode] = useState('mine'); // 'mine' | 'all'
  const [jobsLoadingMore, setJobsLoadingMore] = useState(false);
  const jobsPageSize = 20;
  // Batch Runs (pan-India queued runs) — fetched alongside the jobs list.
  const [batchRuns, setBatchRuns] = useState([]);
  const [expandedBatch, setExpandedBatch] = useState(null);  // batch_id of the currently-expanded parent
  const [batchChildrenById, setBatchChildrenById] = useState({}); // { [batch_id]: [child summaries] }
  const [watchingJobId, setWatchingJobId] = useState(null); // job_id currently being live-previewed in the main Results pane
  const [watchingMeta, setWatchingMeta] = useState(null);   // { batch_id, batch_index, batch_total } — header badge in Results
  const isAdmin = !!(user?.is_admin || (user?.permissions || []).includes('manage_users'));
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [pincodePickerOpen, setPincodePickerOpen] = useState(false);
  const [keywordPickerOpen, setKeywordPickerOpen] = useState(false);
  const TimeIcon = getTimeIcon();

  // Reset columns when platform type changes
  useEffect(() => {
    const cols = isOffline ? OFFLINE_COLUMNS : AVAILABLE_COLUMNS;
    setSelectedColumns(cols.filter(c => c.default).map(c => c.id));
  }, [isOffline]);
  const fileInputAsinRef = useRef(null);
  const fileInputPincodeRef = useRef(null);
  const fileInputKeywordRef = useRef(null);

  const handleProductsPicked = (identifiers) => {
    if (!identifiers.length) return;
    if (isBlinkit) {
      // For Blinkit, identifiers are actually {url, name, image_url} objects
      setBlinkitProducts(prev => {
        const existingUrls = new Set(prev.map(p => p.url));
        const newOnes = identifiers.filter(p => !existingUrls.has(p.url));
        return [...prev, ...newOnes];
      });
      toast.success(`Added ${identifiers.length} product${identifiers.length !== 1 ? 's' : ''}`);
    } else {
      const existing = asins.split('\n').map(s => s.trim()).filter(Boolean);
      const merged = [...new Set([...existing, ...identifiers])];
      setAsins(merged.join('\n'));
      toast.success(`Added ${identifiers.length} ${asinLabel}${identifiers.length !== 1 ? 's' : ''} from products`);
    }
  };

  const handlePincodesPicked = (pins) => {
    if (!pins.length) return;
    const existing = pincodes.split(',').map(s => s.trim()).filter(Boolean);
    const merged = [...new Set([...existing, ...pins])];
    setPincodes(merged.join(', '));
    toast.success(`Added ${pins.length} pincode${pins.length !== 1 ? 's' : ''}`);
  };

  const handleKeywordsPicked = (picked) => {
    if (!picked.length) return;
    const existing = keyword.split('\n').map(s => s.trim()).filter(Boolean);
    const existingLc = new Set(existing.map(s => s.toLowerCase()));
    const newOnes = picked.filter(k => !existingLc.has(k.toLowerCase()));
    if (!newOnes.length) {
      toast.message("All selected keywords are already in the list");
      return;
    }
    const merged = [...existing, ...newOnes];
    setKeyword(merged.join('\n'));
    toast.success(`Added ${newOnes.length} keyword${newOnes.length !== 1 ? 's' : ''} from library`);
  };

  const filteredResults = (() => {
    let rows = results;
    if (statusFilter !== 'all') {
      rows = rows.filter(r => r.status === statusFilter);
    }
    // Apply column-level value filters
    Object.entries(columnFilters).forEach(([colId, allowedValues]) => {
      if (allowedValues && allowedValues.size > 0) {
        rows = rows.filter(r => allowedValues.has(r[colId] || '-'));
      }
    });
    return rows;
  })();

  const activeFilterCount = Object.values(columnFilters).filter(s => s && s.size > 0).length;

  useEffect(() => {
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(clockInterval);
  }, []);

  // Track every job_id we've already played the "completed" sound for, so even if
  // the diff logic ever sees the same transition twice (closure-staleness, double
  // tick races, fork polling, etc.), the chime only plays ONCE per job.
  const alertedJobsRef = useRef(new Set());
  const alertedBatchesRef = useRef(new Set());
  const lastChimeAtRef = useRef(0);
  const playCompletedChimeOnce = useCallback((source) => {
    const now = Date.now();
    // Hard 1.5s cooldown — drops back-to-back beeps when several jobs complete
    // within the same poll tick (e.g., end of a batch run).
    if (now - lastChimeAtRef.current < 1500) return;
    lastChimeAtRef.current = now;
    try { SOUNDS.completed(); } catch {}
    // (source param kept for future debugging / per-source rate limiting)
    void source;
  }, []);

  // Poll jobs for drawer (every 5s when open or when there's an active job)
  const fetchAllJobs = useCallback(async () => {
    try {
      const allParam = jobsViewMode === 'all' ? '&all_users=true' : '';
      const limit = Math.max(jobsPageSize, allUserJobs.length);
      const r = await axios.get(`${API}/jobs?limit=${limit}&skip=0${allParam}`);
      const newJobs = r.data || [];
      // Detect status transitions for sound alerts.
      // RULES:
      //   • Skip children of a batch run (their parent batch_run plays one chime
      //     when the whole run finishes — see fetchBatchRuns below).
      //   • Only fire ONCE per job_id (alertedJobsRef guards re-fires).
      //   • playCompletedChimeOnce enforces a 1.5s cooldown across all sources.
      if (allUserJobs.length > 0) {
        newJobs.forEach(nj => {
          if (nj.batch_id) return; // batch children — silent here
          if (alertedJobsRef.current.has(nj.job_id)) return;
          const prev = allUserJobs.find(pj => pj.job_id === nj.job_id);
          if (prev && prev.status === 'processing' && nj.status !== 'processing') {
            if (nj.status === 'completed') {
              alertedJobsRef.current.add(nj.job_id);
              playCompletedChimeOnce(`job:${nj.job_id}`);
            }
          }
        });
      }
      setAllUserJobs(newJobs);
      try {
        const c = await axios.get(`${API}/jobs/count${jobsViewMode === 'all' ? '?all_users=true' : ''}`);
        setJobsTotal(c.data?.total || 0);
      } catch {}
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsViewMode, allUserJobs, playCompletedChimeOnce]);

  const loadMoreJobs = async () => {
    setJobsLoadingMore(true);
    try {
      const allParam = jobsViewMode === 'all' ? '&all_users=true' : '';
      const r = await axios.get(`${API}/jobs?limit=${jobsPageSize}&skip=${allUserJobs.length}${allParam}`);
      const newer = r.data || [];
      // Merge, dedupe by job_id
      const existingIds = new Set(allUserJobs.map(j => j.job_id));
      setAllUserJobs([...allUserJobs, ...newer.filter(j => !existingIds.has(j.job_id))]);
    } catch {
      toast.error('Failed to load more jobs');
    }
    setJobsLoadingMore(false);
  };

  // When view mode toggles, reset list + refetch
  useEffect(() => {
    setAllUserJobs([]);
    setJobsTotal(0);
    // Trigger immediate fetch
    (async () => {
      try {
        const allParam = jobsViewMode === 'all' ? '&all_users=true' : '';
        const r = await axios.get(`${API}/jobs?limit=${jobsPageSize}&skip=0${allParam}`);
        setAllUserJobs(r.data || []);
        const c = await axios.get(`${API}/jobs/count${jobsViewMode === 'all' ? '?all_users=true' : ''}`);
        setJobsTotal(c.data?.total || 0);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsViewMode]);

  useEffect(() => {
    fetchAllJobs();
    const hasActive = allUserJobs.some(j => j.status === 'processing');
    const interval = setInterval(fetchAllJobs, (drawerOpen || hasActive || isLoading) ? 5000 : 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, isLoading, fetchAllJobs]);

  // ─── Batch Runs polling (sits alongside the main jobs poller) ───
  const fetchBatchRuns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/batch-runs?view=${jobsViewMode === 'all' ? 'all' : 'mine'}`);
      const fresh = r.data || [];
      // ONE chime per batch-run transition into a terminal state. Children
      // are silent (see fetchAllJobs). Guarded by alertedBatchesRef + cooldown.
      if (batchRuns.length > 0) {
        fresh.forEach(nb => {
          const terminal = nb.status === 'completed' || nb.status === 'stopped' || nb.status === 'failed';
          if (!terminal) return;
          if (alertedBatchesRef.current.has(nb.batch_id)) return;
          const prev = batchRuns.find(pb => pb.batch_id === nb.batch_id);
          if (prev && (prev.status === 'running' || prev.status === 'queued')) {
            alertedBatchesRef.current.add(nb.batch_id);
            if (nb.status === 'completed') playCompletedChimeOnce(`batch:${nb.batch_id}`);
          }
        });
      }
      setBatchRuns(fresh);
    } catch { /* silently ignore */ }
  }, [jobsViewMode, batchRuns, playCompletedChimeOnce]);

  useEffect(() => {
    fetchBatchRuns();
    const hasActive = batchRuns.some(b => b.status === 'running' || b.status === 'queued');
    const interval = setInterval(fetchBatchRuns, (drawerOpen || hasActive) ? 5000 : 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, fetchBatchRuns]);

  // Re-fetch children of the currently-expanded batch on the same cadence
  useEffect(() => {
    if (!expandedBatch) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await axios.get(`${API}/batch-runs/${expandedBatch}`);
        if (!cancelled) setBatchChildrenById(prev => ({ ...prev, [expandedBatch]: r.data?.children || [] }));
      } catch { /* noop */ }
    };
    refresh();
    const t = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [expandedBatch]);

  // ─── Live-preview poller for a batch child job ─────────────────────────────
  // When `watchingJobId` is set, we hit /api/jobs/{id} every 3s and push the
  // returned rows into the main Results table so the user can VERIFY data is
  // being scraped in real time. Stops automatically once the job reaches a
  // terminal status (completed/stopped/failed). Independent of the regular
  // single-job poll loop that runs only during direct `handleScrape` flows.
  useEffect(() => {
    if (!watchingJobId) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/jobs/${watchingJobId}`);
        if (cancelled) return;
        const job = r.data;
        const results = job.results || [];
        setResults(results);
        const successCount = results.filter(x => x.status === 'success').length;
        const failedCount  = results.filter(x => x.status === 'error').length;
        const skippedCount = results.filter(x => x.status === 'skipped').length;
        const expectedTotal = job.total || results.length;
        setStats({
          total: expectedTotal,
          success: successCount,
          failed: failedCount,
          skipped: skippedCount,
          missing: Math.max(0, expectedTotal - results.length),
          returned: results.length,
        });
        // Auto-stop when this child is terminal — but KEEP the rows displayed
        // so the user has a final snapshot for verification.
        if (job.status && job.status !== 'processing' && job.status !== 'queued') {
          cancelled = true;
          if (timer) clearInterval(timer);
        }
      } catch { /* swallow — next tick will retry */ }
    };
    // Reset Results pane so the user sees a clean live feed for this batch
    setResults([]);
    setStats(null);
    setStatusFilter('all');
    setColumnFilters({});
    // Make sure column set matches Offline platform
    setSelectedColumns(OFFLINE_COLUMNS.filter(c => c.default).map(c => c.id));
    tick();
    timer = setInterval(tick, 3000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [watchingJobId]);

  // Helper: start watching a child job by job_id; also store batch meta for the
  // "now watching" header badge.
  const startWatchingBatchChild = (batchRun, child) => {
    if (!batchRun || !child || !child.job_id) return;
    setWatchingJobId(child.job_id);
    setWatchingMeta({
      batch_id: batchRun.batch_id,
      batch_index: child.batch_index,
      batch_total: batchRun.total_batches,
      batch_name: batchRun.name,
      job_id: child.job_id,
    });
    setDrawerOpen(false);
    toast.success(`Watching batch #${child.batch_index} live`);
  };
  
  useEffect(() => {
    // Fetch platform details from API
    axios.get(`${API}/platforms`).then(r => {
      const found = (r.data || []).find(p => p.platform_id === platformId);
      setPlatform(found || { platform_id: platformId || 'amazon', name: platformId || 'Amazon', color_bg: 'bg-orange-100', color_text: 'text-orange-600', active: true, description: '' });
    }).catch(() => {
      setPlatform({ platform_id: platformId || 'amazon', name: platformId || 'Amazon', color_bg: 'bg-orange-100', color_text: 'text-orange-600', active: true, description: '' });
    });

    const loadRecentRuns = async () => {
      try {
        const response = await axios.get(`${API}/jobs?limit=5`);
        setRecentRuns(response.data || []);
      } catch (e) {}
    };
    loadRecentRuns();

    // Check notification permission
    if ("Notification" in window && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
    
    // Show tutorial for first-time users
    if (!localStorage.getItem('horizon_tutorial_completed')) {
      const timer = setTimeout(() => setShowTutorial(true), 800);
      return () => clearTimeout(timer);
    }
  }, [platformId]);
  
  const enableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setNotificationsEnabled(granted);
    if (granted) {
      toast.success("Notifications enabled!");
    } else {
      toast.error("Notifications blocked. Please enable in browser settings.");
    }
  };

  const completeTutorial = () => {
    localStorage.setItem('horizon_tutorial_completed', 'true');
    setShowTutorial(false);
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    } catch {
      localStorage.removeItem('horizon_session_token');
      toast.error("Logout failed");
    }
  };
  
  const handleColumnToggle = (columnId) => {
    setSelectedColumns(prev => 
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };
  
  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const lines = event.target.result.split(/[\n,]/).map(l => l.trim()).filter(l => l && !l.toLowerCase().includes('asin') && !l.toLowerCase().includes('pincode') && !l.toLowerCase().includes('keyword'));
      if (type === 'asin') {
        setAsins(lines.join('\n'));
        toast.success(`Loaded ${lines.length} ASINs`);
      } else if (type === 'keyword') {
        setKeyword(lines.join('\n'));
        toast.success(`Loaded ${lines.length} keywords`);
      } else {
        setPincodes(lines.join(', '));
        toast.success(`Loaded ${lines.length} pincodes`);
      }
    };
    reader.readAsText(file);
  };
  
  const downloadSampleCSV = (type) => {
    const content = type === 'asin' ? SAMPLE_ASIN_CSV : type === 'keyword' ? SAMPLE_KEYWORD_CSV : SAMPLE_PINCODE_CSV;
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sample_${type}s.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  
  const addTerminalLog = (message, type = 'info') => {
    setTerminalLogs(prev => [...prev.slice(-15), { message, type, time: new Date().toLocaleTimeString() }]);
  };
  
  const handleScrape = async () => {
    const pincodeList = pincodes.split(',').map(p => p.trim()).filter(Boolean);
    if (!pincodeList.length) return toast.error("Enter at least one pincode");

    let payload, totalTasks, initMsg;

    if (isOffline) {
      const kwList = keyword.split(/[\n]/).map(k => k.trim()).filter(Boolean);
      if (!kwList.length) return toast.error("Enter at least one keyword");

      // ─── Batch mode: split pincodes into chunks, queue serially. Each chunk is a
      // standalone job that already supports its own CSV download. We DON'T enter
      // the live-preview / polling flow — the user monitors batches from the Job
      // Queue drawer (a dedicated Batch Runs section lists them).
      if (offlineBatchMode) {
        const size = Math.max(1, Math.min(parseInt(offlineBatchSize, 10) || 100, 2000));
        const batchPayload = {
          platform: "offline",
          keywords: kwList,
          pincodes: pincodeList,
          batch_size: size,
          engine: offlineEngine,
          enrich_address: offlineEngine === "fast" ? offlineEnrichAddress : false,
          proxy_rotate: offlineEngine === "fast" ? offlineProxyRotate : true,
          name: `${kwList.length} kw × ${pincodeList.length} pins`,
        };
        try {
          const r = await axios.post(`${API}/scrape/batch`, batchPayload, { timeout: 60000 });
          toast.success(`Queued ${r.data.total_batches} batch${r.data.total_batches !== 1 ? "es" : ""} (${r.data.batch_size} pincodes each)`);
          // Open the Job Queue drawer so the user sees the batches arriving
          setDrawerOpen(true);
          fetchAllJobs();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to queue batch run");
        }
        return; // CRITICAL: skip the regular single-job submit/poll flow below
      }

      payload = {
        platform: "offline",
        keywords: kwList,
        pincodes: pincodeList,
        engine: offlineEngine,
        enrich_address: offlineEngine === "fast" ? offlineEnrichAddress : false,
        proxy_rotate: offlineEngine === "fast" ? offlineProxyRotate : false,
      };
      totalTasks = kwList.length * pincodeList.length;
      const engineLabel = offlineEngine === "fast"
        ? (offlineEnrichAddress ? "Fast + full address" : "Fast")
        : "Legacy";
      initMsg = `[SYSTEM] ${kwList.length} keyword(s) × ${pincodeList.length} pincodes = ${totalTasks} tasks · engine: ${engineLabel}`;
    } else if (isBlinkit) {
      if (!blinkitProducts.length) return toast.error("Select at least one product with a Blinkit URL");
      const urls = blinkitProducts.map(p => ({ url: p.url, product_name: p.name }));
      payload = { platform: "blinkit", urls, pincodes: pincodeList };
      totalTasks = urls.length * pincodeList.length;
      initMsg = `[SYSTEM] ${urls.length} product(s) × ${pincodeList.length} pincodes = ${totalTasks} tasks`;
    } else {
      const asinList = asins.split(/[\n,]/).map(a => a.trim()).filter(Boolean);
      if (!asinList.length) return toast.error(`Enter at least one ${asinLabel}`);
      payload = { platform: platformId || "amazon", asins: asinList, pincodes: pincodeList };
      // Amazon delivery: include engine choice (default 'legacy' preserves today's behavior)
      if ((platformId || "amazon") === "amazon" && amazonTab === "delivery") {
        payload.engine = amazonEngine;
      }
      totalTasks = asinList.length * pincodeList.length;
      const engineSuffix = (payload.engine === "fast")
        ? ` · engine: Fast`
        : (payload.engine === "legacy" ? ` · engine: Legacy` : "");
      initMsg = `[SYSTEM] ${asinList.length} ${asinLabel}(s) × ${pincodeList.length} pincodes — ${Math.ceil(asinList.length / 5)} chunk(s)${engineSuffix}`;
    }

    setIsLoading(true);
    setProgress(0);
    setResults([]);
    setStatusFilter('all');
    setColumnFilters({});
    setStartTime(Date.now());
    setEndTime(null);
    setStats(null);
    setTerminalLogs([]);
    
    addTerminalLog(`[INIT] Starting extraction for ${totalTasks} tasks`, 'info');
    addTerminalLog(`[PLATFORM] ${platform?.name?.toUpperCase() || platformId?.toUpperCase()}`, 'info');
    addTerminalLog(initMsg, 'info');
    addTerminalLog(`[SYSTEM] Initializing scraper...`, 'processing');
    
    setProgress(5);
    
    try {
      const response = await axios.post(`${API}/scrape`, payload, { timeout: 30000 });
      const jobId = response.data.job_id;
      const totalTasks = response.data.total || totalTasks;
      
      addTerminalLog(`[JOB] Started job ${jobId} — ${totalTasks} tasks`, 'processing');
      
      // Poll for results — real progress only
      let lastProgressMsg = '';
      let pollFailures = 0;
      const pollStartTime = Date.now();
      const pollInterval = setInterval(async () => {
        // Safety: if polling for over 30 min, stop
        if (Date.now() - pollStartTime > 30 * 60 * 1000) {
          clearInterval(pollInterval);
          addTerminalLog(`[TIMEOUT] Polling timed out after 30 min. Check Job Queue for results.`, 'error');
          setIsLoading(false);
          return;
        }
        try {
          const jobResponse = await axios.get(`${API}/jobs/${jobId}`, { timeout: 15000 });
          const job = jobResponse.data;
          const completedCount = job.completed_count || job.results?.length || 0;
          // Progress: 5% (started) → 95% (all done), never go backwards
          const realPct = Math.min(Math.round((completedCount / totalTasks) * 90) + 5, 95);
          setProgress(prev => Math.max(prev, realPct));
          pollFailures = 0;
          
          // Show progress message from backend
          if (job.progress_message && job.progress_message !== lastProgressMsg) {
            lastProgressMsg = job.progress_message;
            addTerminalLog(`[PROGRESS] ${job.progress_message}`, 'processing');
          }
          
          // Update results progressively
          if (job.results?.length > 0) {
            setResults(job.results);
          }
          
          if (job.status === 'completed' || job.status === 'failed' || job.status === 'stopped') {
            clearInterval(pollInterval);
            setProgress(100);
            setEndTime(Date.now());
            setResults(job.results || []);
            
            const successCount = (job.results || []).filter(r => r.status === 'success').length;
            const failedCount = (job.results || []).filter(r => r.status === 'error').length;
            const skippedCount = (job.results || []).filter(r => r.status === 'skipped').length;
            const resultCount = job.results?.length || 0;
            const expectedTotal = job.total || resultCount;
            const missingCount = expectedTotal - resultCount;
            setStats({ total: expectedTotal, success: successCount, failed: failedCount, skipped: skippedCount, missing: missingCount, returned: resultCount });
            
            if (job.status === 'completed') {
              const missingNote = missingCount > 0 ? ` (${missingCount} pincodes skipped/timed out)` : '';
              addTerminalLog(`[COMPLETE] ${successCount} success, ${failedCount} failed out of ${expectedTotal} tasks${missingNote}`, 'success');
              toast.success(`Extracted ${total} results`);
              // Mark this job as already alerted so the drawer-poller diff doesn't double-fire.
              try { alertedJobsRef.current.add(job.job_id); } catch {}
              playCompletedChimeOnce(`scrape-poller:${job.job_id}`);
              if (notificationsEnabled) {
                sendNotification('HORIZON - Extraction Complete', `Successfully extracted ${successCount}/${total} products from ${platform.name}`);
              }
            } else {
              addTerminalLog(`[ERROR] Job failed: ${job.error || 'Unknown error'}`, 'error');
              toast.error('Extraction failed');
              if (notificationsEnabled) {
                sendNotification('HORIZON - Extraction Failed', 'An error occurred during extraction');
              }
            }
            
            setIsLoading(false);
            // Refresh recent runs
            try {
              const runsResponse = await axios.get(`${API}/jobs?limit=5`);
              setRecentRuns(runsResponse.data || []);
            } catch (e) {}
          }
        } catch (pollErr) {
          pollFailures++;
          console.warn('Poll error:', pollErr.message);
          if (pollFailures > 30) {
            clearInterval(pollInterval);
            addTerminalLog(`[CONNECTION LOST] Don't worry — your scraping job is still running on the server and all results are being saved. Refresh the page to reconnect and view progress in the Job Queue.`, 'error');
            toast.error(
              "Connection lost — but your job is safe! Scraping continues on the server. Refresh when you're back online.",
              { duration: 20000 }
            );
            SOUNDS.completed();
            setIsLoading(false);
          }
        }
      }, 4000);
      
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      addTerminalLog(`[ERROR] ${detail}`, 'error');
      toast.error(detail, { duration: 8000 });
      setEndTime(Date.now());
      
      if (notificationsEnabled) {
        sendNotification('HORIZON - Extraction Failed', detail);
      }
      setIsLoading(false);
    }
  };
  
  const handleExport = async (format, data = null) => {
    const baseData = data || results;
    if (!baseData.length) return toast.error("No results to export");
    
    // Apply all active filters (unless exporting from history — data is passed directly)
    let exportRows = baseData;
    if (!data) {
      exportRows = filteredResults;
    }
    
    try {
      const exportData = exportRows.map(r => {
        const filtered = {};
        selectedColumns.forEach(col => { filtered[col] = r[col]; });
        return filtered;
      });
      
      const response = await axios.post(`${API}/export/${format}`, exportData, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `horizon_${platform.platform_id}_${Date.now()}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} exported`);
    } catch {
      toast.error("Export failed");
    }
  };
  
  const loadRun = (run) => {
    setResults(run.results || []);
    const successCount = (run.results || []).filter(r => r.status === 'success').length;
    const failedCount = (run.results || []).filter(r => r.status === 'error').length;
    const skippedCount = (run.results || []).filter(r => r.status === 'skipped').length;
    const resultCount = run.results?.length || 0;
    const expectedTotal = run.total || resultCount;
    const missingCount = expectedTotal - resultCount;
    setStats({ total: expectedTotal, success: successCount, failed: failedCount, skipped: skippedCount, missing: missingCount, returned: resultCount });
    const runIsOffline = run.platform === 'offline' || (run.results || []).some(r => r.store_name !== undefined || r.keyword);
    const runIsBlinkit = run.platform === 'blinkit';
    const runIsBuybox = run.platform === 'amazon-buybox';
    const runIsBestseller = run.platform === 'amazon-bestseller';
    const cols = runIsOffline ? OFFLINE_COLUMNS : runIsBlinkit ? BLINKIT_COLUMNS : runIsBuybox ? BUYBOX_COLUMNS : runIsBestseller ? MARKET_RESEARCH_COLUMNS : AVAILABLE_COLUMNS;
    setSelectedColumns(cols.filter(c => c.default).map(c => c.id));
    // Switch to correct tab for Buy Box / Market Research
    if (runIsBuybox) {
      setAmazonTab('buybox');
      setBuyboxLoadedRun(run);
    } else if (runIsBestseller) {
      setPlatformId('amazon');
      setAmazonTab('marketresearch');
    } else if (run.platform === 'amazon') {
      setAmazonTab('delivery');
    }
    if (run.created_at) {
      const s = new Date(run.created_at).getTime();
      setStartTime(s);
      setEndTime(run.completed_at ? new Date(run.completed_at).getTime() : s);
    }
    toast.success('Loaded previous run');
  };

  // Load a job even if it's an older/stripped entry — always fetch fresh data first
  const openJobInTable = async (job) => {
    try {
      const r = await axios.get(`${API}/jobs/${job.job_id}`);
      const fresh = r.data;
      if (!fresh || !(fresh.results?.length)) {
        toast.error('This job has no results to display');
        return;
      }
      loadRun(fresh);
      setDrawerOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load job');
    }
  };
  
  const formatDuration = (ms) => {
    if (!ms || ms < 0) return '-';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getJobDuration = (job) => {
    if (!job.created_at) return null;
    const end = job.completed_at ? new Date(job.completed_at) : (job.status === 'processing' ? new Date() : null);
    if (!end) return null;
    const start = new Date(job.created_at);
    const ms = end - start;
    if (ms < 0) return null;
    return formatDuration(ms);
  };

  const handleStopJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/stop`);
      toast.success("Stop signal sent — job will halt after current task.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        toast.error("Job no longer exists or was already cleaned up. Refreshing queue…");
      } else if (status === 400) {
        // Job already stopped/completed/failed — surface server message and refresh
        toast.message(detail || "Job is no longer running.");
      } else {
        toast.error(detail || "Failed to stop job");
      }
      // Always refresh the queue so the UI reflects reality
      try { await fetchAllJobs?.(); } catch { /* noop */ }
    }
  };

  // ─── Delivery Tracker Scheduling ───
  const loadDelivSchedules = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/delivery/schedules`);
      setDelivSchedules(r.data || []);
    } catch {}
  }, []);

  useEffect(() => { if (platformId === 'amazon') loadDelivSchedules(); }, [platformId, loadDelivSchedules]);

  const handleSaveDelivSchedule = async () => {
    const asinList = (asins || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    const pinList = (pincodes || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (!asinList.length) return toast.error("Add at least one ASIN before scheduling");
    if (!pinList.length) return toast.error("Add at least one pincode");
    if (delivScheduleForm.frequency === 'weekly' && !delivScheduleForm.days.length) {
      return toast.error("Select at least one day for a weekly schedule");
    }
    try {
      const payload = {
        name: delivScheduleForm.name?.trim() || `Amazon — ${asinList.length} ASIN${asinList.length !== 1 ? 's' : ''} × ${pinList.length} pin${pinList.length !== 1 ? 's' : ''}`,
        platform: 'amazon',
        asins: asinList,
        pincodes: pinList,
        frequency: delivScheduleForm.frequency,
        time_of_day: delivScheduleForm.time_of_day,
        days: delivScheduleForm.frequency === 'weekly' ? delivScheduleForm.days : [],
        engine: delivScheduleForm.engine || 'legacy',
      };
      await axios.post(`${API}/delivery/schedules`, payload);
      toast.success("Schedule created");
      setDelivScheduleModalOpen(false);
      setDelivScheduleForm({ name: '', frequency: 'daily', time_of_day: '09:00', days: [0,1,2,3,4], engine: 'legacy' });
      loadDelivSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create schedule");
    }
  };

  const handleToggleDelivSchedule = async (sch) => {
    try {
      await axios.put(`${API}/delivery/schedules/${sch.schedule_id}`, { active: !sch.active });
      loadDelivSchedules();
    } catch { toast.error("Failed to update schedule"); }
  };

  const handleDeleteDelivSchedule = async (id) => {
    if (delivPendingDeleteId !== id) {
      setDelivPendingDeleteId(id);
      if (delivPendingDeleteTimer.current) clearTimeout(delivPendingDeleteTimer.current);
      delivPendingDeleteTimer.current = setTimeout(() => setDelivPendingDeleteId(null), 3500);
      toast.message("Click delete again to confirm", { duration: 3000 });
      return;
    }
    if (delivPendingDeleteTimer.current) clearTimeout(delivPendingDeleteTimer.current);
    setDelivPendingDeleteId(null);
    try {
      await axios.delete(`${API}/delivery/schedules/${id}`);
      toast.success("Schedule deleted");
      loadDelivSchedules();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  const formatNextRunDeliv = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
    } catch { return '—'; }
  };

  const describeDelivSchedule = (sch) => {
    if (sch.frequency === 'hourly') return 'Every hour';
    const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    if (sch.frequency === 'daily') return `Daily at ${sch.time_of_day} IST`;
    if (sch.frequency === 'weekly') {
      const ds = (sch.days || []).map(d => dayNames[d]).join(', ');
      return `${ds} at ${sch.time_of_day} IST`;
    }
    return sch.frequency;
  };

  const openDelivRunsModal = async (sch) => {
    setDelivRunsModal({ open: true, schedule: sch, runs: [], loading: true });
    try {
      const r = await axios.get(`${API}/delivery/schedules/${sch.schedule_id}/runs`);
      setDelivRunsModal({ open: true, schedule: r.data.schedule || sch, runs: r.data.runs || [], loading: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load runs");
      setDelivRunsModal({ open: false, schedule: null, runs: [], loading: false });
    }
  };

  const handleLoadDelivRunIntoTable = async (jobId) => {
    try {
      const r = await axios.get(`${API}/jobs/${jobId}`);
      const job = r.data;
      if (!job?.results?.length) { toast.error("This run has no results yet"); return; }
      loadRun(job);
      setDelivRunsModal({ open: false, schedule: null, runs: [], loading: false });
    } catch { toast.error("Failed to load run"); }
  };

  const handleResumeJob = async (jobId) => {
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/resume`);
      toast.success(res.data.message || "Job resumed");
      // Refresh jobs list
      const runsResponse = await axios.get(`${API}/jobs?limit=20`);
      setAllUserJobs(runsResponse.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resume job");
    }
  };
  
  if (!platform) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#32B4AC] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard">
      <AppHeader
        user={user}
        breadcrumbs={(() => {
          const AMAZON_SUB = {
            delivery:       { label: 'Delivery Tracker', icon: <Truck className="w-3.5 h-3.5" /> },
            buybox:         { label: 'Buy Box Monitor',  icon: <Award className="w-3.5 h-3.5" /> },
            marketresearch: { label: 'Market Research',  icon: <Search className="w-3.5 h-3.5" /> },
          };
          const sub = (platformId === 'amazon' && AMAZON_SUB[amazonTab])
            ? [{ label: AMAZON_SUB[amazonTab].label, icon: AMAZON_SUB[amazonTab].icon, testId: `amazon-${amazonTab}` }]
            : [];
          return [
            { label: "Platforms", onClick: () => navigate("/select-platform"), testId: "platforms" },
            {
              label: platform.name,
              icon: <PlatformIcon platform={platform} size="w-3.5 h-3.5" containerSize="w-5 h-5 rounded" />,
              onClick: sub.length > 0 && amazonTab !== 'delivery' ? () => setAmazonTab('delivery') : undefined,
              testId: `platform-${platform.platform_id || platformId}`,
            },
            ...sub,
          ];
        })()}
        onNavigate={(path) => navigate(path)}
        onLogout={handleLogout}
        rightContent={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTutorial(true)}
              className="text-gray-400 hover:text-gray-600"
              title="Show tutorial"
              data-testid="help-btn"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
          </div>
        }
      />
      
      <main className="max-w-full mx-auto px-6 py-6 pb-16">
        {/* Greeting Banner */}
        <div className="greeting-banner animate-fadeInUp mb-6" data-testid="greeting-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="greeting-icon-wrapper">
                <TimeIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{greeting.text}</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {currentTime.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}
                  <span className="mx-2 text-gray-300">|</span>
                  <span className="text-[#32B4AC] font-medium">{currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-5">
              <div className="text-right pl-5 border-l border-gray-200">
                <div className="text-sm font-semibold text-gray-900">{platform.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Active Platform</div>
              </div>
            </div>
          </div>
        </div>
        {/* Flipkart Speed Notice */}
        {isFlipkart && (
          <div className="mb-5 flex items-center gap-3 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200/60" data-testid="flipkart-speed-notice">
            <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-3.5 h-3.5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-amber-800">
                <span className="font-semibold">Heads up:</span> Flipkart scraping takes ~30-40s per FSN due to delivery modal interactions (pincode entry + address confirmation). Amazon is typically 3-5x faster.
              </span>
            </div>
            <button onClick={(e) => e.target.closest('[data-testid="flipkart-speed-notice"]').style.display = 'none'}
              className="shrink-0 text-amber-400 hover:text-amber-600 transition-colors p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {/* Amazon Tabs: Delivery / Buy Box */}
        {platformId === 'amazon' && (
          <div className="flex items-center gap-1 mb-5 bg-white rounded-xl border border-gray-200 p-1 w-fit" data-testid="amazon-tabs">
            <button
              onClick={() => setAmazonTab('delivery')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                amazonTab === 'delivery' ? 'bg-[#32B4AC] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="amazon-tab-delivery"
            >
              Delivery Tracker
            </button>
            <button
              onClick={() => setAmazonTab('buybox')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                amazonTab === 'buybox' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="amazon-tab-buybox"
            >
              <span className="flex items-center gap-1.5">Buy Box Monitor</span>
            </button>
            <button
              onClick={() => setAmazonTab('marketresearch')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                amazonTab === 'marketresearch' ? 'bg-purple-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="amazon-tab-marketresearch"
            >
              <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Market Research</span>
            </button>
          </div>
        )}
        {/* Buy Box Module */}
        {platformId === 'amazon' && amazonTab === 'buybox' ? (
          <BuyBoxModule
            buyboxPincode={buyboxPincode}
            setBuyboxPincode={setBuyboxPincode}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            addTerminalLog={addTerminalLog}
            formatDuration={formatDuration}
            platformId={platformId}
            loadedRun={buyboxLoadedRun}
            clearLoadedRun={() => setBuyboxLoadedRun(null)}
          />
        ) : (platformId === 'amazon' && amazonTab === 'marketresearch') ? (
          <MarketResearchModule
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            results={results}
            setResults={setResults}
            stats={stats}
            setStats={setStats}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            handleExport={handleExport}
            addTerminalLog={addTerminalLog}
            playCompletedChimeOnce={playCompletedChimeOnce}
            alertedJobsRef={alertedJobsRef}
            formatDuration={formatDuration}
          />
        ) : (
        <>
        {/* Delivery Tracker header (Amazon only) — schedule controls */}
        {platformId === 'amazon' && (
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#E8F8F5] to-[#F0FDFA] border border-[#32B4AC]/20 rounded-xl px-5 py-4 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white border border-[#32B4AC]/30 flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#32B4AC]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-gray-900">Delivery Tracker</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">Hyperlocal pricing &amp; delivery dates per pincode (residential proxy)</p>
            </div>
            <button
              onClick={() => setDelivScheduleModalOpen(true)}
              className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md border transition-all ${
                delivSchedules.filter(s => s.active).length > 0
                  ? 'text-white bg-gradient-to-r from-[#32B4AC] to-[#2CD4C4] border-[#32B4AC] shadow-sm shadow-[#32B4AC]/25 hover:shadow-md'
                  : 'text-[#32B4AC] bg-white border-[#32B4AC]/30 hover:bg-[#F0FDFA] hover:border-[#32B4AC]/60'
              }`}
              data-testid="delivery-schedule-btn"
            >
              <CalendarClock className={`w-3.5 h-3.5 ${delivSchedules.filter(s => s.active).length > 0 ? 'animate-pulse' : ''}`} />
              Schedule
              {delivSchedules.filter(s => s.active).length > 0 && (
                <>
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-white text-[#32B4AC] tabular-nums" data-testid="delivery-schedule-count-badge">
                    {delivSchedules.filter(s => s.active).length}
                  </span>
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-white">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Active Delivery Schedules List */}
        {platformId === 'amazon' && delivSchedules.length > 0 && (
          <div className="dashboard-card mb-5" data-testid="delivery-schedules-card">
            <div className="dashboard-card-header flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-[#32B4AC]" />
                <span className="font-semibold text-sm text-gray-700">Scheduled Delivery Runs</span>
                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{delivSchedules.length}</span>
              </div>
            </div>
            <div className="dashboard-card-body p-0">
              <div className="divide-y divide-gray-100">
                {delivSchedules.map(sch => (
                  <div key={sch.schedule_id} className="flex items-center gap-4 px-5 py-3" data-testid={`delivery-schedule-row-${sch.schedule_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${sch.active ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                        <span className="text-sm font-semibold text-gray-800 truncate">{sch.name}</span>
                        <span className="text-[10px] text-[#32B4AC] bg-[#E8F8F5] border border-[#32B4AC]/20 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">{sch.frequency}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide border ${
                            (sch.scrape_engine || 'legacy') === 'fast'
                              ? 'text-amber-700 bg-amber-50 border-amber-200'
                              : 'text-gray-500 bg-gray-50 border-gray-200'
                          }`}
                          title={`Scrape engine: ${sch.scrape_engine || 'legacy'}`}
                          data-testid={`delivery-schedule-engine-badge-${sch.schedule_id}`}
                        >{(sch.scrape_engine || 'legacy') === 'fast' ? 'Fast' : 'Legacy'}</span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span><Repeat className="w-3 h-3 inline mr-1" />{describeDelivSchedule(sch)}</span>
                        <span>•</span>
                        <span>{sch.asins?.length || 0} ASIN{sch.asins?.length !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{sch.pincodes?.length || 0} pin{sch.pincodes?.length !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>Next: <span className="font-medium text-gray-700">{formatNextRunDeliv(sch.next_run)}</span></span>
                      </div>
                    </div>
                    <button
                      onClick={() => openDelivRunsModal(sch)}
                      className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:text-[#32B4AC] hover:border-[#32B4AC]/30 hover:bg-[#F0FDFA] transition-colors"
                      title="View past runs"
                      data-testid={`delivery-schedule-runs-${sch.schedule_id}`}
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleToggleDelivSchedule(sch)}
                      className={`p-1.5 rounded-md border transition-colors ${sch.active ? 'text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-gray-400 border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
                      title={sch.active ? 'Pause schedule' : 'Resume schedule'}
                      data-testid={`delivery-schedule-toggle-${sch.schedule_id}`}
                    >
                      <Power className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteDelivSchedule(sch.schedule_id)}
                      className={`p-1.5 rounded-md border transition-colors ${
                        delivPendingDeleteId === sch.schedule_id
                          ? 'border-red-400 bg-red-100 text-red-600 ring-2 ring-red-200 animate-pulse'
                          : 'border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50'
                      }`}
                      title={delivPendingDeleteId === sch.schedule_id ? 'Click again within 3s to confirm' : 'Delete schedule'}
                      data-testid={`delivery-schedule-delete-${sch.schedule_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Offline platform is scraping-only. Master DB & Analytics moved to /dashboard/analytics → Offline Market Intelligence card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-4 space-y-4">
            <div className="dashboard-card">
              <div className="dashboard-card-header"><span className="font-semibold text-sm text-gray-700">Input</span></div>
              <div className="dashboard-card-body space-y-4">
                {isOffline ? (
                  /* Offline: Keywords input (bulk, like ASINs) */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Keywords</label>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setKeywordPickerOpen(true)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded-md hover:bg-[#E0F7F4] hover:border-[#32B4AC]/50 transition-colors font-medium"
                          data-testid="pick-from-keywords-btn"
                        >
                          <Sparkles className="w-3 h-3" /> Library
                        </button>
                        <button onClick={() => downloadSampleCSV('keyword')} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors">
                          <Download className="w-3 h-3" /> Sample
                        </button>
                        <input type="file" ref={fileInputKeywordRef} onChange={(e) => handleFileUpload(e, 'keyword')} accept=".csv,.txt" className="hidden" />
                        <button onClick={() => fileInputKeywordRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[#32B4AC] rounded-md hover:bg-[#2a9d96] transition-colors">
                          <Upload className="w-3 h-3" /> Upload
                        </button>
                      </div>
                    </div>

                    {/* Compact "how to write good keywords" hint — collapsed by default so it
                        doesn't clutter the form; users click the row to expand full guidance.
                        Uses the same teal accent as the Library / Fast toggle so it reads as
                        guidance, not an error. */}
                    <details
                      className="mb-2 rounded-md border border-[#32B4AC]/25 bg-[#F0FDFA]/60 text-[11px] leading-snug text-slate-700 group"
                      data-testid="keyword-entry-hint"
                    >
                      <summary className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                        <Info className="w-3.5 h-3.5 shrink-0 text-[#32B4AC]" />
                        <span className="font-semibold text-slate-800">How to enter keywords</span>
                        <span className="ml-auto text-[10px] text-slate-400 group-open:hidden">Show tips</span>
                        <span className="ml-auto text-[10px] text-slate-400 hidden group-open:inline">Hide</span>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="px-2.5 pb-2 pt-0.5">
                        <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
                          <li><b>One keyword per line</b> — each line runs against every pincode.</li>
                          <li>Keep them <b>short & category-like</b>: <code className="px-1 py-0.5 rounded bg-white/70 border border-slate-200 text-[10px]">chemist</code>, <code className="px-1 py-0.5 rounded bg-white/70 border border-slate-200 text-[10px]">gym supplement store</code>, <code className="px-1 py-0.5 rounded bg-white/70 border border-slate-200 text-[10px]">nutrition store</code> — not full sentences.</li>
                          <li>For <b>non-India pincodes</b>, add the country to guide Maps: <code className="px-1 py-0.5 rounded bg-white/70 border border-slate-200 text-[10px]">pharmacy Kathmandu Nepal</code>, <code className="px-1 py-0.5 rounded bg-white/70 border border-slate-200 text-[10px]">chemist London UK</code>.</li>
                          <li>Skip brand names (e.g. <i>Apollo Pharmacy</i>) unless you want only that chain — Maps then returns only their outlets.</li>
                          <li>Use <b>Library</b> to reuse saved keyword sets, or <b>Upload</b> a CSV with one keyword per row.</li>
                        </ul>
                      </div>
                    </details>

                    <Textarea
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      placeholder={"chemist\ngym supplement store\nnutrition store\npharmacy Kathmandu Nepal"}
                      className="input-clean text-sm min-h-[100px] resize-none placeholder:text-gray-300"
                      data-testid="keyword-input"
                    />
                    {/* Engine toggle — Fast (default, parallel feed-card extraction) vs Legacy (visit each store) */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Scrape engine</label>
                        <span className="text-[10px] text-gray-400">
                          {offlineEngine === "fast" ? "~110× faster · cards only" : "Slower · visits each store"}
                        </span>
                      </div>
                      <div className="inline-flex w-full rounded-lg border border-gray-200 bg-gray-50 p-1" data-testid="offline-engine-toggle">
                        <button
                          type="button"
                          onClick={() => setOfflineEngine("fast")}
                          className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                            offlineEngine === "fast"
                              ? "bg-white text-[#32B4AC] shadow-sm border border-[#32B4AC]/20"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                          data-testid="offline-engine-fast"
                        >
                          Fast
                        </button>
                        <button
                          type="button"
                          onClick={() => setOfflineEngine("legacy")}
                          className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                            offlineEngine === "legacy"
                              ? "bg-white text-gray-700 shadow-sm border border-gray-300"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                          data-testid="offline-engine-legacy"
                        >
                          Legacy
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
                        {offlineEngine === "fast"
                          ? "Pulls store name, address & phone from search-results cards in parallel. Best for large runs."
                          : "Visits each store's detail page sequentially. Use only if Fast misses fields you need."}
                      </p>
                      {offlineEngine === "fast" && (
                        <label
                          className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-md bg-gray-50/60 border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors select-none"
                          data-testid="offline-enrich-address-toggle"
                        >
                          <input
                            type="checkbox"
                            checked={offlineEnrichAddress}
                            onChange={(e) => setOfflineEnrichAddress(e.target.checked)}
                            className="mt-0.5 h-3.5 w-3.5 accent-[#32B4AC] cursor-pointer"
                            data-testid="offline-enrich-address-checkbox"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-gray-700">
                              Full phone + address (recommended)
                            </div>
                            <div className="text-[10px] text-gray-400 leading-snug mt-0.5">
                              Visits each store after the card sweep to grab the canonical address AND fill in missing phone numbers (~95% coverage vs ~10-20% from cards alone). Rows that already have a phone from the card sweep are skipped, so extra time is only spent where it's needed. Adds ~5–10s per pincode on average.
                            </div>
                          </div>
                        </label>
                      )}
                      {offlineEngine === "fast" && (
                        <label
                          className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-md bg-gray-50/60 border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors select-none"
                          data-testid="offline-proxy-rotate-toggle"
                        >
                          <input
                            type="checkbox"
                            checked={offlineProxyRotate}
                            onChange={(e) => setOfflineProxyRotate(e.target.checked)}
                            className="mt-0.5 h-3.5 w-3.5 accent-[#32B4AC] cursor-pointer"
                            data-testid="offline-proxy-rotate-checkbox"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-gray-700">
                              Rotate proxy per pincode
                            </div>
                            <div className="text-[10px] text-gray-400 leading-snug mt-0.5">
                              Each pincode gets a unique residential IP — dodges Google Maps soft-bans and combo timeouts on large runs. Recommended ON.
                            </div>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                ) : isBlinkit ? (
                  /* Blinkit: Product selector (URL-based) */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Products</label>
                      <button onClick={() => setProductPickerOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded-md hover:bg-[#E0F7F4] hover:border-[#32B4AC]/50 transition-colors font-medium" data-testid="blinkit-pick-products-btn">
                        <ListChecks className="w-3 h-3" /> Select Products
                      </button>
                    </div>
                    {blinkitProducts.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-[#32B4AC]/40 hover:bg-[#F0FDFA]/30 transition-colors"
                        onClick={() => setProductPickerOpen(true)} data-testid="blinkit-empty-state">
                        <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <div className="text-sm text-gray-500 font-medium">No products selected</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">Click to select products with Blinkit URLs from your catalog</div>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="max-h-[140px] overflow-y-auto divide-y divide-gray-50">
                          {blinkitProducts.map((p, i) => (
                            <div key={i} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 group">
                              {p.image_url ? (
                                <img src={p.image_url} alt="" className="w-7 h-7 rounded object-cover border border-gray-200" onError={e => e.target.style.display='none'} />
                              ) : (
                                <div className="w-7 h-7 rounded bg-yellow-50 flex items-center justify-center"><Zap className="w-3.5 h-3.5 text-yellow-500" /></div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-800 truncate">{p.name}</div>
                                <div className="text-[10px] text-gray-400 truncate">{p.url}</div>
                              </div>
                              <button onClick={() => setBlinkitProducts(prev => prev.filter((_, idx) => idx !== i))}
                                className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="bg-gray-50 px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">
                          {blinkitProducts.length} product{blinkitProducts.length !== 1 ? 's' : ''} selected
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Amazon/Flipkart: ASIN/PID input */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">{asinLabel}s</label>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setProductPickerOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded-md hover:bg-[#E0F7F4] hover:border-[#32B4AC]/50 transition-colors font-medium" data-testid="pick-from-products-btn">
                          <ListChecks className="w-3 h-3" /> Products
                        </button>
                        <button onClick={() => downloadSampleCSV('asin')} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors">
                          <Download className="w-3 h-3" /> Sample
                        </button>
                        <input type="file" ref={fileInputAsinRef} onChange={(e) => handleFileUpload(e, 'asin')} accept=".csv,.txt" className="hidden" />
                        <button onClick={() => fileInputAsinRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[#32B4AC] rounded-md hover:bg-[#2a9d96] transition-colors">
                          <Upload className="w-3 h-3" /> Upload
                        </button>
                      </div>
                    </div>
                    <Textarea
                      value={asins}
                      onChange={(e) => setAsins(e.target.value)}
                      placeholder={isFlipkart ? "PSLGNAX3SZHHGRRG\nMOBH4SAM7GRRHFGZ" : "B08N5WRWNW\nB09V3KXJPB"}
                      className="input-clean font-mono text-xs min-h-[100px] resize-none placeholder:text-gray-300"
                      data-testid="asin-input"
                    />
                    {/* Amazon Delivery: scrape engine toggle (Legacy default, Fast opt-in) */}
                    {(platformId === 'amazon' || !platformId) && amazonTab === 'delivery' && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Scrape engine</label>
                          <span className="text-[10px] text-gray-400">
                            {amazonEngine === "fast" ? "~5× faster · parallel pincodes" : "Reliable · full PDP render"}
                          </span>
                        </div>
                        <div className="inline-flex w-full rounded-lg border border-gray-200 bg-gray-50 p-1" data-testid="amazon-engine-toggle">
                          <button
                            type="button"
                            onClick={() => setAmazonEngine("legacy")}
                            className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                              amazonEngine === "legacy"
                                ? "bg-white text-[#32B4AC] shadow-sm border border-[#32B4AC]/20"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            data-testid="amazon-engine-legacy"
                          >
                            Legacy
                          </button>
                          <button
                            type="button"
                            onClick={() => setAmazonEngine("fast")}
                            className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                              amazonEngine === "fast"
                                ? "bg-white text-[#32B4AC] shadow-sm border border-[#32B4AC]/20"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            data-testid="amazon-engine-fast"
                          >
                            Fast <span className="text-[9px] font-medium text-amber-500 ml-1">BETA</span>
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
                          {amazonEngine === "fast"
                            ? "Pincode-sharded parallel PDP render. ~5× faster — runs multiple pincodes side-by-side using the same proven extractor. Skips product/price (auto-filled from Control Panel)."
                            : "Sequential PDP load + DOM render per pincode. Highest reliability, captures all fields directly from Amazon."}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Pincodes</label>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setPincodePickerOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded-md hover:bg-[#E0F7F4] hover:border-[#32B4AC]/50 transition-colors font-medium" data-testid="pick-from-pincodes-btn">
                        <MapPin className="w-3 h-3" /> Pincodes
                      </button>
                      <button onClick={() => downloadSampleCSV('pincode')} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors">
                        <Download className="w-3 h-3" /> Sample
                      </button>
                      <input type="file" ref={fileInputPincodeRef} onChange={(e) => handleFileUpload(e, 'pincode')} accept=".csv,.txt" className="hidden" />
                      <button onClick={() => fileInputPincodeRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[#32B4AC] rounded-md hover:bg-[#2a9d96] transition-colors">
                        <Upload className="w-3 h-3" /> Upload
                      </button>
                    </div>
                  </div>
                  <Input
                    value={pincodes}
                    onChange={(e) => setPincodes(e.target.value)}
                    placeholder="400001, 110001"
                    className="input-clean font-mono text-xs placeholder:text-gray-300"
                    data-testid="pincode-input"
                  />

                  {/* ─── Pan-India Batch Runner — futuristic visual cue (Offline only) ─── */}
                  {isOffline && (
                    <div className="mt-3 batch-runner-frame" data-testid="offline-batch-section">
                      <div className="batch-runner-glow" aria-hidden />
                      <div className="batch-runner-grid" aria-hidden />
                      <div className="relative z-10">
                        <label
                          className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer select-none"
                          data-testid="offline-batch-toggle"
                        >
                          <div className="relative shrink-0 mt-0.5">
                            <input
                              type="checkbox"
                              checked={offlineBatchMode}
                              onChange={(e) => setOfflineBatchMode(e.target.checked)}
                              className="peer h-4 w-4 appearance-none rounded border-2 border-cyan-400/60 bg-slate-900/40 checked:bg-cyan-400 checked:border-cyan-300 cursor-pointer transition-colors"
                              data-testid="offline-batch-checkbox"
                            />
                            <svg
                              className="absolute inset-0 m-auto w-3 h-3 text-slate-900 opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                              viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            >
                              <polyline points="2.5 6.5 5 9 9.5 3.5" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Layers className="w-3.5 h-3.5 text-cyan-300" />
                              <span className="text-[11px] font-bold uppercase tracking-[0.12em] bg-gradient-to-r from-cyan-200 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
                                Pan-India Batch Mode
                              </span>
                              {offlineBatchMode && (
                                <span className="batch-runner-dot" aria-hidden />
                              )}
                            </div>
                            <div className="text-[10px] text-cyan-100/70 leading-snug mt-1">
                              Split a huge pincode list into chunks — each batch runs independently &amp; can be downloaded separately. Resilient: failures don't break the queue.
                            </div>
                          </div>
                        </label>
                        {offlineBatchMode && (
                          <div className="px-3 pb-3 pt-1 flex items-center gap-3 flex-wrap" data-testid="offline-batch-size-row">
                            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-900/40 border border-cyan-400/30 backdrop-blur-sm">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-cyan-200/90 whitespace-nowrap">Batch size</label>
                              <input
                                type="number"
                                min={1}
                                max={2000}
                                value={offlineBatchSize}
                                onChange={(e) => {
                                  const n = parseInt(e.target.value || "0", 10);
                                  setOfflineBatchSize(Number.isFinite(n) ? Math.max(1, Math.min(n, 2000)) : 100);
                                }}
                                className="h-6 w-16 px-1.5 text-xs font-mono text-center text-cyan-50 bg-slate-950/60 border border-cyan-400/40 rounded focus:outline-none focus:border-cyan-300 focus:ring-1 focus:ring-cyan-300/40"
                                data-testid="offline-batch-size-input"
                              />
                              <span className="text-[10px] text-cyan-300/80">pins</span>
                            </div>
                            <div className="text-[10px] text-cyan-100/90 font-mono">
                              {(() => {
                                const total = pincodes.split(/[,\n]/).map(s => s.trim()).filter(Boolean).length;
                                if (!total) return <span className="text-cyan-300/40">→ awaiting pincodes…</span>;
                                const n = Math.max(1, Math.ceil(total / Math.max(1, offlineBatchSize)));
                                return (
                                  <>
                                    → <span className="text-cyan-200 font-bold">{n}</span> batch{n !== 1 ? "es" : ""} queued from <span className="text-cyan-200 font-bold">{total}</span> pincodes
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Button onClick={handleScrape} disabled={isLoading} className="w-full btn-accent py-4 text-base font-semibold" data-testid="start-scraping-btn">
              {isLoading
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Extracting...</>
                : (isOffline && offlineBatchMode
                    ? <><Layers className="w-5 h-5 mr-2" /> Queue Batch Run</>
                    : <><Play className="w-5 h-5 mr-2" /> Start Extraction</>)}
            </Button>
          </div>
          
          {/* Right Panel */}
          <div className="lg:col-span-8 space-y-4">
            {/* Live Activity Ticker */}
            <ActivityTicker logs={terminalLogs} isLoading={isLoading} />
            {(isLoading || terminalLogs.length > 0) && (
              <div className="progress-terminal">
                <div className="progress-terminal-header">
                  <div className="terminal-dot red" />
                  <div className="terminal-dot yellow" />
                  <div className="terminal-dot green" />
                  <span className="text-gray-500 text-xs ml-2">horizon-extractor</span>
                  {endTime && startTime && (
                    <span className="ml-auto text-[#32B4AC] text-xs font-mono">
                      <Timer className="w-3 h-3 inline mr-1" />{formatDuration(endTime - startTime)}
                    </span>
                  )}
                </div>
                
                {isLoading && (
                  <div className="mb-4">
                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-[#32B4AC] transition-all duration-300 progress-futuristic animate-data-flow" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">{Math.round(progress)}%</div>
                  </div>
                )}
                
                <div className="space-y-1 max-h-[120px] overflow-y-auto">
                  {terminalLogs.map((log, i) => (
                    <div key={i} className="terminal-line">
                      <span className="text-gray-600">{log.time}</span>
                      <span className={log.type === 'success' ? 'success' : log.type === 'error' ? 'error' : log.type === 'processing' ? 'processing' : 'text-gray-400'}>
                        {log.message}
                      </span>
                    </div>
                  ))}
                  {isLoading && <div className="terminal-line"><span className="processing">█</span></div>}
                </div>
              </div>
            )}

            {/* Live "now watching" banner — only when user has opened a batch child for live preview */}
            {watchingMeta && (
              <div
                className="batch-runner-frame mb-3 px-4 py-3 flex items-center gap-3"
                data-testid="now-watching-banner"
              >
                <div className="batch-runner-glow" aria-hidden />
                <div className="batch-runner-grid" aria-hidden />
                <div className="relative z-10 flex items-center gap-3 flex-1">
                  <span className="batch-runner-dot" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-200/80">Live Preview</div>
                    <div className="text-xs text-cyan-50 font-mono truncate">
                      <span className="text-cyan-200">Batch #{watchingMeta.batch_index}/{watchingMeta.batch_total}</span>
                      <span className="text-cyan-400/50"> · </span>
                      <span className="text-cyan-100/90">{watchingMeta.batch_name}</span>
                      <span className="text-cyan-400/50"> · </span>
                      <span className="text-cyan-300/70">{watchingMeta.job_id}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setWatchingJobId(null); setWatchingMeta(null); }}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-cyan-400/40 text-cyan-200 bg-slate-900/40 hover:bg-slate-900/60"
                    data-testid="now-watching-close"
                  >
                    <X className="w-3 h-3" /> Stop watching
                  </button>
                </div>
              </div>
            )}

            {stats && (
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-5 py-3 border border-slate-100">
                <div className="flex items-center gap-5 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      <Database className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-900 tabular-nums leading-none">{stats.total}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">Expected</div>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-emerald-600 tabular-nums leading-none">{stats.success}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">Success</div>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-red-500 tabular-nums leading-none">{stats.failed || 0}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">Failed</div>
                    </div>
                  </div>
                  {(stats.missing || 0) > 0 && (
                    <>
                      <div className="w-px h-8 bg-slate-200" />
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <div className="text-lg font-bold text-amber-500 tabular-nums leading-none">{stats.missing}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Missing</div>
                        </div>
                      </div>
                    </>
                  )}
                  {(stats.skipped || 0) > 0 && (
                    <>
                      <div className="w-px h-8 bg-slate-200" />
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <div className="text-lg font-bold text-amber-500 tabular-nums leading-none">{stats.skipped}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Skipped</div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="w-px h-8 bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#E8F8F5] border border-[#32B4AC]/20 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-[#32B4AC]" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-[#32B4AC] tabular-nums leading-none">{endTime && startTime ? formatDuration(endTime - startTime) : '-'}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">Duration</div>
                    </div>
                  </div>
                </div>
                {results.length > 0 && (
                  <Button onClick={() => handleExport('csv')} className="btn-accent text-xs gap-1.5 shrink-0" data-testid="export-csv-btn">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </Button>
                )}
              </div>
            )}

            {results.length > 0 && (
              <>
                <div className="text-[10px] text-gray-400 -mt-2 ml-1">Export respects column & status filters</div>
                <div className="bg-blue-50/60 border border-blue-200/50 rounded-lg px-4 py-2 flex items-center gap-2.5 -mt-1">
                  <Wifi className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <div className="text-[11px] text-blue-700">
                    <span className="font-semibold">Connection drops?</span> Your job keeps running on the server and results are saved in real-time. Just refresh the page and check Job Queue — nothing is lost.
                  </div>
                </div>
              </>
            )}

            <div className="dashboard-card" data-testid="results-card">
              <div className="dashboard-card-header flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-gray-700">Results</span>
                {results.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* Status filter pills */}
                    <div className="flex items-center gap-1" data-testid="status-filter">
                      {['all', 'success', 'error', 'skipped'].map(s => (
                        <button
                          key={s}
                          onClick={() => setStatusFilter(s)}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            statusFilter === s
                              ? s === 'success' ? 'bg-green-50 border-green-300 text-green-700'
                                : s === 'error' ? 'bg-red-50 border-red-300 text-red-700'
                                : s === 'skipped' ? 'bg-amber-50 border-amber-300 text-amber-700'
                                : 'bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC]'
                              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                          data-testid={`status-filter-${s}`}
                        >
                          {s === 'all' ? `All (${results.length})` : s === 'success' ? `Success (${results.filter(r=>r.status==='success').length})` : s === 'skipped' ? `Skipped (${results.filter(r=>r.status==='skipped').length})` : `Failed (${results.filter(r=>r.status==='error').length})`}
                        </button>
                      ))}
                    </div>

                    {/* Column visibility popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 border-gray-200" data-testid="column-filter-btn">
                          <SlidersHorizontal className="w-3.5 h-3.5" />
                          Columns ({selectedColumns.length}/{activeColumns.length})
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-52 p-2" align="end">
                        <div className="text-xs font-medium text-gray-500 px-2 pb-1.5 border-b border-gray-100 mb-1">Toggle columns</div>
                        {activeColumns.map(col => (
                          <label key={col.id} className="flex items-center gap-2 text-xs cursor-pointer px-2 py-1.5 rounded hover:bg-gray-50" data-testid={`col-toggle-${col.id}`}>
                            <Checkbox
                              checked={selectedColumns.includes(col.id)}
                              onCheckedChange={() => handleColumnToggle(col.id)}
                              className="data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC] h-3.5 w-3.5"
                            />
                            <span className="text-gray-700">{col.label}</span>
                          </label>
                        ))}
                        <div className="border-t border-gray-100 mt-1 pt-1 px-2 flex gap-2">
                          <button onClick={() => setSelectedColumns(activeColumns.map(c=>c.id))} className="text-[10px] text-[#32B4AC] hover:underline" data-testid="col-select-all">Select all</button>
                          <button onClick={() => setSelectedColumns(['asin','pincode'])} className="text-[10px] text-gray-400 hover:underline" data-testid="col-select-minimal">Minimal</button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <span className="text-xs font-mono text-[#32B4AC]">{filteredResults.length}{filteredResults.length !== results.length ? `/${results.length}` : ''} records</span>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => { setColumnFilters({}); setStatusFilter('all'); }}
                        className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                        data-testid="clear-all-filters"
                      >
                        <X className="w-3 h-3" /> Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>
              {results.length === 0 ? (
                <div className="py-10 px-6">
                  <div className="max-w-md mx-auto text-center">
                    <div className="w-16 h-16 bg-[#E8F8F5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Database className="w-8 h-8 text-[#32B4AC]" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {isOffline ? "Ready to find stores" : "Ready to extract data"}
                    </h3>
                    <p className="text-sm text-gray-400 mb-6">
                      {isOffline ? "Enter keywords and pincodes, then hit Start Extraction" : isBlinkit ? "Select products and pincodes, then hit Start Extraction" : `Enter ${asinLabel}s and pincodes, then hit Start Extraction`}
                    </p>

                    {/* How it works steps */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {(isOffline ? [
                        { step: '1', icon: Search, label: 'Add Keywords', desc: 'One per line or CSV' },
                        { step: '2', icon: MapPin, label: 'Set Pincodes', desc: 'Comma separated' },
                        { step: '3', icon: FileSpreadsheet, label: 'Get Results', desc: 'Export CSV' },
                      ] : [
                        { step: '1', icon: Package, label: `Add ${asinLabel}s`, desc: 'One per line or CSV' },
                        { step: '2', icon: MapPin, label: 'Set Pincodes', desc: 'Comma separated' },
                        { step: '3', icon: FileSpreadsheet, label: 'Get Results', desc: 'Export CSV' },
                      ]).map((s) => (
                        <div key={s.step} className="relative">
                          <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center mx-auto mb-2">
                            <s.icon className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="text-xs font-semibold text-gray-700">{s.label}</div>
                          <div className="text-[11px] text-gray-400">{s.desc}</div>
                        </div>
                      ))}
                    </div>

                    {/* Sample table preview */}
                    <div className="bg-gray-50/80 rounded-lg border border-dashed border-gray-200 p-3">
                      <div className="grid grid-cols-4 gap-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">
                        {(isOffline ? ['Keyword', 'Pincode', 'Store', 'Phone'] : [asinLabel, 'Pincode', 'Price', 'Status']).map(h => (
                          <div key={h}>{h}</div>
                        ))}
                      </div>
                      {[1,2,3].map(i => (
                        <div key={i} className="grid grid-cols-4 gap-2 px-1 py-1.5">
                          {[1,2,3,4].map(j => (
                            <div key={j} className="h-2.5 bg-gray-200/60 rounded-full" style={{width: `${50 + Math.random()*40}%`}} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-table-wrapper" data-testid="results-table-wrapper">
                  <Table className="data-table results-frozen-table" data-testid="results-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky-col sticky-col-0 z-20 w-10 bg-[#F8FAFC] text-center">
                          <span className="text-[9px]">St</span>
                        </TableHead>
                        {selectedColumns.filter(colId => activeColumns.some(c => c.id === colId)).map((colId, ci) => {
                          const col = activeColumns.find(c => c.id === colId);
                          const uniqueVals = [...new Set(results.map(r => r[colId] || '-'))].sort();
                          const activeFilter = columnFilters[colId];
                          const isFiltered = activeFilter && activeFilter.size > 0;
                          const isFrozen = isOffline ? (colId === 'keyword' || colId === 'pincode') : (colId === 'asin' || colId === 'pincode');
                          const frozenClass = isFrozen ? `sticky-col sticky-col-${colId} z-20 bg-[#F8FAFC]` : '';
                          return (
                            <TableHead key={colId} className={`${frozenClass} whitespace-nowrap`}>
                              <Popover onOpenChange={(open) => { if (open) setFilterSearch(''); }}>
                                <PopoverTrigger asChild>
                                  <button className="flex items-center gap-1 w-full text-left group" data-testid={`filter-${colId}`}>
                                    <span>{col?.label}</span>
                                    <Filter className={`w-3 h-3 flex-shrink-0 transition-colors ${isFiltered ? 'text-[#32B4AC]' : 'text-gray-300 group-hover:text-gray-500'}`} />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-0" align="start">
                                  <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                      <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                      <input
                                        type="text"
                                        placeholder={`Search ${col?.label}...`}
                                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-[#32B4AC]"
                                        value={filterSearch}
                                        onChange={e => setFilterSearch(e.target.value)}
                                        data-testid={`filter-search-${colId}`}
                                      />
                                    </div>
                                  </div>
                                  <div className="px-2 py-1 border-b border-gray-100 flex items-center justify-between">
                                    <button
                                      className="text-[10px] text-[#32B4AC] hover:underline"
                                      onClick={() => setColumnFilters(prev => { const n = {...prev}; delete n[colId]; return n; })}
                                      data-testid={`filter-clear-${colId}`}
                                    >Clear filter</button>
                                    <span className="text-[10px] text-gray-400">{uniqueVals.length} values</span>
                                  </div>
                                  <div className="max-h-[200px] overflow-y-auto p-1">
                                    {uniqueVals
                                      .filter(v => !filterSearch || v.toLowerCase().includes(filterSearch.toLowerCase()))
                                      .map(val => {
                                        const checked = !isFiltered || activeFilter.has(val);
                                        return (
                                          <label key={val} className="flex items-center gap-2 text-xs cursor-pointer px-2 py-1 rounded hover:bg-gray-50" data-testid={`filter-val-${colId}-${val}`}>
                                            <Checkbox
                                              checked={checked}
                                              onCheckedChange={() => {
                                                setColumnFilters(prev => {
                                                  const current = prev[colId] ? new Set(prev[colId]) : null;
                                                  if (!current) {
                                                    return { ...prev, [colId]: new Set([val]) };
                                                  }
                                                  const next = new Set(current);
                                                  if (next.has(val)) {
                                                    next.delete(val);
                                                  } else {
                                                    next.add(val);
                                                  }
                                                  if (next.size === 0 || next.size === uniqueVals.length) {
                                                    const n = {...prev}; delete n[colId]; return n;
                                                  }
                                                  return { ...prev, [colId]: next };
                                                });
                                              }}
                                              className="data-[state=checked]:bg-[#32B4AC] data-[state=checked]:border-[#32B4AC] h-3.5 w-3.5"
                                            />
                                            <span className="text-gray-700 truncate" title={val}>{val}</span>
                                          </label>
                                        );
                                      })}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredResults.map((result, idx) => (
                        <TableRow key={idx} data-testid={`result-row-${idx}`}>
                          <TableCell className="sticky-col sticky-col-0 bg-white w-10 text-center">
                            {result.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> :
                             result.status === 'skipped' ? <AlertCircle className="w-3.5 h-3.5 text-amber-400 mx-auto" /> :
                             result.status === 'error' ? <XCircle className="w-3.5 h-3.5 text-red-400 mx-auto" /> :
                             <AlertCircle className="w-3.5 h-3.5 text-gray-300 mx-auto" />}
                          </TableCell>
                          {selectedColumns.filter(colId => activeColumns.some(c => c.id === colId)).map(colId => {
                            const isFrozen = isOffline ? (colId === 'keyword' || colId === 'pincode') : (colId === 'asin' || colId === 'pincode');
                            const frozenClass = isFrozen ? `sticky-col sticky-col-${colId} bg-white` : '';
                            const val = result[colId];

                            // Smart cell rendering per column type
                            let cellContent;
                            if (colId === 'asin' && val) {
                              const isAmzAsin = /^B[A-Z0-9]{9}$/.test(val);
                              const blinkitUrl = result.url && result.url.includes('blinkit.com') ? result.url : null;
                              const linkUrl = blinkitUrl || (isAmzAsin ? `https://www.amazon.in/dp/${val}` : `https://www.flipkart.com/product/p/itme?pid=${val}`);
                              cellContent = (
                                <a href={linkUrl}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-[11px] text-[#32B4AC] hover:underline truncate block max-w-[160px]"
                                  title={val} data-testid={`asin-link-${val}`}>{val}</a>
                              );
                            } else if (colId === 'pincode' && val) {
                              cellContent = <span className="font-mono text-[11px] text-slate-600 tabular-nums">{val}</span>;
                            } else if (colId === 'price' && val) {
                              cellContent = <span className="font-semibold text-slate-800 tabular-nums">{val}</span>;
                            } else if ((colId === 'delivery_standard' || colId === 'delivery_fast') && val) {
                              const days = parseInt(val);
                              const color = days === 0 ? 'text-emerald-600 bg-emerald-50' : days <= 2 ? 'text-blue-600 bg-blue-50' : days <= 5 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
                              cellContent = (
                                <span className={`inline-flex items-center justify-center text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-md ${color}`}>
                                  {days === 0 ? 'Today' : `${val}d`}
                                </span>
                              );
                            } else if (colId === 'status') {
                              const st = (val || '').toLowerCase();
                              cellContent = (
                                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md ${
                                  st === 'success' ? 'text-emerald-700 bg-emerald-50' : st === 'skipped' ? 'text-amber-600 bg-amber-50' : st === 'error' ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-50'
                                }`}>{val || '-'}</span>
                              );
                            } else if (colId === 'scraped_at' && val) {
                              cellContent = <span className="text-[10px] text-gray-400 tabular-nums whitespace-nowrap">{val}</span>;
                            } else if (colId === 'error' && val) {
                              cellContent = <span className="text-[11px] text-red-500 italic truncate block max-w-[180px]" title={val}>{val}</span>;
                            } else if (colId === 'product_name' && val) {
                              cellContent = <span className="text-[12px] font-medium text-gray-800 truncate block max-w-[200px]" title={val}>{val}</span>;
                            } else if (colId === 'brand_name' && val) {
                              cellContent = <span className="text-[11px] text-gray-500 truncate block max-w-[120px]" title={val}>{val}</span>;
                            } else if (colId === 'seller_name' && val) {
                              cellContent = <span className="text-[11px] text-indigo-600 truncate block max-w-[140px]" title={val}>{val}</span>;
                            } else {
                              cellContent = <span className="truncate block max-w-[140px] text-[12px]" title={val || '-'}>{val || '-'}</span>;
                            }

                            return (
                              <TableCell key={colId} className={frozenClass}>
                                {cellContent}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Job Queue Drawer */}
      <div className={`fixed top-0 right-0 h-full w-[380px] bg-white border-l border-gray-200 shadow-2xl z-50 transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`} data-testid="job-drawer">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#E8F8F5] flex items-center justify-center">
                <Activity className="w-4 h-4 text-[#32B4AC]" />
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900 block">Jobs</span>
                <span className="text-[10px] text-gray-400">
                  {allUserJobs.length}{jobsTotal > allUserJobs.length ? ` of ${jobsTotal}` : ''} shown
                  {allUserJobs.filter(j => j.status === 'processing').length > 0 && ` · ${allUserJobs.filter(j => j.status === 'processing').length} running`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Sound Preview & Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Sound alerts" data-testid="sound-preview-btn">
                    <Bell className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="end">
                  <div className="text-xs font-semibold text-gray-700 mb-1">Completion Sound</div>
                  <div className="text-[10px] text-gray-400 mb-3">Click any preset to preview. The dot marks your current pick.</div>
                  <div className="space-y-1 max-h-72 overflow-y-auto -mr-1 pr-1" data-testid="sound-preset-list">
                    {SOUND_PRESETS.map(preset => {
                      const isActive = activeSoundId === preset.id;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActiveSoundId(preset.id);
                            try { localStorage.setItem(SOUND_STORAGE_KEY, preset.id); } catch {}
                            preset.play();
                          }}
                          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors group ${isActive ? 'bg-[#E8F8F5] border border-[#32B4AC]/30' : 'hover:bg-gray-50 border border-transparent'}`}
                          data-testid={`sound-preset-${preset.id}`}
                        >
                          <div className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-[#32B4AC]' : 'bg-gray-200 group-hover:bg-gray-300'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-medium ${isActive ? 'text-[#268f88]' : 'text-gray-800'}`}>{preset.label}</div>
                            <div className="text-[9px] text-gray-400 truncate">{preset.desc}</div>
                          </div>
                          <span className="text-[9px] text-gray-400 group-hover:text-gray-600">
                            {isActive ? 'active' : 'preview'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-testid="close-drawer-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Admin-only view-mode toggle */}
          {isAdmin && (
            <div className="px-4 pt-3 pb-0" data-testid="jobs-view-toggle">
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setJobsViewMode('mine')}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors ${jobsViewMode === 'mine' ? 'bg-white text-[#32B4AC] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="jobs-view-mine"
                >
                  My Jobs
                </button>
                <button
                  onClick={() => setJobsViewMode('all')}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors flex items-center justify-center gap-1 ${jobsViewMode === 'all' ? 'bg-white text-[#32B4AC] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="jobs-view-all"
                >
                  <Users className="w-3 h-3" /> All Users
                </button>
              </div>
            </div>
          )}

          {/* Batch Runs panel — sits above the regular Jobs list. Only renders when
              there ARE batch runs (most users will never see this). */}
          {batchRuns.length > 0 && (
            <div className="px-4 pt-4 pb-1 border-b border-gray-100" data-testid="batch-runs-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-[#32B4AC]" /> Batch Runs
                </span>
                <span className="text-[10px] text-gray-400">{batchRuns.length}</span>
              </div>
              <div className="space-y-2">
                {batchRuns.slice(0, 5).map(br => {
                  const isExpanded = expandedBatch === br.batch_id;
                  const total = br.total_batches || 0;
                  const done = br.completed_batches || 0;
                  const failed = br.failed_batches || 0;
                  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                  const isActive = br.status === 'running' || br.status === 'queued';
                  const statusBadge = (() => {
                    const map = {
                      queued:    { cls: 'bg-gray-100 text-gray-600',    label: 'Queued' },
                      running:   { cls: 'bg-[#E8F8F5] text-[#1d8a82]',  label: 'Running' },
                      completed: { cls: 'bg-green-50 text-green-700',   label: 'Done' },
                      stopped:   { cls: 'bg-amber-50 text-amber-700',   label: 'Stopped' },
                      failed:    { cls: 'bg-red-50 text-red-600',       label: 'Failed' },
                    };
                    return map[br.status] || map.queued;
                  })();
                  const children = batchChildrenById[br.batch_id] || [];
                  return (
                    <div key={br.batch_id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`batch-run-${br.batch_id}`}>
                      <div className="flex items-stretch">
                        <button
                          onClick={() => setExpandedBatch(isExpanded ? null : br.batch_id)}
                          className="flex-1 flex items-start gap-2 p-2.5 hover:bg-gray-50 transition-colors text-left min-w-0"
                          data-testid={`batch-run-toggle-${br.batch_id}`}
                        >
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[12px] font-semibold text-gray-800 truncate">{br.name || br.batch_id}</span>
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${statusBadge.cls}`}>{statusBadge.label}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                              <span>{done}/{total} batches</span>
                              {failed > 0 && <span className="text-red-500">· {failed} failed</span>}
                              <span className="text-gray-300">·</span>
                              <span>{br.batch_size} pins/batch</span>
                            </div>
                            {total > 0 && (
                              <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                                <div
                                  className={`h-full transition-all duration-300 ${isActive ? 'bg-[#32B4AC]' : br.status === 'completed' ? 'bg-green-400' : 'bg-gray-400'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                            {br.progress_message && (
                              <div className="text-[10px] text-gray-400 truncate mt-1" title={br.progress_message}>{br.progress_message}</div>
                            )}
                          </div>
                        </button>
                        {/* One-click Watch button always visible on collapsed/expanded row */}
                        <button
                          onClick={async () => {
                            // Need fresh children to know which child to target — fetch on demand if we don't have them yet.
                            let list = batchChildrenById[br.batch_id];
                            if (!list || !list.length) {
                              try {
                                const r = await axios.get(`${API}/batch-runs/${br.batch_id}`);
                                list = r.data?.children || [];
                                setBatchChildrenById(prev => ({ ...prev, [br.batch_id]: list }));
                              } catch { /* noop */ }
                            }
                            const running = (list || []).find(c => c.status === 'processing');
                            const target = running
                              || (list || []).find(c => c.status === 'queued')
                              || [...(list || [])].reverse().find(c => c.status === 'completed' || c.status === 'stopped' || c.status === 'failed');
                            if (!target) { toast.error('No batches to watch yet'); return; }
                            startWatchingBatchChild(br, target);
                          }}
                          className={`px-2.5 flex items-center justify-center gap-1 text-[10px] font-semibold border-l ${isActive ? 'bg-gradient-to-b from-cyan-50 to-cyan-100/50 text-cyan-700 border-cyan-200 hover:bg-cyan-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'} transition-colors shrink-0`}
                          title={isActive ? 'Watch this batch run live' : 'View latest batch snapshot'}
                          data-testid={`batch-run-watch-quick-${br.batch_id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {isActive && <span className="batch-runner-dot" style={{width:'5px',height:'5px'}} aria-hidden />}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50/40 px-2.5 py-2 space-y-1.5" data-testid={`batch-run-children-${br.batch_id}`}>
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            {/* Watch live → targets the currently-running child (or last completed one if run is done) */}
                            <button
                              onClick={() => {
                                const list = children;
                                const running = list.find(c => c.status === 'processing');
                                const target = running
                                  || list.find(c => c.status === 'queued')
                                  || [...list].reverse().find(c => c.status === 'completed' || c.status === 'stopped' || c.status === 'failed');
                                if (!target) { toast.error('No batches to watch yet'); return; }
                                startWatchingBatchChild(br, target);
                              }}
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-cyan-400/40 text-cyan-600 bg-cyan-50/60 hover:bg-cyan-50 hover:border-cyan-400/60 transition-colors"
                              data-testid={`batch-run-watch-${br.batch_id}`}
                              title="Open live preview of the running batch"
                            >
                              <Eye className="w-3 h-3" />
                              <span>Watch {isActive ? 'live' : 'snapshot'}</span>
                              {isActive && <span className="batch-runner-dot ml-0.5" style={{width:'5px', height:'5px'}} aria-hidden />}
                            </button>
                            <button
                              onClick={async () => {
                                if (done <= 0) { toast.error('No completed batches yet'); return; }
                                try {
                                  const r = await axios.get(`${API}/batch-runs/${br.batch_id}/export`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(r.data);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `batch_${br.batch_id.slice(-8)}.csv`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Merged export failed');
                                }
                              }}
                              className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border ${done > 0 ? 'border-[#32B4AC]/30 text-[#32B4AC] hover:bg-[#F0FDFA]' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
                              data-testid={`batch-run-export-${br.batch_id}`}
                              disabled={done <= 0}
                            >
                              <Download className="w-3 h-3" /> Merged CSV
                            </button>
                            {isActive && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Stop batch run "${br.name || br.batch_id}"?`)) return;
                                  try {
                                    await axios.post(`${API}/batch-runs/${br.batch_id}/stop`);
                                    toast.success('Stop signal sent');
                                    fetchBatchRuns();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to stop');
                                  }
                                }}
                                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50"
                                data-testid={`batch-run-stop-${br.batch_id}`}
                              >
                                <X className="w-3 h-3" /> Stop run
                              </button>
                            )}
                          </div>
                          {children.length === 0 ? (
                            <div className="text-[10px] text-gray-400 px-1 py-2">Loading children…</div>
                          ) : children.map(c => {
                            const childStatus = c.status;
                            const childBadge = (() => {
                              const m = {
                                queued:     'bg-gray-100 text-gray-500',
                                processing: 'bg-[#E8F8F5] text-[#1d8a82]',
                                completed:  'bg-green-50 text-green-700',
                                stopped:    'bg-amber-50 text-amber-700',
                                failed:     'bg-red-50 text-red-600',
                              };
                              return m[childStatus] || m.queued;
                            })();
                            return (
                              <div key={c.job_id} className="flex items-center gap-2 bg-white border border-gray-100 rounded px-2 py-1.5" data-testid={`batch-child-${c.job_id}`}>
                                <span className="text-[10px] font-semibold text-gray-400 w-8 shrink-0">#{c.batch_index}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[11px] text-gray-700 truncate">{c.pincode_count} pin{c.pincode_count !== 1 ? 's' : ''} · {c.completed_count || 0}/{c.total || 0} combos</div>
                                  {c.progress_message && <div className="text-[9px] text-gray-400 truncate" title={c.progress_message}>{c.progress_message}</div>}
                                </div>
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${childBadge}`}>{childStatus}</span>
                                {/* Watch live (any status): for processing it streams, for completed it shows a snapshot */}
                                <button
                                  onClick={() => startWatchingBatchChild(br, c)}
                                  className={`p-1 ${childStatus === 'processing' ? 'text-cyan-500 hover:text-cyan-600' : 'text-gray-400 hover:text-cyan-500'}`}
                                  title={childStatus === 'processing' ? 'Watch this batch live' : 'View this batch\'s results'}
                                  data-testid={`batch-child-watch-${c.job_id}`}
                                >
                                  <Eye className="w-3 h-3" />
                                </button>
                                {(childStatus === 'completed' || childStatus === 'stopped') && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const r = await axios.get(`${API}/jobs/${c.job_id}`);
                                        const fresh = r.data;
                                        if (fresh.results?.length) handleExport('csv', fresh.results);
                                        else toast.error('No results in this batch');
                                      } catch {
                                        toast.error('Failed to fetch batch results');
                                      }
                                    }}
                                    className="text-gray-400 hover:text-[#32B4AC] p-1"
                                    title="Download this batch"
                                    data-testid={`batch-child-export-${c.job_id}`}
                                  >
                                    <Download className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Job List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {allUserJobs.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <Activity className="w-5 h-5 text-gray-300" />
                </div>
                <div className="text-sm text-gray-400">No jobs yet</div>
                <div className="text-[11px] text-gray-300 mt-0.5">Start an extraction to see jobs here</div>
              </div>
            ) : (
              (() => {
                // In 'all' mode, sort by user so consecutive user jobs stick together,
                // and inject a small user-group header the first time each user appears.
                const sorted = jobsViewMode === 'all'
                  ? [...allUserJobs].sort((a, b) => {
                      const ua = (a.user_email || '').toLowerCase();
                      const ub = (b.user_email || '').toLowerCase();
                      if (ua !== ub) return ua < ub ? -1 : 1;
                      return new Date(b.created_at) - new Date(a.created_at);
                    })
                  : allUserJobs;
                let lastUser = null;
                return sorted.map(job => {
                const userKey = job.user_email || job.user_id || '';
                const showGroupHeader = jobsViewMode === 'all' && userKey && userKey !== lastUser;
                lastUser = userKey;
                const userJobCount = jobsViewMode === 'all' ? sorted.filter(j => (j.user_email || j.user_id) === userKey).length : 0;
                const total = job.total || 0;
                // Always trust completed_count from server. Clamp to total so progress can never exceed 100%.
                // (Offline fast engine appends ~20 rows per combo, so falling back to results.length here
                //  conflated rows with combos and produced bogus 137%/200% readings.)
                const rawDone = typeof job.completed_count === 'number' ? job.completed_count : 0;
                const done = total > 0 ? Math.min(total, rawDone) : rawDone;
                const pct = total > 0 ? Math.min(100, Math.max(0, Math.round((done / total) * 100))) : 0;
                const isActive = job.status === 'processing';
                const successCount = (job.results || []).filter(r => r.status === 'success').length;
                const failedCount = done - successCount;
                const platformLabel =
                  job.platform === 'flipkart' ? 'Flipkart' :
                  job.platform === 'offline' ? 'Offline' :
                  job.platform === 'price-governance' ? 'Price Gov.' :
                  job.platform === 'amazon-bestseller' ? 'Market Research' :
                  'Amazon';
                // Per-platform "what was scraped" labelling — historical jobs all
                // used asins+pincodes, so the count had a single shape. Now Market
                // Research uses source_urls (no pincodes), Offline uses keywords,
                // Flipkart uses FSNs. We compute itemCount/itemLabel/scopeText
                // once here and drop the misleading "× N pincodes" tail for jobs
                // that have no pincodes.
                const isBestseller = job.platform === 'amazon-bestseller';
                const sourceUrlCount = (job.source_urls || []).length;
                let itemCount, itemLabel;
                if (isBestseller) {
                  itemCount = sourceUrlCount;
                  itemLabel = sourceUrlCount === 1 ? 'list' : 'lists';
                } else if (job.keywords?.length || job.keyword) {
                  itemCount = job.keywords?.length || 1;
                  itemLabel = 'keywords';
                } else if (job.platform === 'flipkart') {
                  itemCount = (job.asins || []).length;
                  itemLabel = 'FSNs';
                } else {
                  itemCount = (job.asins || []).length;
                  itemLabel = 'ASINs';
                }
                const pinCount = (job.pincodes || []).length;
                // Market Research uses N best-sellers as the "size", and skips the
                // pincode tail entirely. Other platforms keep "X items × Y pincodes".
                const productCap = job.max_products_per_url;
                const scopeText = isBestseller
                  ? `${sourceUrlCount} ${itemLabel}${productCap ? ` · top ${productCap}` : ''}`
                  : `${itemCount} ${itemLabel}${pinCount > 0 ? ` × ${pinCount} pincodes` : ''}`;

                return (
                  <React.Fragment key={job.job_id}>
                    {showGroupHeader && (
                      <div className="flex items-center gap-2 px-1 pt-2 pb-1" data-testid={`job-group-header-${userKey}`}>
                        {job.user_picture ? (
                          <img src={job.user_picture} alt="" className="w-5 h-5 rounded-full" />
                        ) : (
                          <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold flex items-center justify-center">{(job.user_name || job.user_email || '?')[0]?.toUpperCase()}</span>
                        )}
                        <span className="text-[11px] font-semibold text-gray-700">{job.user_name || job.user_email}</span>
                        <span className="text-[10px] text-gray-400">{job.user_email}</span>
                        <span className="ml-auto text-[10px] text-gray-400">{userJobCount} job{userJobCount !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  <div
                    className={`rounded-xl border overflow-hidden transition-all ${
                      isActive ? 'border-[#32B4AC]/50 shadow-sm' : job.status === 'stopped' ? 'border-amber-200' : job.status === 'failed' ? 'border-red-200' : 'border-gray-150'
                    }`}
                    data-testid={`drawer-job-${job.job_id}`}
                  >
                    {/* Job card header */}
                    <div className={`px-4 py-3 cursor-pointer transition-colors ${
                      isActive ? 'bg-[#F0FDFA]' : 'bg-white hover:bg-gray-50'
                    }`} onClick={() => { if (!isActive) openJobInTable(job); }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide ${
                            isActive ? 'bg-[#32B4AC] text-white' :
                            job.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            job.status === 'stopped' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {isActive ? 'Running' : job.status}
                          </span>
                          <span className="text-[10px] text-gray-400 font-medium">{platformLabel}</span>
                          {/* Engine chip — only meaningful for amazon delivery jobs */}
                          {job.platform === 'amazon' && job.scrape_engine && (
                            <span
                              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border uppercase tracking-wider ${
                                job.scrape_engine === 'fast'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200'
                              }`}
                              title={`Engine: ${job.scrape_engine}`}
                              data-testid={`job-engine-chip-${job.job_id}`}
                            >{job.scrape_engine === 'fast' ? 'Fast' : 'Legacy'}</span>
                          )}
                          {jobsViewMode === 'all' && job.user_name && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
                              title={job.user_email}
                              data-testid={`job-user-tag-${job.job_id}`}
                            >
                              {job.user_picture ? (
                                <img src={job.user_picture} alt="" className="w-3 h-3 rounded-full" />
                              ) : (
                                <span className="w-3 h-3 rounded-full bg-indigo-200 text-indigo-700 text-[7px] font-bold flex items-center justify-center">{(job.user_name || job.user_email || '?')[0]?.toUpperCase()}</span>
                              )}
                              <span className="truncate max-w-[80px]">{(job.user_name || job.user_email || '').split(' ')[0]}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              try {
                                navigator.clipboard?.writeText(job.job_id || '');
                                toast.success("Job ID copied");
                              } catch { /* noop */ }
                            }}
                            className="font-mono text-[9px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
                            title={`Job ID: ${job.job_id} (click to copy)`}
                            data-testid={`job-id-chip-${job.job_id}`}
                          >
                            #{(job.job_id || '').slice(-8)}
                          </button>
                          <span className="text-[10px] text-gray-400">{formatDate(job.created_at)}</span>
                        </div>
                      </div>

                      {/* Scope line */}
                      <div className="text-xs text-gray-700 font-medium mb-2">
                        {scopeText}
                        {getJobDuration(job) && <span className="text-gray-400 font-normal ml-2">{getJobDuration(job)}</span>}
                      </div>

                      {/* Progress */}
                      {isActive && (
                        <div className="mb-2">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#32B4AC] to-[#2CD4C4] rounded-full transition-all duration-700 ease-out" style={{ width: `${Math.max(pct, 2)}%` }} />
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[10px] text-gray-500 truncate max-w-[220px]">{job.progress_message || 'Starting...'}</span>
                            <span className="text-[10px] font-bold text-[#32B4AC] tabular-nums">{done}/{total} ({pct}%)</span>
                          </div>
                        </div>
                      )}

                      {/* Stats row for completed jobs */}
                      {!isActive && done > 0 && (
                        <div className="flex items-center gap-3 text-[10px] mb-1">
                          <span className="text-emerald-600 font-medium">{successCount} success</span>
                          {failedCount > 0 && <span className="text-red-500 font-medium">{failedCount} failed</span>}
                          <span className="text-gray-400">{done} total</span>
                        </div>
                      )}
                    </div>

                    {/* Action bar */}
                    <div className="px-4 py-2 bg-gray-50/80 border-t border-gray-100 flex items-center justify-end gap-2">
                      {isActive && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStopJob(job.job_id); }}
                          className="text-[11px] font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors border border-transparent hover:border-red-200"
                          data-testid={`stop-job-${job.job_id}`}
                        >
                          <Square className="w-3 h-3" /> Stop
                        </button>
                      )}
                      {(job.status === 'stopped' || job.status === 'failed') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleResumeJob(job.job_id); }}
                          className="text-[11px] font-medium text-[#32B4AC] hover:bg-[#F0FDFA] px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors border border-transparent hover:border-[#32B4AC]/30"
                          data-testid={`resume-job-${job.job_id}`}
                        >
                          <RotateCcw className="w-3 h-3" /> Resume
                        </button>
                      )}
                      {!isActive && (job.results?.length > 0 || job.status === 'completed') && (
                        <>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (job.results?.length) {
                                handleExport('csv', job.results);
                                return;
                              }
                              try {
                                const r = await axios.get(`${API}/jobs/${job.job_id}`);
                                const fresh = r.data;
                                if (fresh.results?.length) handleExport('csv', fresh.results);
                                else toast.error('No results to export');
                              } catch {
                                toast.error('Failed to fetch job');
                              }
                            }}
                            className="text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                            data-testid={`drawer-export-${job.job_id}`}
                          >
                            <Download className="w-3 h-3" /> Export
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openJobInTable(job); }}
                            className="text-[11px] font-medium text-[#32B4AC] hover:bg-[#F0FDFA] px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                            data-testid={`drawer-view-${job.job_id}`}
                          >
                            <Eye className="w-3 h-3" /> View
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  </React.Fragment>
                );
              });
              })()
            )}
            {/* Load more */}
            {allUserJobs.length > 0 && allUserJobs.length < jobsTotal && (
              <button
                onClick={loadMoreJobs}
                disabled={jobsLoadingMore}
                className="w-full py-2.5 mt-2 text-[11px] font-semibold text-[#32B4AC] bg-[#F0FDFA] hover:bg-[#E0F9F5] border border-[#32B4AC]/20 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="jobs-load-more-btn"
              >
                {jobsLoadingMore ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5" /> Load {Math.min(jobsPageSize, jobsTotal - allUserJobs.length)} more (of {jobsTotal - allUserJobs.length})</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Drawer backdrop */}
      {drawerOpen && <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setDrawerOpen(false)} />}

      {/* Floating Buttons */}
      <div className="fixed bottom-16 right-6 z-30 flex flex-col items-end gap-3">
        {/* Job Queue Toggle */}
        <button
          onClick={() => setDrawerOpen(!drawerOpen)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 relative ${
            allUserJobs.some(j => j.status === 'processing')
              ? 'bg-[#32B4AC] text-white shadow-[#32B4AC]/30'
              : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
          }`}
          title="Job Queue"
          data-testid="job-queue-btn"
        >
          <PanelRightOpen className="w-5 h-5" />
          {allUserJobs.some(j => j.status === 'processing') && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
              {allUserJobs.filter(j => j.status === 'processing').length}
            </span>
          )}
        </button>
      </div>

      {/* Footer Disclaimer */}
      <footer className="footer-disclaimer" data-testid="footer-disclaimer">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <p>HORIZON leverages intelligent automation and real-time data pipelines to deliver accurate marketplace insights at scale. Only publicly available information is processed.</p>
        </div>
      </footer>

      {/* Tutorial Overlay */}
      {showTutorial && (
        <TutorialOverlay
          onComplete={completeTutorial}
          onSkip={completeTutorial}
          asinLabel={asinLabel}
        />
      )}

      {/* Product Picker Modal */}
      {!isOffline && (
        <ProductPickerModal
          open={productPickerOpen}
          onOpenChange={setProductPickerOpen}
          platformId={platformId}
          onSelect={handleProductsPicked}
        />
      )}

      {/* Pincode Picker Modal */}
      <PincodePickerModal
        open={pincodePickerOpen}
        onOpenChange={setPincodePickerOpen}
        onSelect={handlePincodesPicked}
      />

      {/* Keyword Picker Modal (Offline) */}
      <KeywordPickerModal
        open={keywordPickerOpen}
        onOpenChange={setKeywordPickerOpen}
        onSelect={handleKeywordsPicked}
      />

      {/* Delivery Schedule Creation Modal */}
      <Dialog open={delivScheduleModalOpen} onOpenChange={setDelivScheduleModalOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="delivery-schedule-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="w-4 h-4 text-[#32B4AC]" /> Schedule Delivery Run
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Will run for <span className="font-semibold text-gray-800">{(asins || '').split(/[\n,]/).map(s=>s.trim()).filter(Boolean).length}</span> ASIN{(asins || '').split(/[\n,]/).map(s=>s.trim()).filter(Boolean).length !== 1 ? 's' : ''} × <span className="font-semibold text-gray-800">{(pincodes || '').split(/[,\n]/).map(s=>s.trim()).filter(Boolean).length}</span> pincode{(pincodes || '').split(/[,\n]/).map(s=>s.trim()).filter(Boolean).length !== 1 ? 's' : ''}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Name (optional)</label>
              <input
                value={delivScheduleForm.name}
                onChange={(e) => setDelivScheduleForm({ ...delivScheduleForm, name: e.target.value })}
                placeholder="e.g. Daily delivery check"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC]"
                data-testid="delivery-schedule-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Frequency</label>
              <div className="grid grid-cols-3 gap-2">
                {['hourly', 'daily', 'weekly'].map(freq => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setDelivScheduleForm({ ...delivScheduleForm, frequency: freq })}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize ${
                      delivScheduleForm.frequency === freq
                        ? 'bg-[#32B4AC] text-white border-[#32B4AC]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#32B4AC]/40'
                    }`}
                    data-testid={`delivery-schedule-freq-${freq}`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
            </div>
            {delivScheduleForm.frequency !== 'hourly' && (
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Time of day (IST)</label>
                <input
                  type="time"
                  value={delivScheduleForm.time_of_day}
                  onChange={(e) => setDelivScheduleForm({ ...delivScheduleForm, time_of_day: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] font-mono"
                  data-testid="delivery-schedule-time-input"
                />
              </div>
            )}
            {delivScheduleForm.frequency === 'weekly' && (
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1.5 block">Days of week</label>
                <div className="flex gap-1.5 flex-wrap">
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, idx) => {
                    const active = delivScheduleForm.days.includes(idx);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const days = active ? delivScheduleForm.days.filter(x => x !== idx) : [...delivScheduleForm.days, idx].sort();
                          setDelivScheduleForm({ ...delivScheduleForm, days });
                        }}
                        className={`w-11 h-9 text-xs font-semibold rounded-lg border transition-colors ${
                          active ? 'bg-[#32B4AC] text-white border-[#32B4AC]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#32B4AC]/40'
                        }`}
                        data-testid={`delivery-schedule-day-${idx}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {delivScheduleForm.frequency === 'hourly' && (
              <div className="text-[11px] text-gray-500 bg-[#F0FDFA] border border-[#32B4AC]/20 rounded-lg px-3 py-2">
                Will run at the top of every hour, starting next hour.
              </div>
            )}
            {/* Engine selector — defaults to Legacy (safe, reliable). Fast = parallel pincodes. */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Scrape engine</label>
              <div className="inline-flex w-full rounded-lg border border-gray-200 bg-gray-50 p-1" data-testid="delivery-schedule-engine-toggle">
                <button
                  type="button"
                  onClick={() => setDelivScheduleForm({ ...delivScheduleForm, engine: 'legacy' })}
                  className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                    (delivScheduleForm.engine || 'legacy') === 'legacy'
                      ? 'bg-white text-[#32B4AC] shadow-sm border border-[#32B4AC]/20'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="delivery-schedule-engine-legacy"
                >Legacy</button>
                <button
                  type="button"
                  onClick={() => setDelivScheduleForm({ ...delivScheduleForm, engine: 'fast' })}
                  className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                    delivScheduleForm.engine === 'fast'
                      ? 'bg-white text-[#32B4AC] shadow-sm border border-[#32B4AC]/20'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="delivery-schedule-engine-fast"
                >Fast <span className="text-[9px] font-medium text-amber-500 ml-1">BETA</span></button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
                {delivScheduleForm.engine === 'fast'
                  ? '~5× faster — runs multiple pincodes in parallel. Skips product/price (auto-filled from Control Panel).'
                  : 'Sequential PDP render per pincode. Highest reliability — recommended for unattended overnight runs.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDelivScheduleModalOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              data-testid="delivery-schedule-cancel-btn"
            >Cancel</button>
            <Button
              onClick={handleSaveDelivSchedule}
              className="btn-accent text-sm font-semibold gap-2"
              data-testid="delivery-schedule-save-btn"
            >
              <Save className="w-4 h-4" /> Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Schedule Runs Modal */}
      <Dialog open={delivRunsModal.open} onOpenChange={(o) => !o && setDelivRunsModal({ open: false, schedule: null, runs: [], loading: false })}>
        <DialogContent className="sm:max-w-[760px]" data-testid="delivery-runs-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-[#32B4AC]" />
              Run History — <span className="text-[#32B4AC]">{delivRunsModal.schedule?.name || ''}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {delivRunsModal.loading ? (
              <div className="py-12 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading runs...
              </div>
            ) : delivRunsModal.runs.length === 0 ? (
              <div className="py-12 text-center" data-testid="delivery-runs-empty">
                <CalendarClock className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No runs yet</p>
                <p className="text-[11px] text-gray-400 mt-1">Next run: <span className="font-medium text-gray-600">{formatNextRunDeliv(delivRunsModal.schedule?.next_run)}</span></p>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 overflow-hidden max-h-[460px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr className="text-left text-gray-500">
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Started</th>
                      <th className="px-3 py-2 font-medium">Results</th>
                      <th className="px-3 py-2 font-medium">Progress</th>
                      <th className="px-3 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delivRunsModal.runs.map((run, i) => {
                      const statusBadge = run.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : run.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                        : run.status === 'processing' ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse'
                        : 'bg-gray-50 text-gray-700 border-gray-200';
                      const startedAt = run.created_at ? new Date(run.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—';
                      return (
                        <tr key={run.job_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`delivery-run-row-${i}`}>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${statusBadge}`}>{run.status}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-700 tabular-nums whitespace-nowrap">{startedAt}</td>
                          <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                            <span className="text-emerald-700 font-semibold">{run.success_count || 0}</span>
                            <span className="text-gray-400"> / {run.result_count || run.total || 0}</span>
                            {run.error_count > 0 && <span className="ml-2 text-red-600 font-semibold">{run.error_count} err</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-[280px] truncate" title={run.progress_message || ''}>{run.progress_message || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleLoadDelivRunIntoTable(run.job_id)}
                              disabled={!run.result_count}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-[#32B4AC] bg-[#F0FDFA] border border-[#32B4AC]/30 rounded hover:bg-[#E0F9F5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`delivery-run-load-${i}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setDelivRunsModal({ open: false, schedule: null, runs: [], loading: false })}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              data-testid="delivery-runs-close-btn"
            >Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ControlPanelWrapper = ({ user }) => {
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    } catch {
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    }
  };
  return <ControlPanel user={user} onNavigate={(path) => navigate(path)} onLogout={handleLogout} />;
};

const AnalyticsWrapper = ({ user }) => {
  const navigate = useNavigate();
  // Track selected sub-module here so we can reflect it in the breadcrumb.
  // AnalyticsChooser is now a controlled child.
  const [module, setModule] = useState(null);
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    } catch {
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    }
  };
  const MODULE_META = {
    delivery: { label: 'Delivery Analytics', icon: <BarChart3 className="w-3.5 h-3.5" /> },
    market:   { label: 'Market Research',    icon: <TrendingUp className="w-3.5 h-3.5" /> },
    trends:   { label: 'Search Trends',      icon: <LineChart className="w-3.5 h-3.5" /> },
    offline:  { label: 'Offline Intelligence', icon: <MapPin className="w-3.5 h-3.5" /> },
  };
  const subCrumb = module && MODULE_META[module]
    ? [{ label: MODULE_META[module].label, icon: MODULE_META[module].icon, testId: `analytics-${module}` }]
    : [];
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="analytics-page">
      <AppHeader
        user={user}
        breadcrumbs={[
          { label: "Platforms", onClick: () => navigate("/select-platform"), testId: "platforms" },
          {
            label: "Analytics",
            icon: <BarChart3 className="w-3.5 h-3.5" />,
            onClick: module ? () => setModule(null) : undefined,
            testId: "analytics",
          },
          ...subCrumb,
        ]}
        onNavigate={(path) => navigate(path)}
        onLogout={handleLogout}
      />
      <main className="max-w-full mx-auto px-6 py-6 pb-16">
        <AnalyticsChooser active={module} onActiveChange={setModule} />
      </main>
    </div>
  );
};

const PriceGovernanceWrapper = ({ user }) => {
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    } catch {
      localStorage.removeItem('horizon_session_token');
      navigate('/login');
    }
  };
  return <PriceGovernance user={user} onNavigate={(path) => navigate(path)} onLogout={handleLogout} />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/select-platform" element={<ProtectedRoute>{(props) => <PlatformSelection {...props} />}</ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute>{(props) => <ControlPanelWrapper {...props} />}</ProtectedRoute>} />
      <Route path="/dashboard/price-governance" element={<ProtectedRoute>{(props) => <PriceGovernanceWrapper {...props} />}</ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute>{(props) => <AnalyticsWrapper {...props} />}</ProtectedRoute>} />
      <Route path="/dashboard/:platformId" element={<ProtectedRoute>{(props) => <Dashboard {...props} />}</ProtectedRoute>} />
      <Route path="/" element={<LoginPage />} />
      <Route path="*" element={<LoginPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { background: '#fff', color: '#111', border: '1px solid #e5e7eb' } }} />
      <AppRouter />
      <BandwidthWidget />
    </BrowserRouter>
  );
}
